import { updateAutotranslatorDom } from "./autotranslatorDom";
import { startDiagnosticCapture, type DiagnosticCapture } from "./autotranslatorDiagnostics";
import { waitForAutotranslatorAudioContext, AutotranslatorAudioContextError } from "./autotranslatorAudioContext";
import { AUTOTRANSLATOR_DIAGNOSTIC_PATH } from "../../shared/autotranslatorDiagnostics";
import { createAutotranslatorSpeechDetector, createAutotranslatorLimiterCurve, getAutotranslatorCommitReason, AUTOTRANSLATOR_OUTPUT_GAIN } from "./autotranslatorSpeech";
import { createAutotranslatorVersionGuard } from "./autotranslatorVersion";
import { readAutotranslatorSse } from "../../shared/autotranslatorStream";
import { localizeRuntimeText, t } from "./i18n";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";
import { SITE_TUNING } from "./siteTuning";
declare const __ZIARCADIA_BUILD_ID__: string;
import {
    buildAutotranslatorTextBilling,
    estimateAutotranslatorSessionCostUsd,
    readOpenAITextTokenUsage,
    type AutotranslatorTextBilling
} from "../../shared/autotranslatorCost";
import { AutotranslatorTextMeter, estimateAutotranslatorInputTokens,
    type AutotranslatorBillingProgress } from "../../shared/autotranslatorMeter";
import { buildAutotranslatorTranslationPrompt } from "../../shared/autotranslatorPrompt";

export type AutotranslatorAudioMode = "table" | "near" | "far";
export type AutotranslatorTargetLanguage = "en" | "ko";
type AutotranslatorTextLanguage = AutotranslatorTargetLanguage | "mixed" | "unknown";

export type AutotranslatorProperNoun = {
    id: string;
    english: string;
    korean: string;
};

type AutotranslatorBubbleStatus = "hearing" | "live" | "translating" | "done" | "error" | "pause";

type AutotranslatorBubble = {
    id: string;
    itemId: string;
    transcript: string;
    rawTranscript?: string;
    translation: string;
    translationComparison?: string;
    translatedTranscript?: string;
    translationPending?: boolean;
    literalTranslation?: string;
    interpretation?: string;
    interpretationSource?: string;
    contextApplied?: boolean;
    sourceLanguage: string;
    status: AutotranslatorBubbleStatus;
    createdAt: number;
    updatedAt: number;
    pauseSeconds?: number;
};

type AutotranslatorTranscriptItem = {
    itemId: string;
    speechTurn: number;
    displayOffset: number;
    languagePrefixIds: string[];
    previousItemId: string | null;
    text: string;
    draftText?: string;
    completed: boolean;
    order: number;
    updatedAt: number;
};

type AutotranslatorSessionSummary = {
    seconds: number;
    estimatedCostUsd: number;
};

export type AutotranslatorState = {
    audioMode: AutotranslatorAudioMode;
    tableMode: boolean;
    following: boolean;
    meterOpen: boolean;
    detailsOpen: boolean;
    diagnosticEnabled: boolean | null;
    diagnosticStatus: "loading" | "ready" | "recording" | "saving" | "saved" | "limit" | "error" | "unsupported";
    diagnosticBusy: boolean;
    unseenCount: number;
    bubbles: AutotranslatorBubble[];
    properNouns: AutotranslatorProperNoun[];
    nounInput: string;
    nounKoreanInput: string;
    nounEditingId: string | null;
    nounError: string;
    nounFormOpen: boolean;
    running: boolean;
    connecting: boolean;
    startupFeedbackActive: boolean;
    status: string;
    error: string;
    micLevel: number;
    recognizedSpeechActive: boolean;
    startedAt: number | null;
    elapsedMs: number;
    translationCount: number;
    translationCostUsd: number;
    estimatedTranslationCount: number;
    expandedBubbleId: string | null;
    summary: AutotranslatorSessionSummary | null;
};

type AutotranslatorView = {
    destroy: () => void;
    refreshLanguage: () => void;
};

type RealtimeTokenResponse = {
    clientSecret?: string;
    expiresAt?: number | null;
    error?: string;
};

type TranslateResponse = {
    sourceLanguage?: string;
    translation?: string;
    literalTranslation?: string;
    interpretation?: string;
    contextApplied?: boolean;
    repaired?: boolean;
    skipped?: boolean;
    model?: string;
    usage?: unknown;
    billing?: AutotranslatorTextBilling;
    error?: string;
};

type RealtimeServerEvent = {
    type?: unknown;
    item_id?: unknown;
    previous_item_id?: unknown;
    delta?: unknown;
    transcript?: unknown;
};

type AutotranslatorDebugValue = string | number | boolean | null | undefined;

type AutotranslatorRemoteDebugEntry = {
    time: number;
    sessionId: string;
    sequence: number;
    event: string;
    detail: string;
    page: string;
};

type WakeLockSentinelLike = {
    release: () => Promise<void>;
    addEventListener?: (type: "release", listener: () => void) => void;
};

type NavigatorWithWakeLock = Navigator & {
    wakeLock?: {
        request: (type: "screen") => Promise<WakeLockSentinelLike>;
    };
};

const PROPER_NOUN_STORAGE_KEY = "ziarcadia:autotranslator:proper-nouns";
const AUTOTRANSLATOR_DEBUG_STORAGE_KEY = "ziarcadia:autotranslator:debug";
const AUTOTRANSLATOR_REMOTE_DEBUG_STORAGE_KEY = "ziarcadia:autotranslator:remote-debug";
const AUTOTRANSLATOR_AUTOSTART_STORAGE_KEY = "ziarcadia:autotranslator:auto-start";
const AUTOTRANSLATOR_REMOTE_DEBUG_PATH = "/api/autotranslator/debug-log";
const AUTOTRANSLATOR_VIEW_STORAGE_KEY = "ziarcadia:autotranslator:view";
const OPENAI_REALTIME_SDP_URL = "https://api.openai.com/v1/realtime/calls";
const FOLLOW_BOTTOM_THRESHOLD_PX = 72;
const LIVE_TRANSLATE_DELAY_MS = SITE_TUNING.autotranslator.liveTranslateDelayMs;
const LIVE_TRANSLATE_MAX_WAIT_MS = SITE_TUNING.autotranslator.liveTranslateMaxWaitMs;
const LIVE_TRANSLATE_STALE_ABORT_AFTER_MS = 6000;
const LIVE_TRANSLATE_STALE_MIN_EXTRA_CHARS = 18;
const LIVE_TRANSLATE_STALE_MIN_EXTRA_WORDS = 3;
const FINAL_TRANSLATE_DELAY_MS = SITE_TUNING.autotranslator.finalTranslateDelayMs;
const LIVE_TRANSCRIPTION_COMMIT_CHECK_MS = 220;
const LIVE_TRANSCRIPTION_SPEECH_GRACE_MS = 2100;
const LIVE_TRANSCRIPTION_CONTINUITY_MS = 2800;
const LIVE_TRANSCRIPTION_CONTINUATION_MAX_CHARS = 150;
const LIVE_TRANSCRIPTION_CONTINUATION_MAX_WORDS = 24;
const AUTOTRANSLATOR_DEFERRED_SPLIT_SETTLE_MS = 760;
const AUTOTRANSLATOR_LAYOUT_MOTION_MS = 420;
const AUTOTRANSLATOR_BUBBLE_SPLIT_SOFT_MAX_CHARS = 170;
const AUTOTRANSLATOR_BUBBLE_SPLIT_HARD_MAX_CHARS = 240;
const AUTOTRANSLATOR_PENDING_DOT_ANIMATION_MS = 900;
const AUTOTRANSLATOR_PENDING_DOT_STAGGER_MS = 130;
const MIC_METER_LOG_INTERVAL_MS = 2200;
const REMOTE_DEBUG_FLUSH_MS = 650;
const REMOTE_DEBUG_BATCH_SIZE = 14;
const REMOTE_DEBUG_MAX_QUEUE = 600;
const REMOTE_DEBUG_MAX_VALUE_LENGTH = 360;
const REMOTE_DEBUG_MAX_DETAIL_LENGTH = 1200;
const AUTOTRANSLATOR_MAX_VISIBLE_MESSAGES = 5;
const AUTOTRANSLATOR_EXTRA_SETTLED_MESSAGES_WHILE_ACTIVE = 1;
const AUTOTRANSLATOR_CONTEXT_MAX_TURNS = 8;
const AUTOTRANSLATOR_TRANSLATION_TIMEOUT_MS = 12_000;
const NEAR_MODE_SEND_GAIN = 4;
const TABLE_MODE_SEND_GAIN = 12;
const FAR_MODE_SEND_GAIN = 16;
const AUTOTRANSLATOR_AUDIO_TUNING: Record<
    AutotranslatorAudioMode,
    {
        sendGain: number;
        minimumSpeechRms: number;
        compressorThreshold: number;
        compressorKnee: number;
        compressorRatio: number;
        compressorAttack: number;
        compressorRelease: number;
    }
> = {
    near: {
        sendGain: NEAR_MODE_SEND_GAIN,
        minimumSpeechRms: 0.0006,
        compressorThreshold: -34,
        compressorKnee: 22,
        compressorRatio: 4.5,
        compressorAttack: 0.004,
        compressorRelease: 0.2
    },
    table: {
        sendGain: TABLE_MODE_SEND_GAIN,
        minimumSpeechRms: 0.0003,
        compressorThreshold: -22,
        compressorKnee: 12,
        compressorRatio: 3,
        compressorAttack: 0.003,
        compressorRelease: 0.24
    },
    far: {
        sendGain: FAR_MODE_SEND_GAIN,
        minimumSpeechRms: 0.00015,
        compressorThreshold: -50,
        compressorKnee: 28,
        compressorRatio: 11,
        compressorAttack: 0.002,
        compressorRelease: 0.32
    }
};
const HANGUL_PATTERN = /[\u3131-\u318e\uac00-\ud7a3]/;
const HANGUL_GLOBAL_PATTERN = /[\u3131-\u318e\uac00-\ud7a3]/gu;
const LATIN_GLOBAL_PATTERN = /[A-Za-z]/g;
const LATIN_WORD_GLOBAL_PATTERN = /[A-Za-z][A-Za-z']*/g;
const COMMON_ENGLISH_WORD_PATTERN =
    /\b(?:a|an|and|are|as|at|be|but|can|for|from|have|how|i|in|is|it|it's|its|me|not|now|of|on|or|so|that|the|there|this|to|we|well|what|when|with|you)\b/gi;
const UNSUPPORTED_TRANSLATION_SCRIPT_PATTERN =
    /(?![\p{Script=Hangul}\p{Script=Latin}])\p{Letter}/u;
const SPACED_PUNCTUATION_PATTERN = /\s+([,.;:!?。！？…])/g;
const SENTENCE_END_PATTERN = /[.!?。！？]\s*$/u;
const SENTENCE_BOUNDARY_PATTERN = /[.!?。！？…]/u;
const SENTENCE_BOUNDARY_TRAILING_PATTERN = /["')\]}”’]/u;
const SENTENCE_ABBREVIATION_PATTERN = /\b(?:mr|mrs|ms|dr|prof|sr|jr|st|vs|etc|e\.g|i\.e)\.$/iu;
const SPEECH_COMPACT_PATTERN = /[^A-Za-z0-9\u3131-\u318e\uac00-\ud7a3]+/gu;
const TRAILING_CONTINUATION_WORD_PATTERN =
    /\b(?:a|an|and|are|as|at|be|because|but|can|for|from|going|gonna|have|if|in|is|it|it's|its|of|on|or|so|that|the|this|to|trying|was|were|what|when|where|which|while|with|would|you)\s*$/iu;
const MID_SENTENCE_ASR_TITLECASE_WORDS = new Set([
    "an",
    "and",
    "are",
    "as",
    "at",
    "be",
    "because",
    "big",
    "book",
    "books",
    "bottom",
    "but",
    "can",
    "center",
    "comments",
    "current",
    "does",
    "entering",
    "every",
    "finally",
    "fifteen",
    "first",
    "for",
    "from",
    "good",
    "having",
    "if",
    "in",
    "input",
    "is",
    "it",
    "looks",
    "make",
    "method",
    "mistakes",
    "my",
    "next",
    "open",
    "or",
    "over",
    "page",
    "perhaps",
    "please",
    "properly",
    "quality",
    "read",
    "repair",
    "right",
    "screen",
    "sentence",
    "sentences",
    "see",
    "somewhere",
    "sorry",
    "that",
    "the",
    "there",
    "this",
    "time",
    "to",
    "translation",
    "up",
    "version",
    "was",
    "we",
    "with",
    "without",
    "work",
    "your"
]);
const ROMANIZED_KOREAN_NAME_OVERRIDES = new Map<string, string>([
    ["hyewon", "혜원"],
    ["heewon", "희원"],
    ["hyejin", "혜진"],
    ["hyunwoo", "현우"],
    ["hyeonwoo", "현우"],
    ["hyunjun", "현준"],
    ["hyeonjun", "현준"],
    ["jihoon", "지훈"],
    ["jihun", "지훈"],
    ["jiwoo", "지우"],
    ["jiu", "지우"],
    ["jisoo", "지수"],
    ["jisu", "지수"],
    ["jiwon", "지원"],
    ["minji", "민지"],
    ["minseo", "민서"],
    ["mina", "미나"],
    ["seoyeon", "서연"],
    ["seojun", "서준"],
    ["seojoon", "서준"],
    ["sujin", "수진"],
    ["sumin", "수민"],
    ["yoona", "유나"],
    ["yuna", "유나"],
    ["doyun", "도윤"],
    ["doyoon", "도윤"],
    ["eunji", "은지"],
    ["jiho", "지호"]
]);
const HANGUL_KOREAN_NAME_OVERRIDES = new Map<string, string>(
    [...ROMANIZED_KOREAN_NAME_OVERRIDES.entries()].map(([romanized, hangul]): [string, string] => [
        hangul,
        romanized.charAt(0).toUpperCase() + romanized.slice(1)
    ])
);
const KOREAN_NAME_ROMANIZATION_SYLLABLES = [
    ["su", "수"],
    ["soo", "수"],
    ["jun", "준"],
    ["joon", "준"],
    ["young", "영"],
    ["yeong", "영"],
    ["hyun", "현"],
    ["hyeon", "현"],
    ["yoon", "윤"],
    ["yun", "윤"],
    ["hye", "혜"],
    ["won", "원"],
    ["ji", "지"],
    ["min", "민"],
    ["seo", "서"],
    ["yeon", "연"],
    ["woo", "우"],
    ["u", "우"],
    ["yu", "유"],
    ["yoo", "유"],
    ["na", "나"],
    ["mi", "미"],
    ["jin", "진"],
    ["hoon", "훈"],
    ["hun", "훈"],
    ["ho", "호"],
    ["do", "도"],
    ["eun", "은"],
    ["bin", "빈"],
    ["bom", "봄"],
    ["byeol", "별"],
    ["chan", "찬"],
    ["chang", "창"],
    ["cheol", "철"],
    ["chul", "철"],
    ["da", "다"],
    ["dae", "대"],
    ["gi", "기"],
    ["ki", "기"],
    ["gyu", "규"],
    ["kyu", "규"],
    ["gyeong", "경"],
    ["kyung", "경"],
    ["gyeom", "겸"],
    ["gyun", "균"],
    ["ha", "하"],
    ["hae", "해"],
    ["hee", "희"],
    ["hui", "희"],
    ["hwan", "환"],
    ["hwi", "휘"],
    ["i", "이"],
    ["in", "인"],
    ["ja", "자"],
    ["jae", "재"],
    ["je", "제"],
    ["jeong", "정"],
    ["jung", "정"],
    ["ju", "주"],
    ["joo", "주"],
    ["nam", "남"],
    ["ra", "라"],
    ["rin", "린"],
    ["ri", "리"],
    ["sang", "상"],
    ["seok", "석"],
    ["seon", "선"],
    ["sun", "선"],
    ["seong", "성"],
    ["sung", "성"],
    ["si", "시"],
    ["shi", "시"],
    ["sol", "솔"],
    ["tae", "태"],
    ["uk", "욱"],
    ["wook", "욱"],
    ["ye", "예"],
    ["yul", "율"],
    ["a", "아"],
    ["ah", "아"],
    ["ae", "애"]
] as const;
const KOREAN_NAME_ROMANIZATION_SYLLABLES_BY_LENGTH = [...KOREAN_NAME_ROMANIZATION_SYLLABLES].sort(
    ([left], [right]) => right.length - left.length
);
const KOREAN_NAME_ROMANIZATION_BY_SYLLABLE = new Map<string, string>();

for (const [romanized, hangul] of KOREAN_NAME_ROMANIZATION_SYLLABLES) {
    if (!KOREAN_NAME_ROMANIZATION_BY_SYLLABLE.has(hangul)) {
        KOREAN_NAME_ROMANIZATION_BY_SYLLABLE.set(hangul, romanized);
    }
}

const createId = (): string => {
    const cryptoApi = globalThis.crypto;

    if (typeof cryptoApi?.randomUUID === "function") {
        return cryptoApi.randomUUID();
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

export const resolveAutotranslatorAudioTuning = (mode: AutotranslatorAudioMode) =>
    AUTOTRANSLATOR_AUDIO_TUNING[mode];

const normalizeRomanizedKoreanNameKey = (value: string): string =>
    normalizeLine(value)
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z]/g, "")
        .toLowerCase();

const capitalizeRomanizedNamePart = (value: string): string =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const formatRomanizedKoreanName = (romanizedParts: readonly string[]): string =>
    romanizedParts.length > 0
        ? capitalizeRomanizedNamePart(romanizedParts.join(""))
        : "";

const findRomanizedKoreanNameSegments = (
    value: string
): { romanizedParts: string[]; hangulParts: string[] } | null => {
    const key = normalizeRomanizedKoreanNameKey(value);

    if (key.length < 2 || key.length > 18) {
        return null;
    }

    const override = ROMANIZED_KOREAN_NAME_OVERRIDES.get(key);

    if (override) {
        const romanizedParts = [...override]
            .map((character) => KOREAN_NAME_ROMANIZATION_BY_SYLLABLE.get(character) ?? "")
            .filter(Boolean);

        return {
            romanizedParts: romanizedParts.length === override.length ? romanizedParts : [key],
            hangulParts: [...override]
        };
    }

    const memo = new Map<number, { romanizedParts: string[]; hangulParts: string[] } | null>();

    const findFrom = (index: number): { romanizedParts: string[]; hangulParts: string[] } | null => {
        if (index === key.length) {
            return {
                romanizedParts: [],
                hangulParts: []
            };
        }

        if (memo.has(index)) {
            return memo.get(index) ?? null;
        }

        for (const [romanized, hangul] of KOREAN_NAME_ROMANIZATION_SYLLABLES_BY_LENGTH) {
            if (!key.startsWith(romanized, index)) {
                continue;
            }

            const rest = findFrom(index + romanized.length);

            if (rest) {
                const result = {
                    romanizedParts: [romanized, ...rest.romanizedParts],
                    hangulParts: [hangul, ...rest.hangulParts]
                };
                memo.set(index, result);
                return result;
            }
        }

        memo.set(index, null);
        return null;
    };

    const segments = findFrom(0);

    if (!segments || segments.hangulParts.length < 2 || segments.hangulParts.length > 4) {
        return null;
    }

    return segments;
};

const inferRomanizedKoreanNamePartsFromHangul = (value: string): string[] => {
    const hangul = normalizeLine(value).replace(/\s+/g, "");

    if (!hangul || ![...hangul].every((character) => HANGUL_PATTERN.test(character))) {
        return [];
    }

    const override = HANGUL_KOREAN_NAME_OVERRIDES.get(hangul);

    if (override) {
        const overrideKey = normalizeRomanizedKoreanNameKey(override);
        const directParts = [...hangul]
            .map((character) => KOREAN_NAME_ROMANIZATION_BY_SYLLABLE.get(character) ?? "")
            .filter(Boolean);

        return directParts.length === hangul.length ? directParts : [overrideKey];
    }

    if (hangul.length < 2 || hangul.length > 4) {
        return [];
    }

    const parts: string[] = [];

    for (const character of [...hangul]) {
        const romanized = KOREAN_NAME_ROMANIZATION_BY_SYLLABLE.get(character);

        if (!romanized) {
            return [];
        }

        parts.push(romanized);
    }

    return parts;
};

export const inferHangulFromRomanizedKoreanName = (value: string): string => {
    const segments = findRomanizedKoreanNameSegments(value);

    return segments?.hangulParts.join("") ?? "";
};

export const inferRomanizedFromHangulKoreanName = (value: string): string =>
    formatRomanizedKoreanName(inferRomanizedKoreanNamePartsFromHangul(value));

export const completeAutotranslatorProperNounPair = (
    noun: Omit<AutotranslatorProperNoun, "id">
): Omit<AutotranslatorProperNoun, "id"> => {
    const english = normalizeLine(noun.english).slice(0, 80);
    const korean = normalizeLine(noun.korean).slice(0, 80);

    return {
        english: english || inferRomanizedFromHangulKoreanName(korean),
        korean: korean || inferHangulFromRomanizedKoreanName(english)
    };
};

const properNounKey = (noun: Omit<AutotranslatorProperNoun, "id">): string =>
    `${noun.english.toLowerCase()}\u0000${noun.korean}`;

const safeReadProperNouns = (): AutotranslatorProperNoun[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(PROPER_NOUN_STORAGE_KEY) ?? "[]") as unknown;

        if (!Array.isArray(parsed)) {
            return [];
        }

        const seen = new Set<string>();
        const nouns: AutotranslatorProperNoun[] = [];

        for (const item of parsed) {
            if (!item || typeof item !== "object") {
                continue;
            }

            const completed = completeAutotranslatorProperNounPair({
                english:
                    typeof (item as { english?: unknown }).english === "string"
                        ? (item as { english: string }).english
                        : "",
                korean:
                    typeof (item as { korean?: unknown }).korean === "string"
                        ? (item as { korean: string }).korean
                        : ""
            });
            const id =
                typeof (item as { id?: unknown }).id === "string" && (item as { id: string }).id.trim()
                    ? (item as { id: string }).id
                    : createId();
            const key = properNounKey(completed);

            if ((!completed.english && !completed.korean) || seen.has(key)) {
                continue;
            }

            seen.add(key);
            nouns.push({
                id,
                ...completed
            });

            if (nouns.length >= 40) {
                break;
            }
        }

        return nouns;
    } catch {
        return [];
    }
};

const safeWriteProperNouns = (properNouns: readonly AutotranslatorProperNoun[]) => {
    try {
        localStorage.setItem(
            PROPER_NOUN_STORAGE_KEY,
            JSON.stringify(
                properNouns
                    .map((noun) => ({
                        id: noun.id,
                        ...completeAutotranslatorProperNounPair(noun)
                    }))
                    .filter((noun) => noun.english || noun.korean)
            )
        );
    } catch {
        // Glossary persistence is helpful, not required for live translation.
    }
};

type AutotranslatorViewPreferences = Pick<AutotranslatorState, "meterOpen" | "detailsOpen">;

const safeReadViewPreferences = (): AutotranslatorViewPreferences => {
    try {
        const parsed = JSON.parse(localStorage.getItem(AUTOTRANSLATOR_VIEW_STORAGE_KEY) ?? "{}") as {
            meterOpen?: unknown;
            detailsOpen?: unknown;
        };

        return {
            meterOpen: parsed.meterOpen === true,
            detailsOpen: parsed.detailsOpen === true
        };
    } catch {
        return {
            meterOpen: false,
            detailsOpen: false
        };
    }
};

const safeWriteViewPreferences = (state: Pick<AutotranslatorState, "meterOpen" | "detailsOpen">) => {
    try {
        localStorage.setItem(
            AUTOTRANSLATOR_VIEW_STORAGE_KEY,
            JSON.stringify({
                meterOpen: state.meterOpen,
                detailsOpen: state.detailsOpen
            })
        );
    } catch {
        // Display preferences are useful, not required for live translation.
    }
};

export const createInitialAutotranslatorState = (): AutotranslatorState => {
    const viewPreferences = safeReadViewPreferences();

    return {
        audioMode: "table",
        tableMode: false,
        following: true,
        meterOpen: viewPreferences.meterOpen,
        detailsOpen: viewPreferences.detailsOpen || viewPreferences.meterOpen,
        diagnosticEnabled: null,
        diagnosticStatus: "loading",
        diagnosticBusy: false,
        unseenCount: 0,
        bubbles: [],
        properNouns: safeReadProperNouns(),
        nounInput: "",
        nounKoreanInput: "",
        nounEditingId: null,
        nounError: "",
        nounFormOpen: false,
        running: false,
        connecting: false,
        startupFeedbackActive: false,
        status: "",
        error: "",
        micLevel: 0,
        recognizedSpeechActive: false,
        startedAt: null,
        elapsedMs: 0,
        translationCount: 0,
        translationCostUsd: 0,
        estimatedTranslationCount: 0,
        expandedBubbleId: null,
        summary: null
    };
};

const formatElapsed = (milliseconds: number): string => {
    const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const formatCost = (cost: number): string => `$${cost.toFixed(2)}`;
const meterCostTitle = (estimatedRequests: number): string =>
    t("autotranslatorCostBasis") + (estimatedRequests > 0
        ? ` ${t("autotranslatorCostEstimatedRequests", { count: estimatedRequests })}` : "");

export const normalizeAutotranslatorLine = (value: string): string =>
    value
        .replace(/\s+/g, " ")
        .replace(SPACED_PUNCTUATION_PATTERN, "$1")
        .trim();

const normalizeLine = normalizeAutotranslatorLine;

export const isAutotranslatorTranscriptExtension = (translatedText: string, currentText: string): boolean => {
    const translated = normalizeLine(translatedText);
    const current = normalizeLine(currentText);

    if (!translated || translated.length >= current.length) {
        return false;
    }

    if (!current.toLocaleLowerCase().startsWith(translated.toLocaleLowerCase())) {
        return false;
    }

    const nextCharacter = current.charAt(translated.length);

    return !nextCharacter || /[\s,.;:!?)\]}]/u.test(nextCharacter);
};

export const normalizeAutotranslatorTranscriptLine = (value: string): string =>
    normalizeAutotranslatorLine(value)
        .replace(/\b(?:A|[A-Z][a-z']+)\b/g, (word, offset, text) => {
            const normalizedWord = word.toLowerCase();

            if (!MID_SENTENCE_ASR_TITLECASE_WORDS.has(normalizedWord)) {
                return word;
            }

            const previous = text.slice(0, offset).trimEnd();

            if (!previous || SENTENCE_END_PATTERN.test(previous)) {
                return word;
            }

            return normalizedWord;
        })
        .replace(/\b(i)(?=(?:['`](?:m|d|ll|ve|re))?\b)/giu, "I")
        .replace(/(^|[.!?]\s+)([a-z])/gu, (_match, prefix: string, letter: string) => {
            return `${prefix}${letter.toUpperCase()}`;
        });

const compactAutotranslatorSpeechText = (value: string): string =>
    normalizeAutotranslatorTranscriptLine(value).toLowerCase().replace(SPEECH_COMPACT_PATTERN, "");

const softenAutotranslatorContinuationStart = (current: string, next: string): string => {
    if (!current || SENTENCE_END_PATTERN.test(current)) {
        return next;
    }

    return next.replace(/^[A-Z][A-Za-z']*\b/u, (word) => {
        const normalizedWord = word.toLowerCase();

        return MID_SENTENCE_ASR_TITLECASE_WORDS.has(normalizedWord) ? normalizedWord : word;
    });
};

const autotranslatorSpacingScore = (value: string): number =>
    (value.match(/\b[A-Za-z]{2,}\s+[a-z]{2,}\b/g) ?? []).length + value.length / 10000;

const chooseCleanerEquivalentTranscript = (current: string, next: string): string =>
    autotranslatorSpacingScore(next) < autotranslatorSpacingScore(current) ? next : current;

const getAutotranslatorLatinWordParts = (value: string): string[] =>
    normalizeLine(value)
        .match(LATIN_WORD_GLOBAL_PATTERN)
        ?.map((word) => word.toLowerCase()) ?? [];

const trimLeadingAutotranslatorLatinWords = (value: string, wordCount: number): string => {
    let remaining = value;

    for (let index = 0; index < wordCount; index += 1) {
        remaining = remaining.replace(/^\s*[A-Za-z][A-Za-z']*(?:[.!?,;:]+)?\s*/u, "");
    }

    return remaining;
};

const getRepeatedTrailingWordCleanMerge = (current: string, next: string): string | null => {
    const currentWords = getAutotranslatorLatinWordParts(current);
    const nextWords = getAutotranslatorLatinWordParts(next);

    if (currentWords.length !== nextWords.length + 1 || nextWords.length === 0) {
        return null;
    }

    if (currentWords[currentWords.length - 1] !== currentWords[currentWords.length - 2]) {
        return null;
    }

    const samePrefix = nextWords.every((word, index) => currentWords[index] === word);

    return samePrefix ? next : null;
};

const getAutotranslatorWordOverlapLength = (current: string, next: string): number => {
    const currentWords = getAutotranslatorLatinWordParts(current);
    const nextWords = getAutotranslatorLatinWordParts(next);
    const maxOverlap = Math.min(10, currentWords.length, nextWords.length);

    for (let length = maxOverlap; length >= 1; length -= 1) {
        const currentSuffix = currentWords.slice(currentWords.length - length).join(" ");
        const nextPrefix = nextWords.slice(0, length).join(" ");

        if (currentSuffix && currentSuffix === nextPrefix) {
            return length;
        }
    }

    return 0;
};

const findCompactSuffixStart = (current: string, nextCompact: string): number | null => {
    if (!nextCompact) {
        return null;
    }

    const searchStart = Math.max(0, current.length - nextCompact.length - 48);

    for (let index = current.length - 1; index >= searchStart; index -= 1) {
        if (index > 0 && !/\s/.test(current[index - 1] ?? "")) {
            continue;
        }

        if (compactAutotranslatorSpeechText(current.slice(index)) === nextCompact) {
            return index;
        }
    }

    return null;
};

const getSubsequenceCoverage = (needle: string, haystack: string): number => {
    if (!needle || !haystack) {
        return 0;
    }

    let matched = 0;

    for (const character of haystack) {
        if (character === needle[matched]) {
            matched += 1;

            if (matched >= needle.length) {
                break;
            }
        }
    }

    return matched / needle.length;
};

const findNoisyCompactSuffixStart = (current: string, nextCompact: string): number | null => {
    if (nextCompact.length < 10) {
        return null;
    }

    const searchStart = Math.max(0, current.length - Math.max(80, nextCompact.length * 2));
    let candidate: number | null = null;

    for (let index = current.length - 1; index >= searchStart; index -= 1) {
        if (index > 0 && !/\s/.test(current[index - 1] ?? "")) {
            continue;
        }

        const suffixCompact = compactAutotranslatorSpeechText(current.slice(index));

        if (suffixCompact.length < 8 || suffixCompact.length > nextCompact.length + 4) {
            continue;
        }

        if (
            nextCompact.endsWith(suffixCompact) ||
            (nextCompact.length >= suffixCompact.length && getSubsequenceCoverage(suffixCompact, nextCompact) >= 0.72)
        ) {
            candidate = index;
        }
    }

    return candidate;
};

const isSentenceBoundaryAt = (text: string, index: number): { boundary: boolean; endIndex: number } => {
    const mark = text[index] ?? "";

    if (!SENTENCE_BOUNDARY_PATTERN.test(mark)) {
        return { boundary: false, endIndex: index + 1 };
    }

    if (mark === "." && SENTENCE_ABBREVIATION_PATTERN.test(text.slice(Math.max(0, index - 12), index + 1))) {
        return { boundary: false, endIndex: index + 1 };
    }

    let endIndex = index + 1;

    while (endIndex < text.length && SENTENCE_BOUNDARY_TRAILING_PATTERN.test(text[endIndex] ?? "")) {
        endIndex += 1;
    }

    const next = text[endIndex] ?? "";

    if (next && !/\s/.test(next)) {
        return { boundary: false, endIndex };
    }

    return { boundary: true, endIndex };
};

const splitLongAutotranslatorSegment = (segment: string): string[] => {
    const normalized = normalizeLine(segment);

    if (normalized.length <= AUTOTRANSLATOR_BUBBLE_SPLIT_HARD_MAX_CHARS) {
        return normalized ? [normalized] : [];
    }

    const words = normalized.split(/\s+/);
    const chunks: string[] = [];
    let current = "";

    words.forEach((word) => {
        const candidate = current ? `${current} ${word}` : word;

        if (current && candidate.length > AUTOTRANSLATOR_BUBBLE_SPLIT_SOFT_MAX_CHARS) {
            chunks.push(current);
            current = word;
            return;
        }

        current = candidate;
    });

    if (current) {
        chunks.push(current);
    }

    return chunks;
};

type AutotranslatorDominantLanguage = "ko" | "en" | null;

const getAutotranslatorLanguageStats = (value: string) => {
    const normalized = normalizeLine(value);
    const latinWords = normalized.match(LATIN_WORD_GLOBAL_PATTERN) ?? [];

    return {
        hangulCount: normalized.match(HANGUL_GLOBAL_PATTERN)?.length ?? 0,
        latinLetterCount: normalized.match(LATIN_GLOBAL_PATTERN)?.length ?? 0,
        latinWordCount: latinWords.length
    };
};

const getDominantAutotranslatorSplitLanguage = (value: string): AutotranslatorDominantLanguage => {
    const { hangulCount, latinLetterCount, latinWordCount } = getAutotranslatorLanguageStats(value);

    if (hangulCount === 0 && latinLetterCount === 0) {
        return null;
    }

    if (hangulCount > 0 && latinLetterCount === 0) {
        return hangulCount >= 2 ? "ko" : null;
    }

    if (latinLetterCount > 0 && hangulCount === 0) {
        return latinWordCount >= 2 || latinLetterCount >= 10 ? "en" : null;
    }

    if (hangulCount >= 3 && latinWordCount <= 2) {
        return "ko";
    }

    if (latinWordCount >= 2 && hangulCount <= 3) {
        return "en";
    }

    if (hangulCount >= Math.max(4, latinLetterCount * 0.55)) {
        return "ko";
    }

    if (latinWordCount >= 2 && latinLetterCount >= Math.max(6, hangulCount * 1.2)) {
        return "en";
    }

    return null;
};

export const startsAutotranslatorLanguageTurn = (previous: string, incoming: string): boolean => {
    const before = getDominantAutotranslatorSplitLanguage(previous);
    const text = normalizeLine(incoming);
    const after = getDominantAutotranslatorSplitLanguage(incoming) ??
        (/^(네|예|응|아니|아뇨)[.!?]?$/u.test(text) ? "ko" :
            /^(yes|no|okay|ok|sure|thanks|sorry)[.!?]?$/iu.test(text) ? "en" : null);
    if (!before || !after || before === after) return false;
    // Preserve single embedded names/product terms, but separate short replies.
    return after === "ko" ? (text.match(HANGUL_GLOBAL_PATTERN)?.length ?? 0) >= 3 || /^(네|예|응|아니|아뇨)[.!?]?$/u.test(text)
        : (text.match(LATIN_WORD_GLOBAL_PATTERN)?.length ?? 0) >= 2 || /^(yes|no|okay|ok|sure|thanks|sorry)[.!?]?$/iu.test(text);
};

const isSubstantialAutotranslatorLanguageSegment = (
    value: string,
    language: Exclude<AutotranslatorDominantLanguage, null>
): boolean => {
    const { hangulCount, latinLetterCount, latinWordCount } = getAutotranslatorLanguageStats(value);

    const shortReply = language === "ko" ? /^(네|예|응|아니|아뇨)[.!?]?$/u.test(normalizeLine(value)) :
        /^(yes|no|okay|ok|sure|thanks|sorry)[.!?]?$/iu.test(normalizeLine(value));
    return shortReply || (language === "ko" ? hangulCount >= 3 : latinWordCount >= 3 || latinLetterCount >= 18);
};

const getAutotranslatorLanguageSplitCandidateIndexes = (segment: string): number[] => {
    const indexes = new Set<number>();

    for (const match of segment.matchAll(/\s+/g)) {
        indexes.add(match.index + match[0].length);
    }

    for (let index = 1; index < segment.length; index += 1) {
        const previous = segment[index - 1] ?? "";
        const next = segment[index] ?? "";
        const previousIsHangul = HANGUL_PATTERN.test(previous);
        const nextIsHangul = HANGUL_PATTERN.test(next);
        const previousIsLatin = /[A-Za-z]/.test(previous);
        const nextIsLatin = /[A-Za-z]/.test(next);

        if ((previousIsHangul && nextIsLatin) || (previousIsLatin && nextIsHangul)) {
            indexes.add(index);
        }
    }

    return [...indexes].sort((left, right) => left - right);
};

const findAutotranslatorMixedLanguageSplitIndex = (segment: string): number | null => {
    for (const index of getAutotranslatorLanguageSplitCandidateIndexes(segment)) {
        if (index <= 0 || index >= segment.length) {
            continue;
        }

        const left = normalizeLine(segment.slice(0, index));
        const right = normalizeLine(segment.slice(index));

        if (!left || !right) {
            continue;
        }

        const leftLanguage = getDominantAutotranslatorSplitLanguage(left);
        const rightLanguage = getDominantAutotranslatorSplitLanguage(right) ??
            (/^(네|예|응|아니|아뇨)[.!?]?$/u.test(right) ? "ko" :
                /^(yes|no|okay|ok|sure|thanks|sorry)[.!?]?$/iu.test(right) ? "en" : null);
        const firstRightLetter = right.match(/[A-Za-z\uac00-\ud7a3]/u)?.[0] ?? "";
        const rightStartsInLanguage = rightLanguage === "ko" ? HANGUL_PATTERN.test(firstRightLetter) : /[A-Za-z]/u.test(firstRightLetter);

        if (
            leftLanguage &&
            rightLanguage &&
            leftLanguage !== rightLanguage &&
            rightStartsInLanguage &&
            isSubstantialAutotranslatorLanguageSegment(left, leftLanguage) &&
            isSubstantialAutotranslatorLanguageSegment(right, rightLanguage)
        ) {
            return index;
        }
    }

    return null;
};

const splitAutotranslatorMixedLanguageSegment = (segment: string): string[] => {
    let remaining = normalizeLine(segment);
    const pieces: string[] = [];

    for (let guard = 0; remaining && guard < 8; guard += 1) {
        const splitIndex = findAutotranslatorMixedLanguageSplitIndex(remaining);

        if (splitIndex === null) {
            break;
        }

        const left = normalizeAutotranslatorTranscriptLine(remaining.slice(0, splitIndex));

        if (left) {
            pieces.push(left);
        }

        remaining = normalizeLine(remaining.slice(splitIndex));
    }

    if (remaining) {
        pieces.push(normalizeAutotranslatorTranscriptLine(remaining));
    }

    return pieces;
};

export const splitAutotranslatorTranscriptIntoBubbleTexts = (value: string): string[] => {
    const normalized = normalizeAutotranslatorTranscriptLine(value);

    if (!normalized) {
        return [];
    }

    const sentenceRuns: string[] = [];
    let startIndex = 0;

    for (let index = 0; index < normalized.length; index += 1) {
        const boundary = isSentenceBoundaryAt(normalized, index);

        if (!boundary.boundary) {
            continue;
        }

        const sentence = normalizeLine(normalized.slice(startIndex, boundary.endIndex));

        if (sentence) {
            sentenceRuns.push(sentence);
        }

        startIndex = boundary.endIndex;

        while (startIndex < normalized.length && /\s/.test(normalized[startIndex] ?? "")) {
            startIndex += 1;
        }

        index = startIndex - 1;
    }

    const trailing = normalizeLine(normalized.slice(startIndex));

    if (trailing) {
        sentenceRuns.push(trailing);
    }

    return (sentenceRuns.length > 0 ? sentenceRuns : [normalized])
        .flatMap(splitAutotranslatorMixedLanguageSegment)
        .flatMap(splitLongAutotranslatorSegment);
};

type AutotranslatorRetainedBubble = Pick<AutotranslatorBubble, "status" | "transcript"> & {
    translation?: string;
    translatedTranscript?: string;
    translationPending?: boolean;
};

const countsTowardAutotranslatorMessageLimit = (bubble: AutotranslatorRetainedBubble): boolean =>
    bubble.status !== "pause" && normalizeLine(bubble.transcript).length > 0;

const isActiveAutotranslatorMessage = (bubble: AutotranslatorRetainedBubble): boolean =>
    bubble.status === "hearing" || bubble.status === "live" || bubble.status === "translating";

const hasCurrentAutotranslatorRetainedTranslation = (bubble: AutotranslatorRetainedBubble): boolean =>
    normalizeLine(bubble.transcript).length > 0 &&
    normalizeLine(bubble.translation ?? "").length > 0 &&
    normalizeLine(bubble.translatedTranscript ?? bubble.transcript) === normalizeLine(bubble.transcript);

const isSettledTranslatedAutotranslatorMessage = (bubble: AutotranslatorRetainedBubble): boolean =>
    bubble.status === "done" && hasCurrentAutotranslatorRetainedTranslation(bubble);

const needsAutotranslatorSettledAnchor = (bubble: AutotranslatorRetainedBubble): boolean =>
    isActiveAutotranslatorMessage(bubble) &&
    (bubble.translationPending === true || !hasCurrentAutotranslatorRetainedTranslation(bubble));

export const trimAutotranslatorBubblesForFollowing = <T extends AutotranslatorRetainedBubble>(
    bubbles: readonly T[],
    maxMessages = AUTOTRANSLATOR_MAX_VISIBLE_MESSAGES
): T[] => {
    const messageIndexes = bubbles.flatMap((bubble, index) =>
        countsTowardAutotranslatorMessageLimit(bubble) ? [index] : []
    );

    if (messageIndexes.length <= maxMessages) {
        return [...bubbles];
    }

    const firstRetainedMessageIndex = messageIndexes[messageIndexes.length - maxMessages] ?? 0;
    const retainedIndexes = new Set(
        bubbles.flatMap((bubble, index) => (index >= firstRetainedMessageIndex ? [index] : []))
    );

    if (bubbles.some(needsAutotranslatorSettledAnchor) &&
        !bubbles.slice(firstRetainedMessageIndex).some(isSettledTranslatedAutotranslatorMessage)) {
        bubbles
            .flatMap((bubble, index) =>
                index < firstRetainedMessageIndex && isSettledTranslatedAutotranslatorMessage(bubble)
                    ? [index]
                    : []
            )
            .slice(-AUTOTRANSLATOR_EXTRA_SETTLED_MESSAGES_WHILE_ACTIVE)
            .forEach((index) => retainedIndexes.add(index));
    }

    return bubbles.filter((_, index) => retainedIndexes.has(index));
};

const getAutotranslatorNextExpiringBubbleId = <T extends AutotranslatorRetainedBubble & { id: string }>(
    bubbles: readonly T[],
    maxMessages = AUTOTRANSLATOR_MAX_VISIBLE_MESSAGES
): string | null => {
    const messageBubbles = bubbles.filter(countsTowardAutotranslatorMessageLimit);

    if (messageBubbles.length < maxMessages) {
        return null;
    }

    return messageBubbles[0]?.id ?? null;
};

const isAutotranslatorPendingBubble = <T extends AutotranslatorRetainedBubble>(bubble: T): boolean =>
    bubble.status === "hearing";

export const mergeAutotranslatorTranscriptText = (existing: string, incoming: string): string => {
    const current = normalizeAutotranslatorTranscriptLine(existing);
    const normalizedNext = normalizeAutotranslatorTranscriptLine(incoming);

    if (!current) {
        return normalizedNext;
    }

    if (!normalizedNext) {
        return current;
    }

    const repeatedTrailingWordCleanMerge = getRepeatedTrailingWordCleanMerge(current, normalizedNext);

    if (repeatedTrailingWordCleanMerge) {
        return repeatedTrailingWordCleanMerge;
    }

    const next = softenAutotranslatorContinuationStart(current, normalizedNext);
    const currentLower = current.toLowerCase();
    const nextLower = next.toLowerCase();
    const currentCompact = compactAutotranslatorSpeechText(current);
    const nextCompact = compactAutotranslatorSpeechText(next);

    if (currentCompact && nextCompact) {
        if (currentCompact === nextCompact) {
            return chooseCleanerEquivalentTranscript(current, next);
        }

        if (currentCompact.includes(nextCompact)) {
            const suffixStart = findCompactSuffixStart(current, nextCompact);

            if (suffixStart !== null) {
                return normalizeAutotranslatorTranscriptLine(`${current.slice(0, suffixStart)} ${next}`);
            }

            return current;
        }

        const noisySuffixStart = findNoisyCompactSuffixStart(current, nextCompact);

        if (noisySuffixStart !== null) {
            return normalizeAutotranslatorTranscriptLine(`${current.slice(0, noisySuffixStart)} ${next}`);
        }

        if (nextCompact.includes(currentCompact)) {
            return next;
        }
    }

    if (currentLower === nextLower || currentLower.includes(nextLower)) {
        return current;
    }

    if (nextLower.includes(currentLower)) {
        return next;
    }

    const wordOverlapLength = getAutotranslatorWordOverlapLength(current, next);

    if (wordOverlapLength > 0) {
        const nextRemainder = trimLeadingAutotranslatorLatinWords(next, wordOverlapLength);

        return normalizeAutotranslatorTranscriptLine(
            nextRemainder ? `${current} ${nextRemainder}` : current
        );
    }

    const maxOverlap = Math.min(80, current.length, next.length);

    for (let length = maxOverlap; length >= 5; length -= 1) {
        if (currentLower.slice(-length) === nextLower.slice(0, length)) {
            return normalizeAutotranslatorTranscriptLine(`${current}${next.slice(length)}`);
        }
    }

    return normalizeAutotranslatorTranscriptLine(`${current} ${next}`);
};

export const mergeAutotranslatorTranscriptDocumentTexts = (items: readonly string[]): string =>
    items
        .map((item) => normalizeAutotranslatorTranscriptLine(item))
        .filter(Boolean)
        .reduce((merged, item) => mergeAutotranslatorTranscriptText(merged, item), "");

export const appendAutotranslatorTranscriptDelta = (existing: string, delta: string): string => {
    const current = normalizeAutotranslatorTranscriptLine(existing);
    const next = delta.trim();

    if (!current) {
        return normalizeAutotranslatorTranscriptLine(next);
    }

    if (!next) {
        return current;
    }

    const currentLower = current.toLowerCase();
    const nextLower = normalizeAutotranslatorTranscriptLine(next).toLowerCase();
    const canDedupe = Math.min(currentLower.length, nextLower.length) >= 4;

    if (canDedupe && (currentLower === nextLower || currentLower.includes(nextLower))) {
        return current;
    }

    if (canDedupe && nextLower.includes(currentLower)) {
        return normalizeAutotranslatorTranscriptLine(next);
    }

    if (/^\s/.test(delta) || HANGUL_PATTERN.test(current.slice(-1)) || HANGUL_PATTERN.test(next[0] ?? "")) {
        return normalizeAutotranslatorTranscriptLine(`${current}${delta}`);
    }

    return normalizeAutotranslatorTranscriptLine(`${current} ${next}`);
};

export const inferAutotranslatorPreferredTargetLanguage = (
    transcript: string
): AutotranslatorTargetLanguage | null => {
    const language = inferAutotranslatorTextLanguage(transcript);

    if (language === "ko") {
        return "en";
    }

    if (language === "en" || language === "mixed") {
        return "ko";
    }

    return null;
};

export const isAutotranslatorRecognizedSpeech = (text: string): boolean => {
    const normalized = normalizeLine(text);
    return /[A-Za-z\uac00-\ud7a3]/u.test(normalized) &&
        !hasUnsupportedAutotranslatorScript(normalized) &&
        !/^\[[^\]]*\]?[.!?]*$/u.test(normalized) &&
        !/^[\[(](?:music|noise|silence|inaudible|unintelligible|background noise|음악|잡음|무음|알아들을 수 없음)[\])]\.?$/iu.test(normalized);
};

const inferAutotranslatorTextLanguage = (text: string): AutotranslatorTextLanguage => {
    const normalized = normalizeLine(text);

    if (!normalized || hasUnsupportedAutotranslatorScript(normalized)) {
        return "unknown";
    }

    const { hangulCount, latinLetterCount, latinWordCount } = getAutotranslatorLanguageStats(normalized);

    if (hangulCount > 0 && latinLetterCount > 0) {
        if (hangulCount >= 3 && latinWordCount <= 2) {
            return "ko";
        }

        return hangulCount >= latinLetterCount ? "ko" : "mixed";
    }

    if (hangulCount > 0) {
        return "ko";
    }

    if (latinLetterCount > 0) {
        return "en";
    }

    return "unknown";
};

const hasUnsupportedAutotranslatorScript = (transcript: string): boolean =>
    UNSUPPORTED_TRANSLATION_SCRIPT_PATTERN.test(normalizeLine(transcript));

const shouldTranslateAutotranslatorTranscript = (transcript: string): boolean =>
    inferAutotranslatorPreferredTargetLanguage(transcript) !== null;

const inferAutotranslatorDominantDisplayLanguage = (text: string): AutotranslatorTargetLanguage | null => {
    const splitLanguage = getDominantAutotranslatorSplitLanguage(text);

    if (splitLanguage) {
        return splitLanguage;
    }

    const language = inferAutotranslatorTextLanguage(text);

    return language === "en" || language === "ko" ? language : null;
};

const canDisplayAutotranslatorInterpretation = (transcript: string, interpretation: string): boolean => {
    const preferredTarget = inferAutotranslatorPreferredTargetLanguage(transcript);
    const interpretationLanguage = inferAutotranslatorDominantDisplayLanguage(interpretation);

    return Boolean(interpretationLanguage && (!preferredTarget || interpretationLanguage !== preferredTarget));
};

const isLikelyEnglishLiveTranscript = (transcript: string): boolean => {
    const normalized = normalizeLine(transcript);

    if (normalized.length < 10 || HANGUL_PATTERN.test(normalized) || hasUnsupportedAutotranslatorScript(normalized)) {
        return false;
    }

    const words = normalized.match(LATIN_WORD_GLOBAL_PATTERN) ?? [];

    if (words.length < 3) {
        return false;
    }

    const commonWords = normalized.match(COMMON_ENGLISH_WORD_PATTERN) ?? [];

    if (commonWords.length >= 2) {
        return true;
    }

    return normalized.length >= 32 && words.length >= 5 && /[.!?',]/.test(normalized);
};

export const parseAutotranslatorProperNounInput = (value: string): Omit<AutotranslatorProperNoun, "id"> | null => {
    const normalized = normalizeLine(value);

    if (!normalized) {
        return null;
    }

    const parts = normalized
        .split(/[\/|,]+/)
        .map((part) => normalizeLine(part).slice(0, 80))
        .filter(Boolean)
        .slice(0, 2);

    const first = parts[0] ?? "";
    const second = parts[1] ?? "";
    const english = [first, second].find((part) => part && !HANGUL_PATTERN.test(part)) ?? "";
    const korean = [first, second].find((part) => HANGUL_PATTERN.test(part)) ?? "";

    if (!english && !korean) {
        return null;
    }

    return completeAutotranslatorProperNounPair({ english, korean });
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const romanizedKoreanNameReplacementVariants = (english: string): string[] => {
    const variants = new Set([english]);
    const segments = findRomanizedKoreanNameSegments(english);

    if (segments && segments.romanizedParts.length > 1) {
        const titleParts = segments.romanizedParts.map(capitalizeRomanizedNamePart);
        variants.add(formatRomanizedKoreanName(segments.romanizedParts));
        variants.add(titleParts.join("-"));
        variants.add(titleParts.join(" "));
    }

    return [...variants].filter(Boolean);
};

const replaceKnownProperNounForm = (text: string, knownForm: string, replacement: string): string => {
    if (!knownForm || !replacement || knownForm === replacement) {
        return text;
    }

    if (HANGUL_PATTERN.test(knownForm)) {
        return text.replace(new RegExp(escapeRegExp(knownForm), "g"), replacement);
    }

    const latinPattern = new RegExp(
        `(^|[^A-Za-z])${knownForm.split(/\s+/).map(escapeRegExp).join("\\s+")}(?=$|[^A-Za-z])`,
        "giu"
    );

    return text.replace(latinPattern, (_match, prefix: string) => `${prefix}${replacement}`);
};

export const applyAutotranslatorProperNounGlossary = (
    translation: string,
    targetLanguage: AutotranslatorTargetLanguage,
    properNouns: readonly AutotranslatorProperNoun[]
): string => {
    let updated = translation;

    for (const noun of properNouns) {
        const completed = completeAutotranslatorProperNounPair(noun);

        if (!completed.english || !completed.korean) {
            continue;
        }

        if (targetLanguage === "ko") {
            for (const englishVariant of romanizedKoreanNameReplacementVariants(completed.english)) {
                updated = replaceKnownProperNounForm(updated, englishVariant, completed.korean);
            }
        } else {
            updated = replaceKnownProperNounForm(updated, completed.korean, completed.english);
        }
    }

    return normalizeLine(updated);
};

const getAutotranslatorError = (payload: unknown, fallback: string): string => {
    if (payload && typeof payload === "object" && typeof (payload as { error?: unknown }).error === "string") {
        return (payload as { error: string }).error;
    }

    return fallback;
};

const renderNounLabel = (noun: AutotranslatorProperNoun): string => {
    const english = noun.english ? escapeHtml(noun.english) : "";
    const korean = noun.korean ? escapeHtml(noun.korean) : "";

    if (english && korean) {
        return `${english}<span aria-hidden="true">/</span>${korean}`;
    }

    return english || korean;
};

const renderNounToggleLabel = (state: AutotranslatorState): string =>
    state.properNouns.length > 0
        ? t("autotranslatorNounCount", { count: String(state.properNouns.length) })
        : t("autotranslatorAddNoun");

const nounDraftPair = (state: AutotranslatorState) => {
    const parsed = parseAutotranslatorProperNounInput(state.nounInput);
    return completeAutotranslatorProperNounPair({ english: parsed?.english ?? "", korean: normalizeLine(state.nounKoreanInput) || parsed?.korean || "" });
};

const renderNounEditor = (state: AutotranslatorState): string => {
    const draft = nounDraftPair(state);
    return `<aside class="autotranslator-noun-drawer" data-autotranslator-region="noun-drawer" role="dialog"
        aria-modal="false" aria-label="${escapeHtml(t("autotranslatorProperNouns"))}">
        <header class="autotranslator-noun-heading">
            <strong>${escapeHtml(t("autotranslatorProperNouns"))}</strong>
            <span class="autotranslator-noun-total">${state.properNouns.length}/40</span>
            <button class="autotranslator-noun-close" type="button" data-autotranslator-close-noun-form
                aria-label="${escapeHtml(t("close"))}" title="${escapeHtml(t("close"))}">
                ${renderZiarcadiaIcon("close", { className: "autotranslator-noun-close-icon" })}
            </button>
        </header>
        <form class="autotranslator-noun-form" data-autotranslator-noun-form>
            <div class="autotranslator-noun-fields">
                <label>${escapeHtml(t("autotranslatorNameEnglish"))}
                    <input type="text" autocomplete="off" autocapitalize="words" spellcheck="false" maxlength="160"
                    enterkeyhint="done" placeholder="${escapeHtml(draft.english || "Jisu")}" value="${escapeHtml(state.nounInput)}"
                        data-autotranslator-noun-input />
                </label>
                <label>${escapeHtml(t("autotranslatorNameKorean"))}
                    <input type="text" autocomplete="off" spellcheck="false" maxlength="80" enterkeyhint="done"
                        placeholder="${escapeHtml(draft.korean || "지수")}" value="${escapeHtml(state.nounKoreanInput)}"
                        data-autotranslator-noun-korean-input />
                </label>
            </div>
            <div class="autotranslator-noun-save-row">
                <span class="autotranslator-noun-feedback${state.nounError ? " has-error" : ""}" data-autotranslator-noun-feedback aria-live="polite">
                    ${state.nounError ? escapeHtml(localizeRuntimeText(state.nounError)) : draft.english || draft.korean
                        ? escapeHtml(`${draft.english || "—"} / ${draft.korean || "—"}`) : ""}
                </span>
                ${state.nounEditingId ? `<button type="button" class="autotranslator-noun-cancel" data-autotranslator-cancel-noun-edit>${escapeHtml(t("autotranslatorNameCancel"))}</button>` : ""}
                <button class="autotranslator-noun-submit" type="submit" data-autotranslator-save-noun
                    ${!state.nounInput.trim() && !state.nounKoreanInput.trim() ? "disabled" : ""}>
                    ${escapeHtml(t(state.nounEditingId ? "autotranslatorNameSave" : "autotranslatorNameAdd"))}
                </button>
            </div>
        </form>
        <div class="autotranslator-noun-list" aria-label="${escapeHtml(t("autotranslatorProperNouns"))}">
            ${state.properNouns.map(noun => `<div class="autotranslator-noun-row${state.nounEditingId === noun.id ? " is-editing" : ""}" data-autotranslator-noun-row="${escapeHtml(noun.id)}">
                <button class="autotranslator-noun-chip" type="button" data-autotranslator-edit-noun="${escapeHtml(noun.id)}"
                    aria-pressed="${state.nounEditingId === noun.id}"
                    aria-label="${escapeHtml(t("autotranslatorNameEdit", { name: noun.english || noun.korean }))}" title="${escapeHtml(t("autotranslatorNameEdit", { name: noun.english || noun.korean }))}">
                    <span>${renderNounLabel(noun)}</span>
                    ${renderZiarcadiaIcon("edit", { className: "autotranslator-noun-edit-icon" })}
                </button>
                <button class="autotranslator-noun-remove" type="button" data-autotranslator-remove-noun="${escapeHtml(noun.id)}"
                    aria-label="${escapeHtml(t("autotranslatorNameRemove", { name: noun.english || noun.korean }))}" title="${escapeHtml(t("autotranslatorNameRemove", { name: noun.english || noun.korean }))}">
                    ${renderZiarcadiaIcon("close", { className: "autotranslator-noun-close-icon" })}
                </button>
            </div>`).join("") || `<p class="autotranslator-noun-empty">${escapeHtml(t("autotranslatorNameEmpty"))}</p>`}
        </div>
    </aside>`;
};

type AutotranslatorContextDiffPart = {
    text: string;
    changed: boolean;
    kind: "word" | "punctuation" | "other";
};

type AutotranslatorContextDiffToken = AutotranslatorContextDiffPart & {
    tokenIndex: number | null;
    matchKey: string;
};

const AUTOTRANSLATOR_CONTEXT_DIFF_TOKEN_PATTERN =
    /\s+|[A-Za-z0-9]+(?:['’][A-Za-z0-9]+)?|[\u3131-\u318e\uac00-\ud7a3]+|[^\sA-Za-z0-9\u3131-\u318e\uac00-\ud7a3]+/gu;
const AUTOTRANSLATOR_CONTEXT_DIFF_BROAD_CHANGE_RATIO = 0.55;
const AUTOTRANSLATOR_CONTEXT_DIFF_BROAD_CHANGE_MIN_TOKENS = 6;

const tokenizeAutotranslatorContextDiff = (text: string): AutotranslatorContextDiffToken[] => {
    const normalized = text.normalize("NFKC");
    const tokens: AutotranslatorContextDiffToken[] = [];
    const matches = normalized.matchAll(AUTOTRANSLATOR_CONTEXT_DIFF_TOKEN_PATTERN);
    let tokenIndex = 0;

    for (const match of matches) {
        const token = match[0] ?? "";
        const whitespace = /^\s+$/u.test(token);
        const word = /[A-Za-z0-9\u3131-\u318e\uac00-\ud7a3]/u.test(token);
        const punctuation = !whitespace && !word;
        const matchKey = whitespace ? "" : token.toLocaleLowerCase();

        tokens.push({
            text: token,
            changed: false,
            kind: punctuation ? "punctuation" : word ? "word" : "other",
            tokenIndex: whitespace ? null : tokenIndex,
            matchKey
        });

        if (!whitespace) {
            tokenIndex += 1;
        }
    }

    return tokens.length > 0 ? tokens : [{ text, changed: false, kind: "other", tokenIndex: text ? 0 : null, matchKey: text }];
};

const getAutotranslatorContextDiffMatches = (
    rawTokens: readonly AutotranslatorContextDiffToken[],
    displayedTokens: readonly AutotranslatorContextDiffToken[]
): Map<number, number> => {
    const rows = rawTokens.length + 1;
    const columns = displayedTokens.length + 1;
    const lengths = Array.from({ length: rows }, () => Array<number>(columns).fill(0));

    for (let rawIndex = rawTokens.length - 1; rawIndex >= 0; rawIndex -= 1) {
        for (let displayedIndex = displayedTokens.length - 1; displayedIndex >= 0; displayedIndex -= 1) {
            lengths[rawIndex][displayedIndex] =
                rawTokens[rawIndex]?.matchKey === displayedTokens[displayedIndex]?.matchKey
                    ? (lengths[rawIndex + 1]?.[displayedIndex + 1] ?? 0) + 1
                    : Math.max(lengths[rawIndex + 1]?.[displayedIndex] ?? 0, lengths[rawIndex]?.[displayedIndex + 1] ?? 0);
        }
    }

    const matches = new Map<number, number>();
    let rawIndex = 0;
    let displayedIndex = 0;

    while (rawIndex < rawTokens.length && displayedIndex < displayedTokens.length) {
        if (rawTokens[rawIndex]?.matchKey === displayedTokens[displayedIndex]?.matchKey) {
            matches.set(displayedIndex, rawIndex);
            rawIndex += 1;
            displayedIndex += 1;
        } else if (
            (lengths[rawIndex + 1]?.[displayedIndex] ?? 0) >=
            (lengths[rawIndex]?.[displayedIndex + 1] ?? 0)
        ) {
            rawIndex += 1;
        } else {
            displayedIndex += 1;
        }
    }

    return matches;
};

export const getAutotranslatorContextDiffParts = (
    displayedText: string,
    rawText: string,
    options: { highlightRewrite?: boolean } = {}
): AutotranslatorContextDiffPart[] => {
    const displayed = normalizeLine(displayedText);
    const raw = normalizeLine(rawText);

    if (!displayed || !raw || displayed === raw) {
        return [{ text: displayedText, changed: false, kind: "other" }];
    }

    const displayedTokens = tokenizeAutotranslatorContextDiff(displayed);
    const rawTokens = tokenizeAutotranslatorContextDiff(raw).filter((token) => token.tokenIndex !== null);
    const displayedMeaningfulTokens = displayedTokens.filter((token) => token.tokenIndex !== null);
    const matches = getAutotranslatorContextDiffMatches(rawTokens, displayedMeaningfulTokens);
    const changedTokenIndexes = new Set<number>();
    const lightChangedTokenIndexes = new Set<number>();

    displayedMeaningfulTokens.forEach((token, displayedTokenIndex) => {
        const rawTokenIndex = matches.get(displayedTokenIndex);
        const rawToken = rawTokenIndex === undefined ? null : rawTokens[rawTokenIndex];

        if (!rawToken) {
            changedTokenIndexes.add(displayedTokenIndex);
            if (token.kind === "punctuation") {
                lightChangedTokenIndexes.add(displayedTokenIndex);
            }
            return;
        }

        if (rawToken.text !== token.text) {
            changedTokenIndexes.add(displayedTokenIndex);
            lightChangedTokenIndexes.add(displayedTokenIndex);
        }
    });

    const meaningfulCount = Math.max(displayedMeaningfulTokens.length, 1);
    const broadChangeLimit = Math.max(
        AUTOTRANSLATOR_CONTEXT_DIFF_BROAD_CHANGE_MIN_TOKENS,
        Math.ceil(meaningfulCount * AUTOTRANSLATOR_CONTEXT_DIFF_BROAD_CHANGE_RATIO)
    );
    const safeChangedTokenIndexes =
        !options.highlightRewrite && (changedTokenIndexes.size >= meaningfulCount || changedTokenIndexes.size > broadChangeLimit)
            ? lightChangedTokenIndexes
            : changedTokenIndexes;

    return displayedTokens.map(({ text, kind, tokenIndex }) => ({
        text,
        kind,
        changed: tokenIndex !== null && safeChangedTokenIndexes.has(tokenIndex)
    }));
};

const renderAutotranslatorContextDiffText = (displayedText: string, rawText: string, highlightRewrite = false): string =>
    getAutotranslatorContextDiffParts(displayedText, rawText, { highlightRewrite })
        .map((part) => {
            const text = escapeHtml(part.text);

            return part.changed
                ? `<span class="autotranslator-context-diff-token is-${part.kind}">${text}</span>`
                : text;
        })
        .join("");

const renderAutotranslatorTextLine = (
    className: string,
    text: string,
    options: { extraClasses?: readonly string[]; contextComparison?: string; translationComparison?: string } = {}
): string => {
    const language = inferAutotranslatorTextLanguage(text);
    const extraClasses = options.extraClasses?.length ? ` ${options.extraClasses.join(" ")}` : "";
    const content = options.translationComparison
        ? renderAutotranslatorContextDiffText(text, options.translationComparison, true)
        : options.contextComparison ? renderAutotranslatorContextDiffText(text, options.contextComparison) : escapeHtml(text);
    return `<span class="${className} autotranslator-bubble-line is-${language}${extraClasses}">${content}</span>`;
};

export const resolveAutotranslatorDisplayedTranscript = (
    transcript: string,
    interpretation?: string,
    contextApplied = false,
    interpretationSource = transcript
): string => {
    const normalizedInterpretation = normalizeLine(interpretation ?? "");

    return contextApplied &&
        normalizedInterpretation &&
        normalizeLine(interpretationSource) === normalizeLine(transcript) &&
        canDisplayAutotranslatorInterpretation(transcript, normalizedInterpretation)
        ? normalizedInterpretation
        : transcript;
};

export const resolveAutotranslatorCanonicalTranscript = (
    transcript: string,
    interpretation?: string,
    contextApplied = false,
    interpretationSource = transcript
): { transcript: string; rawTranscript: string; applied: boolean } => {
    const normalizedTranscript = normalizeLine(transcript);
    const displayedTranscript = normalizeLine(
        resolveAutotranslatorDisplayedTranscript(
            normalizedTranscript,
            interpretation,
            contextApplied,
            interpretationSource
        )
    );

    if (!displayedTranscript || displayedTranscript === normalizedTranscript) {
        return {
            transcript: normalizedTranscript,
            rawTranscript: "",
            applied: false
        };
    }

    return {
        transcript: displayedTranscript,
        rawTranscript: normalizeLine(interpretationSource) || normalizedTranscript,
        applied: true
    };
};

const renderPendingDots = (): string => {
    const phase = Date.now() % AUTOTRANSLATOR_PENDING_DOT_ANIMATION_MS;
    const dots = [0, 1, 2]
        .map((index) => {
            const offset = index * AUTOTRANSLATOR_PENDING_DOT_STAGGER_MS;
            const delay = -((phase + AUTOTRANSLATOR_PENDING_DOT_ANIMATION_MS - offset) % AUTOTRANSLATOR_PENDING_DOT_ANIMATION_MS);

            return `<span style="animation-delay: ${delay}ms"></span>`;
        })
        .join("");

    return `<span class="autotranslator-pending-dots" aria-hidden="true">${dots}</span>`;
};

const renderPendingTranslationLine = (targetLanguage: AutotranslatorTextLanguage): string => `
    <span
        class="autotranslator-bubble-translation autotranslator-bubble-line is-${targetLanguage} is-pending"
        aria-label="${escapeHtml(t("autotranslatorTranslating"))}"
    >
        ${renderPendingDots()}
        <span class="visually-hidden">${escapeHtml(t("autotranslatorTranslating"))}</span>
    </span>
`;

const hasCurrentAutotranslatorTranslation = (bubble: AutotranslatorBubble): boolean =>
    Boolean(bubble.translation.trim()) &&
    normalizeLine(bubble.translatedTranscript ?? bubble.transcript) === normalizeLine(bubble.transcript);

const getCurrentAutotranslatorTranslation = (bubble: AutotranslatorBubble): string =>
    hasCurrentAutotranslatorTranslation(bubble) ? bubble.translation : "";

const setAutotranslatorBubbleTranslation = (bubble: AutotranslatorBubble, translation: string): void => {
    // Identical completion receipts and unrelated renders must keep the marks.
    if (normalizeLine(translation) !== normalizeLine(bubble.translation)) {
        bubble.translationComparison = normalizeLine(bubble.translation);
    }
    bubble.translation = translation;
};

const getRetainedAutotranslatorTranslation = (bubble: AutotranslatorBubble): string => {
    const retainedTranslation = normalizeLine(bubble.translation);

    if (
        hasCurrentAutotranslatorTranslation(bubble) ||
        (bubble.translationPending !== true && bubble.status !== "done") ||
        !retainedTranslation
    ) {
        return "";
    }

    const preferredTarget = inferAutotranslatorPreferredTargetLanguage(bubble.transcript);
    const retainedLanguage = inferAutotranslatorDominantDisplayLanguage(retainedTranslation);

    return preferredTarget && retainedLanguage && preferredTarget !== retainedLanguage ? "" : retainedTranslation;
};

const getCurrentAutotranslatorLiteralTranslation = (bubble: AutotranslatorBubble): string =>
    hasCurrentAutotranslatorTranslation(bubble) ? bubble.literalTranslation || bubble.translation : "";

const getAutotranslatorRawTranscript = (bubble: AutotranslatorBubble): string =>
    normalizeLine(bubble.rawTranscript ?? "");

const hasAutotranslatorInputCorrection = (bubble: AutotranslatorBubble, displayedTranscript: string): boolean => {
    const rawTranscript = getAutotranslatorRawTranscript(bubble);

    return rawTranscript.length > 0 && rawTranscript !== normalizeLine(displayedTranscript);
};

const renderBubble = (
    bubble: AutotranslatorBubble,
    options: {
        nextToFade?: boolean;
    } = {}
): string => {
    if (bubble.status === "pause") {
        return `
            <div class="autotranslator-pause">
                ${escapeHtml(t("autotranslatorPause", { seconds: String(bubble.pauseSeconds ?? 10) }))}
            </div>
        `;
    }

    if (bubble.status === "hearing") {
        return `
            <button
                class="autotranslator-bubble is-hearing${options.nextToFade ? " is-next-to-fade" : ""}"
                type="button"
                data-autotranslator-bubble="${escapeHtml(bubble.id)}"
                aria-label="${escapeHtml(t("autotranslatorHearing"))}"
            >
                <span class="autotranslator-bubble-content">
                    ${renderPendingDots()}
                    <span class="autotranslator-bubble-transcript visually-hidden">${escapeHtml(t("autotranslatorHearing"))}</span>
                </span>
            </button>
        `;
    }

    const currentTranslation = getCurrentAutotranslatorTranslation(bubble);
    const retainedTranslation = currentTranslation ? "" : getRetainedAutotranslatorTranslation(bubble);
    const translation =
        currentTranslation || retainedTranslation || (bubble.status === "error" ? t("autotranslatorRetryTranslation") : "");
    const displayedTranscript = resolveAutotranslatorDisplayedTranscript(
        bubble.transcript,
        bubble.interpretation,
        bubble.contextApplied,
        bubble.interpretationSource
    );
    const hasApplicableContext =
        displayedTranscript !== bubble.transcript || hasAutotranslatorInputCorrection(bubble, displayedTranscript);
    const targetLanguage = inferAutotranslatorPreferredTargetLanguage(bubble.transcript);
    const inputDirection = targetLanguage === "ko" ? "en" : targetLanguage === "en" ? "ko" : "unknown";
    const pendingTargetLanguage =
        inferAutotranslatorPreferredTargetLanguage(displayedTranscript || bubble.transcript) ?? "unknown";
    const pendingTranslation =
        !translation && bubble.translationPending === true
            ? renderPendingTranslationLine(pendingTargetLanguage)
            : "";

    return `
        <button
            class="autotranslator-bubble is-${bubble.status} is-${inputDirection}-input${hasApplicableContext ? " has-context" : ""}${
                options.nextToFade ? " is-next-to-fade" : ""
            }"
            type="button"
            data-autotranslator-bubble="${escapeHtml(bubble.id)}"
            ${bubble.status === "error" ? `aria-label="${escapeHtml(t("autotranslatorRetryTranslation"))}"` : ""}
        >
            <span class="autotranslator-bubble-content">
                ${renderAutotranslatorTextLine("autotranslator-bubble-transcript", displayedTranscript || t("autotranslatorListening"), {
                    contextComparison: hasApplicableContext ? getAutotranslatorRawTranscript(bubble) || bubble.transcript : ""
                })}
                ${
                    translation
                        ? renderAutotranslatorTextLine("autotranslator-bubble-translation", translation, {
                              translationComparison: bubble.status !== "error" ? bubble.translationComparison : "",
                              extraClasses: retainedTranslation
                                  ? ["is-retained", ...(bubble.translationPending ? ["is-pending"] : [])]
                                  : []
                          })
                        : pendingTranslation
                }
            </span>
        </button>
    `;
};

const renderExpandedBubble = (bubble: AutotranslatorBubble | null): string => {
    if (!bubble) {
        return "";
    }

    const displayedTranscript = resolveAutotranslatorDisplayedTranscript(
        bubble.transcript,
        bubble.interpretation,
        bubble.contextApplied,
        bubble.interpretationSource
    );
    const rawTranscript = getAutotranslatorRawTranscript(bubble);
    const heardTranscript = rawTranscript || bubble.transcript;
    const hasApplicableContext =
        displayedTranscript !== bubble.transcript || hasAutotranslatorInputCorrection(bubble, displayedTranscript);
    const translation = getCurrentAutotranslatorTranslation(bubble);
    const literalTranslation = getCurrentAutotranslatorLiteralTranslation(bubble);

    return `
            <div class="autotranslator-expanded" role="dialog" aria-modal="true">
                <button
                    class="autotranslator-expanded-scrim"
                    type="button"
                    data-autotranslator-close-expanded
                    aria-label="${escapeHtml(t("close"))}"
                ></button>
                <article class="autotranslator-expanded-panel">
                    <button
                        class="autotranslator-expanded-close"
                        type="button"
                        data-autotranslator-close-expanded
                        aria-label="${escapeHtml(t("close"))}"
                    >
                        ${escapeHtml(t("close"))}
                    </button>
                    <section class="autotranslator-expanded-primary">
                        <span>${escapeHtml(t("autotranslatorContextualTranslation"))}</span>
                        <p class="autotranslator-expanded-translation is-${inferAutotranslatorTextLanguage(
                            translation || t("autotranslatorTranslating")
                        )}">${translation && bubble.translationComparison
                            ? renderAutotranslatorContextDiffText(translation, bubble.translationComparison, true)
                            : escapeHtml(translation || t("autotranslatorTranslating"))}</p>
                    </section>
                    <div class="autotranslator-expanded-comparison">
                        <section>
                            <span>${escapeHtml(t("autotranslatorHeardText"))}</span>
                            <p class="autotranslator-expanded-transcript is-${inferAutotranslatorTextLanguage(
                                heardTranscript
                            )}">${escapeHtml(heardTranscript)}</p>
                        </section>
                        <section>
                            <span>${escapeHtml(t("autotranslatorLiteralTranslation"))}</span>
                            <p class="autotranslator-expanded-literal is-${inferAutotranslatorTextLanguage(
                                literalTranslation || t("autotranslatorTranslating")
                            )}">${escapeHtml(
                                literalTranslation || t("autotranslatorTranslating")
                            )}</p>
                        </section>
                    </div>
                    ${
                        hasApplicableContext
                            ? `
                                <section class="autotranslator-expanded-interpretation">
                                    <span>${escapeHtml(t("autotranslatorContextInterpretation"))}</span>
                                    <p class="is-${inferAutotranslatorTextLanguage(displayedTranscript)}">${renderAutotranslatorContextDiffText(
                                        displayedTranscript, heardTranscript
                                    )}</p>
                                </section>
                            `
                            : ""
                    }
                </article>
            </div>
        `;
};

const renderMarkup = (state: AutotranslatorState): string => {
    const elapsedMs = state.running && state.startedAt ? Date.now() - state.startedAt : state.elapsedMs;
    const summary = state.summary;
    const expandedBubble = state.bubbles.find((bubble) => bubble.id === state.expandedBubbleId) ?? null;
    const costNow = estimateAutotranslatorSessionCostUsd(elapsedMs, state.translationCostUsd);
    const visibleBubbles = state.bubbles.filter((bubble) => !isAutotranslatorPendingBubble(bubble));
    const hasPendingBubble = state.bubbles.length !== visibleBubbles.length;
    const nextExpiringBubbleId = state.following ? getAutotranslatorNextExpiringBubbleId(visibleBubbles) : null;
    const diagnosticLabel = t(state.diagnosticStatus === "recording" ? "autotranslatorDiagnosticRecording"
        : state.diagnosticStatus === "saving" ? "autotranslatorDiagnosticSaving"
        : state.diagnosticStatus === "saved" ? "autotranslatorDiagnosticSaved"
        : state.diagnosticStatus === "error" || state.diagnosticStatus === "unsupported" ? "autotranslatorDiagnosticUnavailable"
        : state.diagnosticStatus === "limit" ? "autotranslatorDiagnosticLimit"
        : state.diagnosticEnabled ? "autotranslatorDiagnosticOn" : "autotranslatorDiagnosticOff");

    return `
        <section class="autotranslator-tool${state.tableMode ? " is-table-mode" : ""}${
            state.audioMode === "far" ? " is-far-mode" : ""
        }${state.running ? " is-running" : ""}${state.connecting ? " is-connecting" : ""}" style="--autotranslator-speech-glow-cycle: ${SITE_TUNING.autotranslator.speechGlowCycleMs}ms; --autotranslator-speech-glow-fade: ${SITE_TUNING.autotranslator.speechGlowFadeMs}ms;">
            <div class="autotranslator-stage">
                <div class="autotranslator-stage-inner">
                    <header class="autotranslator-pagebar${state.nounFormOpen ? " is-noun-form-open" : ""}">
                        <div class="autotranslator-mode-toggle" role="group" aria-label="${escapeHtml(t("mode"))}">
                            <button
                                type="button"
                                class="${state.audioMode === "near" ? "is-selected" : ""}"
                                data-autotranslator-audio-mode="near"
                            >
                                ${escapeHtml(t("autotranslatorNearMode"))}
                            </button>
                            <button
                                type="button"
                                class="${state.audioMode === "table" ? "is-selected" : ""}"
                                data-autotranslator-audio-mode="table"
                            >
                                ${escapeHtml(t("autotranslatorTableMode"))}
                            </button>
                            <button
                                type="button"
                                class="${state.audioMode === "far" ? "is-selected" : ""}"
                                data-autotranslator-audio-mode="far"
                            >
                                ${escapeHtml(t("autotranslatorFarMode"))}
                            </button>
                        </div>
                        <div class="autotranslator-top-actions${state.nounFormOpen ? " is-noun-form-open" : ""}">
                            <button class="autotranslator-flip-button" type="button" data-autotranslator-flip>${escapeHtml(t("autotranslatorFlip"))}</button>
                            <button
                                class="autotranslator-icon-button autotranslator-noun-toggle"
                                type="button"
                                data-autotranslator-toggle-noun-form
                                aria-expanded="${state.nounFormOpen}"
                                aria-label="${escapeHtml(renderNounToggleLabel(state))}"
                                title="${escapeHtml(renderNounToggleLabel(state))}"
                            >
                                ${renderZiarcadiaIcon("wordBank", {
                                    className: "autotranslator-action-icon"
                                })}
                                ${
                                    state.properNouns.length > 0
                                        ? `<span class="autotranslator-noun-count">${escapeHtml(
                                            String(state.properNouns.length)
                                          )}</span>`
                                        : ""
                                }
                            </button>
                            <button
                                class="autotranslator-icon-button"
                                type="button"
                                data-autotranslator-clear
                                aria-label="${escapeHtml(t("autotranslatorClear"))}"
                                title="${escapeHtml(t("autotranslatorClear"))}"
                            >${renderZiarcadiaIcon("clear", {
                                className: "autotranslator-clear-icon"
                            })}</button>
                        </div>
                    </header>

                    ${
                        state.nounFormOpen
                            ? renderNounEditor(state)
                            : ""
                    }

                    <div class="autotranslator-feed-shell" data-autotranslator-region="feed-shell">
                        <main class="autotranslator-feed" data-autotranslator-feed aria-live="polite" tabindex="0">
                            ${
                                visibleBubbles.length > 0
                                    ? visibleBubbles
                                        .map((bubble) =>
                                            renderBubble(bubble, {
                                                nextToFade: bubble.id === nextExpiringBubbleId
                                            })
                                        )
                                        .join("")
                                    : hasPendingBubble || state.startupFeedbackActive
                                      ? ""
                                      : `<div class="autotranslator-empty">${escapeHtml(t("autotranslatorEmpty"))}</div>`
                            }
                        </main>
                        ${state.startupFeedbackActive ? `
                            <span class="autotranslator-startup-indicator" data-autotranslator-startup-indicator
                                role="status" aria-label="${escapeHtml(t("autotranslatorWarmingUp"))}">
                                ${renderPendingDots()}
                            </span>` : ""}
                        <span class="autotranslator-speech-glow${state.recognizedSpeechActive ? " is-active" : ""}" data-autotranslator-speech-glow aria-hidden="true"></span>
                    </div>

                    <footer class="autotranslator-control-dock" data-autotranslator-region="control-dock">
                        <button
                            class="autotranslator-start-button"
                            type="button"
                            data-autotranslator-start
                            aria-label="${escapeHtml(state.running || state.connecting ? t("autotranslatorStop") : t("autotranslatorStart"))}"
                        >
                            ${escapeHtml(state.running || state.connecting ? t("autotranslatorStop") : t("autotranslatorStart"))}
                        </button>
                        <button
                            class="autotranslator-follow-button"
                            type="button"
                            data-autotranslator-follow
                            ${state.following ? "hidden" : ""}
                        >
                            ${escapeHtml(t("autotranslatorFollow"))}
                        </button>
                        <div class="autotranslator-info-cluster">
                            <button
                                class="autotranslator-dock-toggle"
                                type="button"
                                data-autotranslator-toggle-details
                                aria-pressed="${state.detailsOpen}"
                                aria-label="${escapeHtml(t("autotranslatorSessionInfo"))}"
                                title="${escapeHtml(t("autotranslatorSessionInfo"))}"
                            >
                                ${escapeHtml(t("autotranslatorInfo"))}
                            </button>
                            <div class="autotranslator-details" ${state.detailsOpen ? "" : "hidden"}>
                                <button class="autotranslator-diagnostic-toggle" type="button" data-autotranslator-diagnostic-toggle
                                    aria-pressed="${state.diagnosticEnabled === true}" data-recording="${state.diagnosticStatus === "recording"}"
                                    data-status="${state.diagnosticStatus}"
                                    ${state.diagnosticEnabled === null || state.diagnosticBusy ? "disabled" : ""}
                                    aria-label="${escapeHtml(diagnosticLabel)}"
                                    title="${escapeHtml(diagnosticLabel)} · ${escapeHtml(t("autotranslatorDiagnosticRetention"))}">
                                    ${renderZiarcadiaIcon("audio", { className: "autotranslator-diagnostic-icon" })}
                                </button>
                                <button class="autotranslator-dock-toggle" type="button" data-autotranslator-diagnostic-delete
                                    aria-label="${escapeHtml(t("autotranslatorDiagnosticDelete"))}" title="${escapeHtml(t("autotranslatorDiagnosticDelete"))}"
                                    ${state.diagnosticBusy || state.diagnosticEnabled === null ? "disabled" : ""}>
                                    ${renderZiarcadiaIcon("clear", { className: "autotranslator-clear-icon" })}
                                </button>
                                <span data-autotranslator-elapsed>${escapeHtml(formatElapsed(elapsedMs))}</span>
                                <span data-autotranslator-cost title="${escapeHtml(meterCostTitle(state.estimatedTranslationCount))}">
                                    ${escapeHtml(formatCost(summary?.estimatedCostUsd ?? costNow))}
                                </span>
                            </div>
                        </div>
                    </footer>

                    ${
                        state.error
                            ? `<p class="autotranslator-error">${escapeHtml(localizeRuntimeText(state.error))}</p>`
                            : ""
                    }
                </div>
            </div>
            ${renderExpandedBubble(expandedBubble)}
        </section>
    `;
};

const waitForIceGathering = (peerConnection: RTCPeerConnection): Promise<void> =>
    new Promise((resolve) => {
        if (peerConnection.iceGatheringState === "complete") {
            resolve();
            return;
        }

        const timeout = window.setTimeout(() => {
            peerConnection.removeEventListener("icegatheringstatechange", handleStateChange);
            resolve();
        }, 1200);

        function handleStateChange() {
            if (peerConnection.iceGatheringState === "complete") {
                window.clearTimeout(timeout);
                peerConnection.removeEventListener("icegatheringstatechange", handleStateChange);
                resolve();
            }
        }

        peerConnection.addEventListener("icegatheringstatechange", handleStateChange);
    });

export const createAutotranslatorTool = (
    root: HTMLElement,
    state: AutotranslatorState
): AutotranslatorView => {
    type RealtimeConnection = {
        peerConnection: RTCPeerConnection;
        dataChannel: RTCDataChannel;
        manualCommitTimer: number | null;
    };

    type InFlightTranslation = {
        controller: AbortController;
        startedAt: number;
        firstPartialAt: number | null;
        text: string;
    };

    let realtimeConnections: RealtimeConnection[] = [];
    let rawLocalStream: MediaStream | null = null;
    let processedLocalStream: MediaStream | null = null;
    let diagnosticCapture: DiagnosticCapture | null = null;
    const diagnosticCaptures = new Set<DiagnosticCapture>();
    let diagnosticGeneration = 0;
    let diagnosticStarting = false;
    let diagnosticSettingRequest: Promise<void> | null = null;
    let diagnosticStartRequest: Promise<void> | null = null;
    let audioContext: AudioContext | null = null;
    let audioRecovery: Promise<void> | null = null;
    let analyser: AnalyserNode | null = null;
    let sendAnalyser: AnalyserNode | null = null;
    let sendMeterBuffer: Float32Array<ArrayBuffer> | null = null;
    let activeSendGain: GainNode | null = null;
    let activeCompressor: DynamicsCompressorNode | null = null;
    let meterBuffer: Float32Array<ArrayBuffer> | null = null;
    let speechDetector = createAutotranslatorSpeechDetector();
    let localSpeechTurn = 0;
    let localSpeechBoundaryAt = 0;
    let lastSpeechFrame = { noiseFloor: 0, threshold: 0, speech: false };
    const pendingCommitSpeechTurns: number[] = [];
    let meterFrame: number | null = null;
    let lastMeterLogAt = 0;
    let lastLocalSpeechAt = 0;
    let localSpeechStartedAt = 0;
    let lastManualCommitAt = 0;
    let lastCommittedSpeechAt = 0;
    let elapsedTimer: number | null = null;
    let backgroundStopTimer: number | null = null;
    let backgroundDeadline: number | null = null;
    let sessionGeneration = 0;
    let textCostMeter = new AutotranslatorTextMeter();
    let lastTextCostSyncAt = 0;
    const syncTextCostMeter = (meter: AutotranslatorTextMeter, force = true) => {
        // An older request may finish after Stop or a new Start. It can refine
        // its own run, but must never settle against a different run's total.
        if (meter !== textCostMeter) return;
        if (!force && Date.now() - lastTextCostSyncAt < 250) return;
        lastTextCostSyncAt = Date.now();
        state.translationCostUsd = meter.costUsd;
        state.translationCount = meter.requestCount;
        state.estimatedTranslationCount = meter.estimatedRequests;
        const elapsed = state.running && state.startedAt ? Date.now() - state.startedAt : state.elapsedMs;
        const total = estimateAutotranslatorSessionCostUsd(elapsed, state.translationCostUsd);
        if (state.summary) state.summary.estimatedCostUsd = total;
        const element = root.querySelector<HTMLElement>("[data-autotranslator-cost]");
        if (element) {
            element.title = meterCostTitle(state.estimatedTranslationCount);
            element.textContent = formatCost(total);
        }
    };
    let sessionStartController: AbortController | null = null;
    let speechGlowTimer: number | null = null;
    let startupFeedbackTimer: number | null = null;
    let wakeLock: WakeLockSentinelLike | null = null;
    let restoringScroll = false;
    let autoScrollingUntil = 0;
    let followScrollSettleTimer: number | null = null;
    let destroyed = false;
    let activeBubbleId: string | null = null;
    let liveTranscriptItemId: string | null = null;
    let lastContinuityBubbleId: string | null = null;
    let lastContinuityAt = 0;
    let autoStartAttempted = false;
    let speechRunOpen = false;
    let speechRunStopped = false;
    let inspectionPaused = false;
    const itemBubbleIds = new Map<string, string>();
    const translationTimers = new Map<string, number>();
    const translationSequences = new Map<string, number>();
    const inFlightTranslations = new Map<string, InFlightTranslation>();
    const queuedTranslations = new Set<string>();
    const lastTranslatedTexts = new Map<string, string>();
    const liveTranslationQueuedAt = new Map<string, number>();
    const deferredSplitTimers = new Map<string, number>();
    let bindingController: AbortController | null = null;
    const exitingBubbleSlots = new Map<string, { element: HTMLElement; beforeId: string | null }>();
    const bubbleLayoutFloors = new Map<string, { width: number; height: number; source: number; translation: number }>();
    const conversationHistory: Array<{ id: string; transcript: string; translation: string }> = [];
    const transcriptItems = new Map<string, AutotranslatorTranscriptItem>();
    const finalizedTranscriptItemIds = new Set<string>();
    const debugSessionId = createId();
    let debugSequence = 0;
    let transcriptItemOrder = 0;
    let remoteDebugQueue: AutotranslatorRemoteDebugEntry[] = [];
    let remoteDebugFlushTimer: number | null = null;
    let remoteDebugInFlight = false;
    let remoteDebugDroppedCount = 0;
    let versionRefreshPending = false;

    const clearStartupFeedback = () => {
        if (startupFeedbackTimer !== null) window.clearTimeout(startupFeedbackTimer);
        startupFeedbackTimer = null;
        state.startupFeedbackActive = false;
        root.querySelector("[data-autotranslator-startup-indicator]")?.remove();
    };

    const clearRecognizedSpeechGlow = () => {
        if (speechGlowTimer !== null) window.clearTimeout(speechGlowTimer);
        speechGlowTimer = null;
        state.recognizedSpeechActive = false;
        root.querySelector("[data-autotranslator-speech-glow]")?.classList.remove("is-active");
    };

    const noteRecognizedSpeech = (text: string) => {
        // Only new recognition owns this clock; volume and renders cannot replay it.
        if (!state.running || !isAutotranslatorRecognizedSpeech(text)) return;
        clearStartupFeedback();
        if (speechGlowTimer !== null) window.clearTimeout(speechGlowTimer);
        state.recognizedSpeechActive = true;
        root.querySelector("[data-autotranslator-speech-glow]")?.classList.add("is-active");
        speechGlowTimer = window.setTimeout(clearRecognizedSpeechGlow, SITE_TUNING.autotranslator.speechGlowHoldMs);
    };

    const updateFollowButton = () => {
        const followButton = root.querySelector<HTMLButtonElement>("[data-autotranslator-follow]");
        const feed = root.querySelector<HTMLElement>("[data-autotranslator-feed]");

        if (!followButton) {
            return;
        }

        if (!inspectionPaused && feed && feed.scrollHeight - feed.clientHeight <= FOLLOW_BOTTOM_THRESHOLD_PX) {
            state.following = true;
            state.unseenCount = 0;
        }

        followButton.hidden = state.following;
        followButton.textContent = t("autotranslatorFollow");
    };

    const scrollFeedToBottom = (feed: HTMLElement) => {
        autoScrollingUntil = Date.now() + AUTOTRANSLATOR_LAYOUT_MOTION_MS + 120;
        const setExactBottom = () => {
            feed.scrollTop = Math.max(0, feed.scrollHeight - feed.clientHeight);
        };
        setExactBottom();
        void feed.scrollTop;

        if (followScrollSettleTimer !== null) {
            window.cancelAnimationFrame(followScrollSettleTimer);
        }

        const settleStartedAt = performance.now();
        const keepFollowingBottom = () => {
            if (!state.following || !feed.isConnected) {
                followScrollSettleTimer = null;
                return;
            }

            setExactBottom();
            if (performance.now() - settleStartedAt < AUTOTRANSLATOR_LAYOUT_MOTION_MS + 80) {
                followScrollSettleTimer = window.requestAnimationFrame(keepFollowingBottom);
            } else {
                followScrollSettleTimer = null;
            }
        };
        followScrollSettleTimer = window.requestAnimationFrame(keepFollowingBottom);
    };

    const captureBubbleLayout = (): Map<string, { top: number; height: number; paint: Keyframe }> => {
        const snapshot = new Map<string, { top: number; height: number; paint: Keyframe }>();

        root.querySelectorAll<HTMLElement>("[data-autotranslator-bubble]").forEach((element) => {
            const id = element.getAttribute("data-autotranslator-bubble");

            if (id) {
                const rect = element.getBoundingClientRect();
                const style = getComputedStyle(element);
                snapshot.set(id, { top: rect.top, height: rect.height, paint: {
                    height: `${rect.height}px`, minHeight: "0px", paddingTop: style.paddingTop,
                    paddingBottom: style.paddingBottom, borderTopWidth: style.borderTopWidth,
                    borderBottomWidth: style.borderBottomWidth, marginTop: style.marginTop, opacity: style.opacity
                } });
            }
        });

        return snapshot;
    };

    const animateBubbleLayout = (previousBubbleLayout: Map<string, { top: number; height: number; paint: Keyframe }>) => {
        if (!state.following || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
        root.querySelectorAll<HTMLElement>("[data-autotranslator-bubble]").forEach((element) => {
            const id = element.getAttribute("data-autotranslator-bubble") ?? "";
            const previous = previousBubbleLayout.get(id);
            const height = element.getBoundingClientRect().height;
            const previousHeight = previous?.height ?? 0;
            if (Math.abs(height - previousHeight) <= 1) return;
            const style = getComputedStyle(element);
            element.style.overflow = "hidden";
            const animation = element.animate([
                previous?.paint ?? { height: "0px", minHeight: "0px", paddingTop: "0px", paddingBottom: "0px",
                    borderTopWidth: "0px", borderBottomWidth: "0px", marginTop: "-7px", opacity: 0 },
                { height: `${height}px`, minHeight: "0px",
                    paddingTop: style.paddingTop, paddingBottom: style.paddingBottom,
                    borderTopWidth: style.borderTopWidth, borderBottomWidth: style.borderBottomWidth,
                    marginTop: "0px", opacity: 1 }
            ], { duration: AUTOTRANSLATOR_LAYOUT_MOTION_MS, easing: "linear" });
            void animation.finished.catch(() => undefined).then(() => {
                if (element.isConnected && element.getAnimations().length === 0) element.style.overflow = "";
            });
        });
    };

    const preserveBubbleReadingSpace = () => {
        const retainedIds = new Set<string>();
        root.querySelectorAll<HTMLElement>("[data-autotranslator-bubble]").forEach(element => {
            const id = element.dataset.autotranslatorBubble!;
            retainedIds.add(id);
            const width = element.getBoundingClientRect().width;
            const saved = bubbleLayoutFloors.get(id);
            // Reflow at a new width is intentional; text corrections at the same
            // width must never reclaim space underneath the reader's eyes.
            const floor = saved && Math.abs(saved.width - width) < 1
                ? saved : { width, height: 0, source: 0, translation: 0 };
            for (const [selector, field] of [
                [".autotranslator-bubble-transcript:not(.visually-hidden)", "source"],
                [".autotranslator-bubble-translation", "translation"]
            ] as const) {
                const line = element.querySelector<HTMLElement>(selector);
                if (!line) continue;
                floor[field] = Math.max(floor[field], line.getBoundingClientRect().height);
                line.style.minHeight = `${floor[field]}px`;
            }
            floor.height = Math.max(floor.height, element.getBoundingClientRect().height);
            element.style.minHeight = `${floor.height}px`;
            bubbleLayoutFloors.set(id, floor);
        });
        for (const id of bubbleLayoutFloors.keys()) if (!retainedIds.has(id)) bubbleLayoutFloors.delete(id);
    };

    const render = () => {
        if (destroyed) {
            return;
        }

        const previousFeed = root.querySelector<HTMLElement>("[data-autotranslator-feed]");
        const previousBubbleLayout = captureBubbleLayout();
        const previousBubbleIds = [...previousBubbleLayout.keys()];
        const previousScrollTop = previousFeed?.scrollTop ?? 0;
        const previousScrollHeight = previousFeed?.scrollHeight ?? 0;
        const previousFeedHeight = previousFeed?.clientHeight ?? 0;
        const readingAnchor = !state.following && previousFeed
            ? [...previousBubbleLayout].find(([, rect]) => rect.top + rect.height > previousFeed.getBoundingClientRect().top)
            : undefined;
        root.querySelectorAll<HTMLElement>("[data-autotranslator-bubble]").forEach((element) => {
            element.getAnimations().forEach((animation) => animation.cancel());
        });
        updateAutotranslatorDom(root, renderMarkup(state));
        bind();
        syncNameViewport();
        preserveBubbleReadingSpace();

        const nextFeed = root.querySelector<HTMLElement>("[data-autotranslator-feed]");

        if (!nextFeed) {
            return;
        }

        // Evicted rows give up space as incoming rows grow. Empty slots have no
        // captions or hit targets, and cannot briefly exceed the visible card cap.
        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (!reducedMotion && state.following && state.bubbles.length > 0) {
            previousBubbleIds.forEach((id, index) => {
                if (root.querySelector(`[data-autotranslator-bubble="${id}"]`)) return;
                const slot = document.createElement("div");
                slot.style.cssText = "flex: 0 0 auto; pointer-events: none;";
                slot.setAttribute("aria-hidden", "true");
                const height = previousBubbleLayout.get(id)?.height ?? 0;
                slot.style.height = `${height}px`;
                const beforeId = previousBubbleIds.slice(index + 1).find(nextId => root.querySelector(`[data-autotranslator-bubble="${nextId}"]`)) ?? null;
                exitingBubbleSlots.set(id, { element: slot, beforeId });
                const animation = slot.animate([{ height: `${height}px`, marginBottom: "0px" }, { height: "0px", marginBottom: "-7px" }], {
                    duration: AUTOTRANSLATOR_LAYOUT_MOTION_MS, easing: "linear", fill: "forwards"
                });
                void animation.finished.catch(() => undefined).then(() => { slot.remove(); exitingBubbleSlots.delete(id); });
            });
            exitingBubbleSlots.forEach(({ element, beforeId }) => {
                const anchor = beforeId ? nextFeed.querySelector(`[data-autotranslator-bubble="${beforeId}"]`) : null;
                nextFeed.insertBefore(element, anchor);
            });
        } else {
            exitingBubbleSlots.forEach(({ element }) => { element.getAnimations().forEach(animation => animation.cancel()); element.remove(); });
            exitingBubbleSlots.clear();
        }

        restoringScroll = true;
        // Establish the animation's starting geometry before calculating scroll.
        // Scrolling to the final height first makes every live update jump.
        animateBubbleLayout(previousBubbleLayout);
        if (state.following) {
            const feedContentHeightChanged =
                !previousFeed || Math.abs(nextFeed.scrollHeight - previousScrollHeight) > 1 || Math.abs(nextFeed.clientHeight - previousFeedHeight) > 1;
            if (feedContentHeightChanged) {
                scrollFeedToBottom(nextFeed);
            } else {
                nextFeed.scrollTop = previousScrollTop;
            }
        } else {
            nextFeed.scrollTop = previousScrollTop;
            if (readingAnchor) {
                const anchor = root.querySelector<HTMLElement>(`[data-autotranslator-bubble="${readingAnchor[0]}"]`);
                if (anchor) nextFeed.scrollTop += anchor.getBoundingClientRect().top - readingAnchor[1].top;
            }
        }
        window.requestAnimationFrame(() => {
            restoringScroll = false;
            updateFollowButton();
        });
    };

    const setStatus = (status: string) => {
        state.status = status;
        render();
    };

    const setError = (message: string) => {
        clearStartupFeedback();
        state.error = message;
        state.status = t("autotranslatorReady");
        render();
    };

    const debugEnabled = (): boolean => {
        const queryValue = new URLSearchParams(window.location.search).get("autotranslatorDebug");

        if (queryValue === "0" || queryValue === "false" || queryValue === "off") {
            return false;
        }

        let storedValue: string | null = null;

        try {
            storedValue = localStorage.getItem(AUTOTRANSLATOR_DEBUG_STORAGE_KEY);
        } catch {
            storedValue = null;
        }

        return storedValue !== "0" && storedValue !== "false" && storedValue !== "off";
    };

    const remoteDebugEnabled = (): boolean => {
        const queryValue =
            new URLSearchParams(window.location.search).get("autotranslatorRemoteDebug") ??
            new URLSearchParams(window.location.search).get("autotranslatorDebugLog");

        if (queryValue === "0" || queryValue === "false" || queryValue === "off") {
            return false;
        }

        let storedValue: string | null = null;

        try {
            storedValue = localStorage.getItem(AUTOTRANSLATOR_REMOTE_DEBUG_STORAGE_KEY);
        } catch {
            storedValue = null;
        }

        return debugEnabled() && storedValue !== "0" && storedValue !== "false" && storedValue !== "off";
    };

    const shouldAutoStart = (): boolean => {
        const queryValue = new URLSearchParams(window.location.search).get("autotranslatorAutoStart");

        if (queryValue === "0" || queryValue === "false" || queryValue === "off") {
            return false;
        }

        let storedValue: string | null = null;

        try {
            storedValue = localStorage.getItem(AUTOTRANSLATOR_AUTOSTART_STORAGE_KEY);
        } catch {
            storedValue = null;
        }

        return storedValue !== "0" && storedValue !== "false" && storedValue !== "off";
    };

    const formatDebugValue = (value: AutotranslatorDebugValue): string =>
        String(value === null ? "null" : value)
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, REMOTE_DEBUG_MAX_VALUE_LENGTH);

    const formatDebugPayload = (values: Record<string, AutotranslatorDebugValue>): string =>
        Object.entries(values)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => `${key}=${formatDebugValue(value)}`)
            .join(" ")
            .slice(0, REMOTE_DEBUG_MAX_DETAIL_LENGTH);

    const buildRemoteDebugEntries = (limit = REMOTE_DEBUG_BATCH_SIZE): AutotranslatorRemoteDebugEntry[] => {
        if (remoteDebugQueue.length === 0) {
            return [];
        }

        return remoteDebugQueue.splice(0, limit);
    };

    const scheduleRemoteDebugFlush = (delayMs = REMOTE_DEBUG_FLUSH_MS) => {
        if (remoteDebugFlushTimer !== null || remoteDebugInFlight || remoteDebugQueue.length === 0) {
            return;
        }

        remoteDebugFlushTimer = window.setTimeout(() => {
            remoteDebugFlushTimer = null;
            void flushRemoteDebugLog();
        }, delayMs);
    };

    const flushRemoteDebugLog = async (preferBeacon = false): Promise<void> => {
        if (!remoteDebugEnabled() || remoteDebugInFlight || remoteDebugQueue.length === 0) {
            return;
        }

        if (remoteDebugFlushTimer !== null) {
            window.clearTimeout(remoteDebugFlushTimer);
            remoteDebugFlushTimer = null;
        }

        const entries = buildRemoteDebugEntries();

        if (entries.length === 0) {
            return;
        }

        const body = JSON.stringify({ entries });

        if (preferBeacon && navigator.sendBeacon) {
            const sent = navigator.sendBeacon(
                AUTOTRANSLATOR_REMOTE_DEBUG_PATH,
                new Blob([body], {
                    type: "application/json"
                })
            );

            if (sent) {
                return;
            }
        }

        remoteDebugInFlight = true;

        try {
            await fetch(AUTOTRANSLATOR_REMOTE_DEBUG_PATH, {
                method: "POST",
                credentials: "same-origin",
                keepalive: body.length < 60000,
                headers: {
                    "Content-Type": "application/json"
                },
                body
            });
        } catch {
            remoteDebugQueue = [...entries, ...remoteDebugQueue].slice(-REMOTE_DEBUG_MAX_QUEUE);
        } finally {
            remoteDebugInFlight = false;

            if (remoteDebugQueue.length > 0) {
                scheduleRemoteDebugFlush(120);
            }
        }
    };

    const queueRemoteDebugLog = (event: string, detail: string) => {
        if (!remoteDebugEnabled()) {
            return;
        }

        if (remoteDebugQueue.length >= REMOTE_DEBUG_MAX_QUEUE) {
            remoteDebugQueue.shift();
            remoteDebugDroppedCount += 1;
        }

        const dropped = remoteDebugDroppedCount > 0 ? ` dropped=${remoteDebugDroppedCount}` : "";
        remoteDebugDroppedCount = 0;
        remoteDebugQueue.push({
            time: Date.now(),
            sessionId: debugSessionId,
            sequence: (debugSequence += 1),
            event,
            detail: `${detail}${dropped}`.slice(0, REMOTE_DEBUG_MAX_DETAIL_LENGTH),
            page: `${window.location.pathname}${window.location.search}`.slice(0, 260)
        });

        if (remoteDebugQueue.length >= REMOTE_DEBUG_BATCH_SIZE) {
            void flushRemoteDebugLog();
        } else {
            scheduleRemoteDebugFlush();
        }
    };

    const debugLog = (event: string, values: Record<string, AutotranslatorDebugValue>) => {
        if (!debugEnabled()) {
            return;
        }

        const payload = formatDebugPayload(values);

        console.info("[Ziarcadia][Autotranslator]", `${event}${payload ? ` ${payload}` : ""}`);
        queueRemoteDebugLog(event, payload);
        diagnosticCapture?.note({ time: Date.now(), sequence: debugSequence, event, detail: payload });
    };

    const versionGuard = createAutotranslatorVersionGuard({
        buildId: typeof __ZIARCADIA_BUILD_ID__ === "string" ? __ZIARCADIA_BUILD_ID__ : "local",
        log: debugLog
    });
    const canRefreshIdleVersion = () => !destroyed && !state.running && !state.connecting &&
        !state.bubbles.length && !state.nounFormOpen && !state.nounInput.trim() && !state.nounKoreanInput.trim() && !state.diagnosticBusy &&
        !diagnosticStarting && !diagnosticCaptures.size && document.visibilityState === "visible";
    const checkIdleVersion = async () => {
        if (destroyed || versionRefreshPending || document.visibilityState !== "visible") return;
        const generation = sessionGeneration;
        if (await versionGuard.check() && generation === sessionGeneration && canRefreshIdleVersion()) {
            versionRefreshPending = versionGuard.refresh();
        }
    };

    const stopDiagnosticCapture = () => {
        diagnosticGeneration += 1;
        const capture = diagnosticCapture;
        diagnosticCapture = null;
        if (capture && state.diagnosticStatus === "recording") state.diagnosticStatus = "saving";
        if (capture) {
            const finished = capture.stop(); // Stops encoders before microphone tracks.
            void finished.finally(() => diagnosticCaptures.delete(capture));
            return finished;
        }
        return Promise.resolve();
    };

    const beginDiagnosticCapture = () => {
        if (!state.running || !state.diagnosticEnabled || diagnosticCapture || diagnosticStarting || !rawLocalStream || !processedLocalStream) return;
        const generation = ++diagnosticGeneration;
        diagnosticStarting = true;
        const isCurrent = () => !destroyed && state.running && state.diagnosticEnabled === true && generation === diagnosticGeneration;
        debugLog("diagnostic_capture_requested", { mode: state.audioMode });
        diagnosticStartRequest = startDiagnosticCapture({ raw: rawLocalStream, processed: processedLocalStream, debugSessionId, mode: state.audioMode,
            settings: { ...rawLocalStream.getAudioTracks()[0]?.getSettings?.(), gain: activeSendGain?.gain.value,
                compressorThreshold: activeCompressor?.threshold.value, compressorRatio: activeCompressor?.ratio.value,
                userAgent: navigator.userAgent, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                buildId: typeof __ZIARCADIA_BUILD_ID__ === "string" ? __ZIARCADIA_BUILD_ID__ : "local" },
            isCurrent,
            onDiagnostic: debugLog,
            onState(status) {
                const finishingStoppedRun = !destroyed && !state.running && !state.connecting &&
                    !diagnosticCapture && generation + 1 === diagnosticGeneration && state.diagnosticEnabled === true;
                if (isCurrent() || finishingStoppedRun) { state.diagnosticStatus = status; render(); }
            }
        }).then(async capture => {
            if (!capture) return;
            if (!isCurrent()) { await capture.stop(); return; }
            diagnosticCapture = capture;
            diagnosticCaptures.add(capture);
            capture.note({ time: Date.now(), sequence: debugSequence, event: "diagnostic_capture_started", detail: `mode=${state.audioMode}` });
        }).catch(() => {
            if (isCurrent()) { state.diagnosticStatus = "error"; render(); }
        }).finally(() => {
            diagnosticStarting = false;
            diagnosticStartRequest = null;
            // A new listening run may have started while an old encoder was
            // finishing startup. It must still get its own capture attempt.
            if (generation !== diagnosticGeneration && state.running) beginDiagnosticCapture();
        });
    };

    const loadDiagnosticSetting = (): Promise<void> => {
        if (diagnosticSettingRequest) return diagnosticSettingRequest;
        diagnosticSettingRequest = (async () => {
            for (let attempt = 1; attempt <= 3 && !destroyed; attempt++) {
                let status = 0;
                try {
                    const response = await fetch(AUTOTRANSLATOR_DIAGNOSTIC_PATH, { credentials: "same-origin", signal: AbortSignal.timeout(10_000) });
                    status = response.status;
                    if (!response.ok) throw new Error("Diagnostic setting unavailable");
                    const result = await response.json() as { enabled?: boolean };
                    if (typeof result.enabled !== "boolean") throw new Error("Invalid diagnostic setting");
                    if (destroyed) return;
                    state.diagnosticEnabled = result.enabled;
                    state.diagnosticStatus = "ready";
                    debugLog("diagnostic_setting_ready", { enabled: result.enabled, attempt });
                    render();
                    beginDiagnosticCapture();
                    return;
                } catch (error) {
                    if (destroyed) return;
                    debugLog("diagnostic_setting_failed", { attempt, status, error: error instanceof Error ? error.name : "Error" });
                    if (attempt < 3 && (!status || status === 401 || status === 429 || status >= 500)) {
                        await new Promise(resolve => window.setTimeout(resolve, attempt * 500));
                        continue;
                    }
                    state.diagnosticStatus = "error"; render(); return;
                }
            }
        })().finally(() => { diagnosticSettingRequest = null; });
        return diagnosticSettingRequest;
    };

    const handleAutotranslatorPageHide = () => {
        void stopSession(false, "page_hidden");
        void flushRemoteDebugLog(true);
    };
    window.addEventListener("pagehide", handleAutotranslatorPageHide);

    const findBubbleById = (bubbleId: string | null): AutotranslatorBubble | null =>
        bubbleId ? state.bubbles.find((bubble) => bubble.id === bubbleId) ?? null : null;

    const isContinuableBubble = (bubble: AutotranslatorBubble): boolean =>
        bubble.status !== "error" && bubble.status !== "pause";

    const getBubbleContinuationBlockReason = (bubble: AutotranslatorBubble): string | null => {
        const transcript = normalizeLine(bubble.transcript);

        if (!transcript || bubble.status === "hearing") {
            return null;
        }

        const wordCount = transcript.match(LATIN_WORD_GLOBAL_PATTERN)?.length ?? 0;

        if (
            transcript.length > LIVE_TRANSCRIPTION_CONTINUATION_MAX_CHARS ||
            wordCount > LIVE_TRANSCRIPTION_CONTINUATION_MAX_WORDS
        ) {
            return "long_bubble";
        }

        if (SENTENCE_END_PATTERN.test(transcript) && !TRAILING_CONTINUATION_WORD_PATTERN.test(transcript)) {
            return "sentence_complete";
        }

        return null;
    };

    const canAppendNewItemToBubble = (bubble: AutotranslatorBubble): boolean =>
        isContinuableBubble(bubble) && getBubbleContinuationBlockReason(bubble) === null;

    const clearTranscriptContinuity = (bubbleId?: string) => {
        if (bubbleId && lastContinuityBubbleId !== bubbleId) {
            return;
        }

        lastContinuityBubbleId = null;
        lastContinuityAt = 0;
    };

    const rememberTranscriptContinuity = (bubble: AutotranslatorBubble) => {
        if (!isContinuableBubble(bubble)) {
            clearTranscriptContinuity(bubble.id);
            return;
        }

        lastContinuityBubbleId = bubble.id;
        lastContinuityAt = Date.now();
    };

    const getRecentContinuityBubble = (): AutotranslatorBubble | null => {
        const now = Date.now();
        const activeBubble = findBubbleById(activeBubbleId);

        if (
            activeBubble &&
            activeBubble.id === lastContinuityBubbleId &&
            canAppendNewItemToBubble(activeBubble) &&
            now - lastContinuityAt <= LIVE_TRANSCRIPTION_CONTINUITY_MS
        ) {
            return activeBubble;
        }

        const continuityBubble = findBubbleById(lastContinuityBubbleId);

        if (
            continuityBubble &&
            canAppendNewItemToBubble(continuityBubble) &&
            now - lastContinuityAt <= LIVE_TRANSCRIPTION_CONTINUITY_MS
        ) {
            return continuityBubble;
        }

        clearTranscriptContinuity();
        return null;
    };

    const getRecentMappedBubble = (itemId: string | null): AutotranslatorBubble | null => {
        if (!itemId || finalizedTranscriptItemIds.has(itemId)) {
            return null;
        }

        const bubble = findBubbleById(itemBubbleIds.get(itemId) ?? null);

        if (!bubble || !canAppendNewItemToBubble(bubble)) {
            return null;
        }

        const ageMs =
            bubble.id === lastContinuityBubbleId && lastContinuityAt
                ? Date.now() - lastContinuityAt
                : Date.now() - bubble.updatedAt;

        return ageMs <= LIVE_TRANSCRIPTION_CONTINUITY_MS ? bubble : null;
    };

    const bumpUnseen = () => {
        if (!state.following) {
            state.unseenCount += 1;
        }
    };

    let suppressFollowingBubbleLimit = false;

    const markBubbleUpdated = (bubble: AutotranslatorBubble) => {
        bubble.updatedAt = Date.now();

        if (!suppressFollowingBubbleLimit) {
            applyFollowingBubbleLimit();
        }
    };

    const clearDeferredBubbleSplit = (bubbleId: string) => {
        const timer = deferredSplitTimers.get(bubbleId);

        if (timer !== undefined) {
            window.clearTimeout(timer);
            deferredSplitTimers.delete(bubbleId);
        }
    };

    const markBubbleTranslationStale = (bubble: AutotranslatorBubble) => {
        clearDeferredBubbleSplit(bubble.id);
        const canonicalTranscript = normalizeLine(bubble.transcript);
        const rawTranscript = normalizeLine(bubble.rawTranscript ?? "");
        const interpretation = normalizeLine(bubble.interpretation ?? "");
        const interpretationSource = normalizeLine(bubble.interpretationSource ?? "");
        const correctionStillMatches =
            rawTranscript.length > 0 &&
            bubble.contextApplied === true &&
            interpretation.length > 0 &&
            rawTranscript === canonicalTranscript &&
            interpretationSource === canonicalTranscript;

        if (!correctionStillMatches) {
            bubble.rawTranscript = "";
            bubble.interpretation = "";
            bubble.interpretationSource = "";
            bubble.contextApplied = false;
        }

        if (!bubble.translation.trim() || hasCurrentAutotranslatorTranslation(bubble)) {
            return;
        }

        bubble.translationPending = shouldTranslateAutotranslatorTranscript(bubble.transcript);
        lastTranslatedTexts.delete(bubble.id);
    };

    const upsertBubble = (itemId: string): AutotranslatorBubble => {
        const mappedBubbleId = itemBubbleIds.get(itemId);
        const mappedBubble = mappedBubbleId ? state.bubbles.find((bubble) => bubble.id === mappedBubbleId) : null;
        const existing = mappedBubble ?? state.bubbles.find((bubble) => bubble.itemId === itemId);

        if (existing) {
            itemBubbleIds.set(itemId, existing.id);
            return existing;
        }

        const bubble: AutotranslatorBubble = {
            id: createId(),
            itemId,
            transcript: "",
            rawTranscript: "",
            translation: "",
            translatedTranscript: "",
            translationPending: false,
            literalTranslation: "",
            interpretation: "",
            interpretationSource: "",
            contextApplied: false,
            sourceLanguage: "unknown",
            status: "hearing",
            createdAt: Date.now(),
            updatedAt: Date.now()
        };

        state.bubbles.push(bubble);
        itemBubbleIds.set(itemId, bubble.id);
        bumpUnseen();
        debugLog("bubble_created", {
            bubble: bubble.id,
            item: itemId,
            active: activeBubbleId,
            live: liveTranscriptItemId,
            count: state.bubbles.length
        });

        return bubble;
    };

    const getMappedItemCountForBubble = (bubbleId: string): number => {
        let count = 0;

        itemBubbleIds.forEach((mappedBubbleId) => {
            if (mappedBubbleId === bubbleId) {
                count += 1;
            }
        });

        return count;
    };

    const getOrCreateTranscriptItem = (
        itemId: string,
        previousItemId: string | null = null
    ): AutotranslatorTranscriptItem => {
        const existing = transcriptItems.get(itemId);

        if (existing) {
            if (previousItemId && !existing.previousItemId) {
                existing.previousItemId = previousItemId;
            }

            return existing;
        }

        const item: AutotranslatorTranscriptItem = {
            itemId,
            speechTurn: localSpeechTurn,
            displayOffset: 0,
            languagePrefixIds: [],
            previousItemId,
            text: "",
            completed: false,
            order: (transcriptItemOrder += 1),
            updatedAt: Date.now()
        };
        transcriptItems.set(itemId, item);

        return item;
    };

    const getTranscriptItemIdsForBubble = (bubbleId: string): string[] => {
        const itemIds: string[] = [];

        itemBubbleIds.forEach((mappedBubbleId, itemId) => {
            if (mappedBubbleId === bubbleId && transcriptItems.has(itemId)) {
                itemIds.push(itemId);
            }
        });

        return itemIds.sort(
            (left, right) =>
                (transcriptItems.get(left)?.order ?? 0) - (transcriptItems.get(right)?.order ?? 0)
        );
    };

    const getBubbleTranscriptDocumentText = (bubbleId: string): string =>
        mergeAutotranslatorTranscriptDocumentTexts(
            getTranscriptItemIdsForBubble(bubbleId).map((itemId) => {
                const item = transcriptItems.get(itemId);
                return item ? item.text.slice(item.displayOffset) : "";
            })
        );

    const updateBubbleTranscriptFromDocument = (
        bubble: AutotranslatorBubble,
        reason: string,
        itemId: string
    ): boolean => {
        const documentText = getBubbleTranscriptDocumentText(bubble.id);

        if (!documentText) {
            return false;
        }

        const beforeTranscript = bubble.transcript;

        if (normalizeLine(beforeTranscript) === documentText) {
            return false;
        }

        bubble.transcript = documentText;
        markBubbleTranslationStale(bubble);
        debugLog("transcript_document_updated", {
            bubble: bubble.id,
            item: itemId,
            reason,
            before: beforeTranscript,
            after: bubble.transcript,
            mappedItems: getMappedItemCountForBubble(bubble.id)
        });

        return true;
    };

    const getOrCreateLiveTranslationBubble = (): AutotranslatorBubble => {
        const activeBubble = activeBubbleId
            ? state.bubbles.find((bubble) => bubble.id === activeBubbleId) ?? null
            : null;

        if (activeBubble) {
            return activeBubble;
        }

        liveTranscriptItemId ??= `transcript-live-${createId()}`;
        const bubble = upsertBubble(liveTranscriptItemId);
        activeBubbleId = bubble.id;

        return bubble;
    };

    const removeMergedBubble = (bubble: AutotranslatorBubble, targetBubbleId: string) => {
        state.bubbles = state.bubbles.filter((item) => item.id !== bubble.id);
        itemBubbleIds.forEach((bubbleId, itemId) => {
            if (bubbleId === bubble.id) {
                itemBubbleIds.set(itemId, targetBubbleId);
            }
        });

        const timer = translationTimers.get(bubble.id);

        if (timer !== undefined) {
            window.clearTimeout(timer);
            translationTimers.delete(bubble.id);
        }

        translationSequences.delete(bubble.id);
        inFlightTranslations.delete(bubble.id);
        queuedTranslations.delete(bubble.id);
        lastTranslatedTexts.delete(bubble.id);
        liveTranslationQueuedAt.delete(bubble.id);
        clearDeferredBubbleSplit(bubble.id);

        if (activeBubbleId === bubble.id) {
            activeBubbleId = targetBubbleId;
        }

        if (liveTranscriptItemId === bubble.itemId) {
            liveTranscriptItemId = null;
        }

        if (state.expandedBubbleId === bubble.id) {
            state.expandedBubbleId = targetBubbleId;
        }
    };

    const mergeBubbleIntoBubble = (
        sourceBubble: AutotranslatorBubble,
        targetBubble: AutotranslatorBubble,
        itemId: string,
        reason: string
    ): AutotranslatorBubble => {
        const beforeTranscript = targetBubble.transcript;

        if (sourceBubble.transcript) {
            targetBubble.transcript = mergeAutotranslatorTranscriptText(
                targetBubble.transcript,
                sourceBubble.transcript
            );
        }

        if (
            !getCurrentAutotranslatorTranslation(targetBubble) &&
            getCurrentAutotranslatorTranslation(sourceBubble) &&
            normalizeLine(sourceBubble.translatedTranscript ?? sourceBubble.transcript) ===
                normalizeLine(targetBubble.transcript)
        ) {
            targetBubble.translation = sourceBubble.translation;
            targetBubble.translatedTranscript = sourceBubble.translatedTranscript;
            targetBubble.literalTranslation = sourceBubble.literalTranslation;
            targetBubble.interpretation = sourceBubble.interpretation;
            targetBubble.interpretationSource = sourceBubble.interpretationSource;
            targetBubble.contextApplied = sourceBubble.contextApplied;
            targetBubble.sourceLanguage = sourceBubble.sourceLanguage;
        }

        markBubbleTranslationStale(targetBubble);

        if (sourceBubble.status !== "done" || targetBubble.status === "hearing") {
            targetBubble.status = sourceBubble.status;
        }

        targetBubble.updatedAt = Math.max(targetBubble.updatedAt, sourceBubble.updatedAt);
        itemBubbleIds.set(itemId, targetBubble.id);
        removeMergedBubble(sourceBubble, targetBubble.id);
        activeBubbleId = targetBubble.id;
        liveTranscriptItemId = null;
        rememberTranscriptContinuity(targetBubble);
        debugLog("bubble_merged", {
            source: sourceBubble.id,
            target: targetBubble.id,
            item: itemId,
            reason,
            before: beforeTranscript,
            sourceTranscript: sourceBubble.transcript,
            after: targetBubble.transcript,
            mappedItems: getMappedItemCountForBubble(targetBubble.id)
        });

        return targetBubble;
    };

    const attachCommittedItemToLiveBubble = (
        itemId: string,
        previousItemId: string | null = null,
        options: { createIfMissing?: boolean } = {}
    ): AutotranslatorBubble | null => {
        const mappedBubbleId = itemBubbleIds.get(itemId);
        const mappedBubble = mappedBubbleId ? state.bubbles.find((bubble) => bubble.id === mappedBubbleId) : null;
        const item = getOrCreateTranscriptItem(itemId, previousItemId);
        const acceptsItem = (candidate: AutotranslatorBubble | null) => {
            if (!candidate) return null;
            const previousItems = getTranscriptItemIdsForBubble(candidate.id).filter(id => id !== itemId);
            if (previousItems.some(id => transcriptItems.get(id)?.speechTurn !== item.speechTurn)) return null;
            const previousText = mergeAutotranslatorTranscriptDocumentTexts(previousItems.map(id => {
                const previous = transcriptItems.get(id);
                return previous ? previous.text.slice(previous.displayOffset) : "";
            }));
            if (startsAutotranslatorLanguageTurn(previousText, item.text.slice(item.displayOffset))) return null;
            return candidate;
        };
        const previousBubble = acceptsItem(getRecentMappedBubble(previousItemId));

        if (mappedBubble) {
            if (!acceptsItem(mappedBubble)) {
                itemBubbleIds.delete(itemId);
                updateBubbleTranscriptFromDocument(mappedBubble, "turn_boundary", itemId);
                clearTranscriptContinuity(mappedBubble.id);
                const separateBubble = upsertBubble(itemId);
                debugLog("bubble_turn_boundary", { item: itemId, previousBubble: mappedBubble.id,
                    bubble: separateBubble.id, speechTurn: item.speechTurn, reason: "pause_or_language" });
                if (mappedBubble.transcript) scheduleTranslation(mappedBubble.id, FINAL_TRANSLATE_DELAY_MS);
                return separateBubble;
            }
            if (previousBubble && previousBubble.id !== mappedBubble.id) {
                return mergeBubbleIntoBubble(mappedBubble, previousBubble, itemId, "previous_item_after_delta");
            }

            rememberTranscriptContinuity(mappedBubble);
            return mappedBubble;
        }

        const liveBubble =
            liveTranscriptItemId && activeBubbleId
                ? state.bubbles.find((bubble) => bubble.id === activeBubbleId && bubble.itemId === liveTranscriptItemId)
                : null;

        if (liveBubble) {
            itemBubbleIds.delete(liveBubble.itemId);
            liveBubble.itemId = itemId;
            itemBubbleIds.set(itemId, liveBubble.id);
            liveTranscriptItemId = null;
            activeBubbleId = liveBubble.id;
            debugLog("bubble_remapped", {
                bubble: liveBubble.id,
                item: itemId,
                count: state.bubbles.length
            });
            rememberTranscriptContinuity(liveBubble);
            return liveBubble;
        }

        if (previousBubble) {
            itemBubbleIds.set(itemId, previousBubble.id);
            activeBubbleId = previousBubble.id;
            liveTranscriptItemId = null;
            debugLog("bubble_item_joined", {
                bubble: previousBubble.id,
                item: itemId,
                previous: previousItemId,
                reason: "previous_item",
                mappedItems: getMappedItemCountForBubble(previousBubble.id)
            });
            rememberTranscriptContinuity(previousBubble);
            return previousBubble;
        }

        const continuityBubble = acceptsItem(getRecentContinuityBubble());

        if (continuityBubble) {
            itemBubbleIds.set(itemId, continuityBubble.id);
            activeBubbleId = continuityBubble.id;
            liveTranscriptItemId = null;
            debugLog("bubble_item_joined", {
                bubble: continuityBubble.id,
                item: itemId,
                previous: previousItemId,
                reason:
                    options.createIfMissing === false
                        ? "commit_continuity"
                        : speechRunOpen
                          ? "speech_run_continuity"
                          : "recent_continuity",
                mappedItems: getMappedItemCountForBubble(continuityBubble.id)
            });
            rememberTranscriptContinuity(continuityBubble);
            return continuityBubble;
        }

        if (options.createIfMissing === false) {
            return null;
        }

        const bubble = upsertBubble(itemId);
        rememberTranscriptContinuity(bubble);
        return bubble;
    };

    const moveBubbleAfterPreviousItem = (itemId: string, previousItemId: string | null) => {
        if (!previousItemId) {
            return;
        }

        const currentIndex = state.bubbles.findIndex((bubble) => bubble.itemId === itemId);
        const previousIndex = state.bubbles.findIndex((bubble) => bubble.itemId === previousItemId);

        if (currentIndex < 0 || previousIndex < 0 || currentIndex === previousIndex + 1) {
            return;
        }

        const [bubble] = state.bubbles.splice(currentIndex, 1);

        if (!bubble) {
            return;
        }

        const adjustedPreviousIndex = state.bubbles.findIndex((candidate) => candidate.itemId === previousItemId);
        state.bubbles.splice(adjustedPreviousIndex + 1, 0, bubble);
    };

    const separateLiveLanguageTurn = (itemId: string) => {
        const item = transcriptItems.get(itemId);
        const bubble = findBubbleById(itemBubbleIds.get(itemId) ?? null);
        if (!item || !bubble) return;
        const remaining = item.text.slice(item.displayOffset);
        const boundary = findAutotranslatorMixedLanguageSplitIndex(remaining);
        if (boundary === null) return;
        const prefixId = `${itemId}:language:${item.displayOffset}`;
        const prefix = getOrCreateTranscriptItem(prefixId);
        prefix.text = remaining.slice(0, boundary).trim();
        prefix.completed = true;
        prefix.speechTurn = item.speechTurn;
        prefix.order = item.order - 1 / (item.displayOffset + 2);
        itemBubbleIds.set(prefixId, bubble.id);
        item.languagePrefixIds.push(prefixId);
        itemBubbleIds.delete(itemId);
        if (bubble.itemId === itemId) bubble.itemId = prefixId;
        item.displayOffset += boundary;
        updateBubbleTranscriptFromDocument(bubble, "live_language_boundary", prefixId);
        clearTranscriptContinuity();
        abortInFlightTranslation(bubble.id, "language_boundary");
        scheduleTranslation(bubble.id, FINAL_TRANSLATE_DELAY_MS);
        const reply = upsertBubble(itemId);
        activeBubbleId = reply.id;
        debugLog("bubble_turn_boundary", { item: itemId, previousBubble: bubble.id, bubble: reply.id,
            speechTurn: item.speechTurn, reason: "live_language_change" });
    };

    const applyAudioModeToPipeline = (options: { logEvent?: string } = {}) => {
        if (!activeSendGain || !activeCompressor) {
            return;
        }

        const tuning = resolveAutotranslatorAudioTuning(state.audioMode);
        const contextTime = audioContext?.currentTime ?? 0;
        activeSendGain.gain.cancelScheduledValues(contextTime);
        activeSendGain.gain.setTargetAtTime(tuning.sendGain, contextTime, 0.025);
        activeCompressor.threshold.setValueAtTime(tuning.compressorThreshold, contextTime);
        activeCompressor.knee.setValueAtTime(tuning.compressorKnee, contextTime);
        activeCompressor.ratio.setValueAtTime(tuning.compressorRatio, contextTime);
        activeCompressor.attack.setValueAtTime(tuning.compressorAttack, contextTime);
        activeCompressor.release.setValueAtTime(tuning.compressorRelease, contextTime);

        if (options.logEvent) {
            debugLog(options.logEvent, {
                mode: state.audioMode,
                gain: tuning.sendGain.toFixed(2),
                threshold: tuning.compressorThreshold,
                ratio: tuning.compressorRatio
            });
        }
    };

    const stopMeter = () => {
        if (meterFrame !== null) {
            window.cancelAnimationFrame(meterFrame);
            meterFrame = null;
        }

        analyser = null;
        sendAnalyser = null;
        sendMeterBuffer = null;
        activeSendGain = null;
        activeCompressor = null;
        meterBuffer = null;
        speechDetector = createAutotranslatorSpeechDetector();
        localSpeechTurn = 0;
        localSpeechBoundaryAt = 0;
        pendingCommitSpeechTurns.length = 0;
        lastMeterLogAt = 0;
        lastLocalSpeechAt = 0;
        localSpeechStartedAt = 0;
        lastManualCommitAt = 0;
        lastCommittedSpeechAt = 0;
        state.micLevel = 0;

        if (audioContext) {
            audioContext.onstatechange = null;
            void audioContext.close().catch(() => undefined);
            audioContext = null;
        }
    };

    const buildProcessedAudioStream = (stream: MediaStream): MediaStream => {
        try {
            const AudioContextConstructor =
                window.AudioContext ||
                (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

            if (!AudioContextConstructor) {
                return stream;
            }

            audioContext ??= new AudioContextConstructor();
            const source = audioContext.createMediaStreamSource(stream);
            const highPass = audioContext.createBiquadFilter();
            highPass.type = "highpass";
            highPass.frequency.value = 120;
            const lowPass = audioContext.createBiquadFilter();
            lowPass.type = "lowpass";
            lowPass.frequency.value = 3800;
            const sendGain = audioContext.createGain();
            const compressor = audioContext.createDynamicsCompressor();
            const limiter = audioContext.createWaveShaper();
            limiter.curve = createAutotranslatorLimiterCurve();
            limiter.oversample = "2x";
            const outputGain = audioContext.createGain();
            outputGain.gain.value = AUTOTRANSLATOR_OUTPUT_GAIN;
            const destination = audioContext.createMediaStreamDestination();
            destination.channelCount = 1;
            destination.channelCountMode = "explicit";
            analyser = audioContext.createAnalyser();
            sendAnalyser = audioContext.createAnalyser();
            sendAnalyser.fftSize = 1024;
            sendMeterBuffer = new Float32Array(sendAnalyser.fftSize);
            activeSendGain = sendGain;
            activeCompressor = compressor;
            analyser.fftSize = 1024;
            meterBuffer = new Float32Array(analyser.fftSize);

            applyAudioModeToPipeline();

            source.connect(highPass);
            highPass.connect(lowPass);
            lowPass.connect(analyser);
            // Keep the full voice spectrum for ASR; the speech band is only
            // for local boundaries, where bass/music would mask pauses.
            source.connect(sendGain);
            sendGain.connect(compressor);
            compressor.connect(limiter);
            limiter.connect(outputGain);
            outputGain.connect(destination);
            outputGain.connect(sendAnalyser);

            const sourceSettings = stream.getAudioTracks()[0]?.getSettings?.();
            const tuning = resolveAutotranslatorAudioTuning(state.audioMode);
            debugLog("audio_pipeline_ready", {
                mode: state.audioMode,
                gain: tuning.sendGain.toFixed(2),
                threshold: tuning.compressorThreshold,
                ratio: tuning.compressorRatio,
                outputGain: AUTOTRANSLATOR_OUTPUT_GAIN,
                sampleRate: sourceSettings?.sampleRate ?? null,
                channelCount: sourceSettings?.channelCount ?? null,
                echoCancellation: sourceSettings?.echoCancellation ?? null,
                noiseSuppression: sourceSettings?.noiseSuppression ?? null,
                autoGainControl: sourceSettings?.autoGainControl ?? null
            });

            const updateMeter = () => {
                if (!analyser || !meterBuffer) {
                    return;
                }

                analyser.getFloatTimeDomainData(meterBuffer);
                let peak = 0;
                let squareSum = 0;

                for (const value of meterBuffer) {
                    const normalized = value;
                    const absolute = Math.abs(normalized);
                    peak = Math.max(peak, absolute);
                    squareSum += normalized * normalized;
                }

                const rms = Math.sqrt(squareSum / meterBuffer.length);
                state.micLevel = Math.min(1, Math.max(peak * 1.9, rms * 5.4));
                const tuning = resolveAutotranslatorAudioTuning(state.audioMode);
                const now = Date.now();

                const speechFrame = speechDetector.update(rms, now, tuning.minimumSpeechRms);
                lastSpeechFrame = speechFrame;
                if (speechFrame.speech) {
                    if (speechFrame.started || !lastLocalSpeechAt || now - lastLocalSpeechAt > LIVE_TRANSCRIPTION_SPEECH_GRACE_MS) {
                        localSpeechStartedAt = now;
                    }
                    lastLocalSpeechAt = now;
                    speechRunOpen = true;
                    speechRunStopped = false;
                }
                if (speechFrame.ended) {
                    localSpeechBoundaryAt = now;
                    localSpeechTurn++;
                    speechRunOpen = false;
                    speechRunStopped = true;
                    clearTranscriptContinuity();
                    debugLog("local_speech_boundary", { turn: localSpeechTurn, quietMs: now - lastLocalSpeechAt,
                        rawRms: rms.toFixed(5), noiseFloor: speechFrame.noiseFloor.toFixed(5), threshold: speechFrame.threshold.toFixed(5) });
                }

                if (now - lastMeterLogAt >= MIC_METER_LOG_INTERVAL_MS) {
                    lastMeterLogAt = now;
                    let sendPeak = 0, sendSquares = 0, clippedSamples = 0;
                    if (sendAnalyser && sendMeterBuffer) {
                        sendAnalyser.getFloatTimeDomainData(sendMeterBuffer);
                        for (const sample of sendMeterBuffer) {
                            sendPeak = Math.max(sendPeak, Math.abs(sample));
                            sendSquares += sample * sample;
                            if (Math.abs(sample) >= 0.999) clippedSamples++;
                        }
                    }
                    debugLog("mic_meter", {
                        mode: state.audioMode,
                        peak: peak.toFixed(3),
                        rms: rms.toFixed(3),
                        level: state.micLevel.toFixed(3),
                        contextState: audioContext?.state ?? "none", contextTime: audioContext?.currentTime.toFixed(3) ?? "none",
                        rawTrackState: rawLocalStream?.getAudioTracks()[0]?.readyState ?? "none",
                        rawTrackMuted: rawLocalStream?.getAudioTracks()[0]?.muted ?? false,
                        detector: "raw_adaptive", noiseFloor: speechFrame.noiseFloor.toFixed(5),
                        rawThreshold: speechFrame.threshold.toFixed(5), speech: speechFrame.speech,
                        sendPeak: sendPeak.toFixed(5), sendRms: Math.sqrt(sendSquares / (sendMeterBuffer?.length || 1)).toFixed(5),
                        clippedSamples, compressorReductionDb: typeof activeCompressor?.reduction === "number"
                            ? activeCompressor.reduction.toFixed(1) : "unknown"
                    });
                }

                meterFrame = window.requestAnimationFrame(updateMeter);
            };

            updateMeter();
            return destination.stream;
        } catch (error) {
            debugLog("audio_pipeline_fallback", { reason: error instanceof Error ? error.message : "unknown" });
            stopMeter();
            return stream;
        }
    };

    const ensureAudioContext = async (context: AudioContext, signal: AbortSignal, phase: string) => {
        try {
            const advancedMs = await waitForAutotranslatorAudioContext(context, signal, SITE_TUNING.autotranslator.audioReadyTimeoutMs);
            if (audioContext === context && !signal.aborted) debugLog("audio_context_ready", {
                phase, contextState: context.state, contextTime: context.currentTime.toFixed(3), advancedMs: Math.round(advancedMs)
            });
        } catch (error) {
            if (audioContext === context && !signal.aborted) debugLog("audio_context_failed", {
                phase, contextState: context.state, contextTime: context.currentTime.toFixed(3),
                reason: error instanceof AutotranslatorAudioContextError ? error.reason : "unknown"
            });
            throw error;
        }
    };

    const recoverAudioContext = () => {
        const context = audioContext, controller = sessionStartController, generation = sessionGeneration;
        if (!context || !controller || audioRecovery || !state.running || document.visibilityState !== "visible") return;
        const pending = ensureAudioContext(context, controller.signal, "recovery").catch(async error => {
            if (generation !== sessionGeneration || context !== audioContext || !state.running || document.visibilityState !== "visible") return;
            await stopSession(false, "audio_context_failed");
            if (generation + 1 === sessionGeneration && !destroyed && error instanceof AutotranslatorAudioContextError) setError(t("autotranslatorAudioNotReady"));
        }).finally(() => { if (audioRecovery === pending) audioRecovery = null; });
        audioRecovery = pending;
    };

    const releaseWakeLock = async () => {
        const lock = wakeLock;
        wakeLock = null;

        if (lock) {
            await lock.release().catch(() => undefined);
        }
    };

    const requestWakeLock = async (generation = sessionGeneration) => {
        const navigatorWithWakeLock = navigator as NavigatorWithWakeLock;

        if (!navigatorWithWakeLock.wakeLock || wakeLock || document.visibilityState !== "visible") {
            return;
        }

        const lock = await navigatorWithWakeLock.wakeLock.request("screen").catch(() => null);
        if (destroyed || generation !== sessionGeneration || (!state.running && !state.connecting) || document.visibilityState !== "visible") {
            await lock?.release().catch(() => undefined);
            return;
        }
        wakeLock = lock;
        lock?.addEventListener?.("release", () => {
            if (wakeLock === lock) wakeLock = null;
        });
    };

    const clearBackgroundDeadline = () => {
        if (backgroundStopTimer !== null) window.clearTimeout(backgroundStopTimer);
        backgroundStopTimer = null;
        backgroundDeadline = null;
    };

    const stopSession = async (summarize = true, reason = "manual") => {
        sessionGeneration += 1;
        sessionStartController?.abort();
        sessionStartController = null;
        clearBackgroundDeadline();
        clearStartupFeedback();
        clearRecognizedSpeechGlow();
        const wasStarted = state.startedAt !== null;
        const elapsedMs = state.startedAt ? Date.now() - state.startedAt : state.elapsedMs;

        // From the user's point of view, recording has already stopped the moment
        // they press the button. Flip the live flags first so any late channel
        // events cannot recreate "hearing" UI while teardown finishes.
        state.running = false;
        state.connecting = false;
        state.startedAt = null;
        diagnosticCapture?.note({ time: Date.now(), sequence: debugSequence, event: "diagnostic_capture_stopped", detail: `reason=${reason}` });
        void stopDiagnosticCapture();

        realtimeConnections.forEach(({ dataChannel, peerConnection, manualCommitTimer }) => {
            if (manualCommitTimer !== null) {
                window.clearInterval(manualCommitTimer);
            }
            dataChannel.close();
            peerConnection.close();
        });
        realtimeConnections = [];

        processedLocalStream?.getTracks().forEach((track) => track.stop());
        processedLocalStream = null;
        rawLocalStream?.getTracks().forEach((track) => track.stop());
        rawLocalStream = null;

        stopMeter();
        const wakeLockRelease = releaseWakeLock();

        if (elapsedTimer !== null) {
            window.clearInterval(elapsedTimer);
            elapsedTimer = null;
        }

        translationTimers.forEach((timer) => {
            window.clearTimeout(timer);
        });
        translationTimers.clear();
        deferredSplitTimers.forEach((timer) => {
            window.clearTimeout(timer);
        });
        deferredSplitTimers.clear();
        abortAllInFlightTranslations("session_stopped");
        inFlightTranslations.clear();
        queuedTranslations.clear();
        liveTranslationQueuedAt.clear();
        activeBubbleId = null;
        liveTranscriptItemId = null;
        clearTranscriptContinuity();
        speechRunOpen = false;
        speechRunStopped = false;

        const stoppedHearingBubbles = state.bubbles.filter((bubble) => bubble.status === "hearing");
        stoppedHearingBubbles.forEach((bubble) => {
            discardBubble(bubble, "session_stopped_hearing");
        });
        state.bubbles.forEach((bubble) => {
            if (bubble.translationPending || bubble.status === "live" || bubble.status === "translating") {
                bubble.translationPending = false;
                bubble.status = "done";
            }
        });

        state.elapsedMs = elapsedMs;
        state.status = t("autotranslatorReady");
        debugLog("session_stopped", {
            reason,
            removedHearingBubbles: stoppedHearingBubbles.length,
            remainingBubbles: state.bubbles.length
        });

        if (wasStarted && summarize && elapsedMs >= 1000) {
            state.summary = {
                seconds: Math.round(elapsedMs / 1000),
                estimatedCostUsd: estimateAutotranslatorSessionCostUsd(elapsedMs, state.translationCostUsd)
            };
        }

        render();
        await wakeLockRelease;
    };

    // Use wall time as well as a timer: a suspended mobile page may miss its
    // callback, but must never resume an expired microphone session on return.
    const stopExpiredBackgroundSession = (): boolean => {
        if (backgroundDeadline === null || Date.now() < backgroundDeadline) return false;
        debugLog("session_background_timeout", { graceMs: SITE_TUNING.autotranslator.backgroundGraceMs });
        void stopSession(true, "background_timeout");
        return true;
    };

    const handleAutotranslatorVisibility = () => {
        if (destroyed || stopExpiredBackgroundSession()) return;
        if (document.visibilityState === "visible") {
            clearBackgroundDeadline();
            if (state.running) void requestWakeLock();
            if (state.running) recoverAudioContext();
            void checkIdleVersion();
            return;
        }
        if ((!state.running && !state.connecting) || backgroundDeadline !== null) return;
        backgroundDeadline = Date.now() + SITE_TUNING.autotranslator.backgroundGraceMs;
        backgroundStopTimer = window.setTimeout(stopExpiredBackgroundSession, SITE_TUNING.autotranslator.backgroundGraceMs);
        debugLog("session_background_grace", { graceMs: SITE_TUNING.autotranslator.backgroundGraceMs });
    };
    document.addEventListener("visibilitychange", handleAutotranslatorVisibility);
    document.addEventListener("resume", handleAutotranslatorVisibility);

    const clearTranslationTimer = (bubbleId: string) => {
        const timer = translationTimers.get(bubbleId);

        if (timer !== undefined) {
            window.clearTimeout(timer);
            translationTimers.delete(bubbleId);
        }
    };

    const abortInFlightTranslation = (bubbleId: string, reason: string): boolean => {
        const inFlight = inFlightTranslations.get(bubbleId);

        if (!inFlight) {
            return false;
        }

        inFlight.controller.abort(reason);
        debugLog("translation_superseded", {
            bubble: bubbleId,
            reason,
            ageMs: Date.now() - inFlight.startedAt,
            partialAgeMs: inFlight.firstPartialAt === null ? null : Date.now() - inFlight.firstPartialAt,
            text: inFlight.text
        });
        return true;
    };

    const abortAllInFlightTranslations = (reason: string) => {
        [...inFlightTranslations.keys()].forEach((bubbleId) => {
            abortInFlightTranslation(bubbleId, reason);
        });
    };

    const discardBubble = (bubble: AutotranslatorBubble, reason = "removed") => {
        debugLog("bubble_discarded", {
            bubble: bubble.id,
            item: bubble.itemId,
            reason,
            status: bubble.status,
            transcript: bubble.transcript,
            rawTranscript: bubble.rawTranscript,
            translatedTranscript: bubble.translatedTranscript,
            translation: bubble.translation
        });
        if (reason === "follow_limit" && bubble.transcript.trim()) {
            conversationHistory.push({
                id: bubble.id,
                transcript: resolveAutotranslatorDisplayedTranscript(bubble.transcript, bubble.interpretation, bubble.contextApplied, bubble.interpretationSource),
                translation: getCurrentAutotranslatorTranslation(bubble)
            });
            conversationHistory.splice(0, Math.max(0, conversationHistory.length - AUTOTRANSLATOR_CONTEXT_MAX_TURNS));
        }
        state.bubbles = state.bubbles.filter((item) => item.id !== bubble.id);
        itemBubbleIds.forEach((bubbleId, itemId) => {
            if (bubbleId === bubble.id) {
                itemBubbleIds.delete(itemId);
                transcriptItems.delete(itemId);
                finalizedTranscriptItemIds.add(itemId);
            }
        });
        clearTranslationTimer(bubble.id);
        abortInFlightTranslation(bubble.id, `discard_${reason}`);
        translationSequences.delete(bubble.id);
        inFlightTranslations.delete(bubble.id);
        queuedTranslations.delete(bubble.id);
        lastTranslatedTexts.delete(bubble.id);
        liveTranslationQueuedAt.delete(bubble.id);
        clearDeferredBubbleSplit(bubble.id);

        if (activeBubbleId === bubble.id) {
            activeBubbleId = null;
        }

        clearTranscriptContinuity(bubble.id);

        if (liveTranscriptItemId === bubble.itemId) {
            liveTranscriptItemId = null;
        }

        if (state.expandedBubbleId === bubble.id) {
            state.expandedBubbleId = null;
        }
    };

    const pruneExtraHearingBubbles = (currentBubbleId: string | null = activeBubbleId) => {
        const hearingBubbles = state.bubbles.filter((bubble) => bubble.status === "hearing");

        if (hearingBubbles.length <= 1) {
            return;
        }

        const keepId = currentBubbleId ?? hearingBubbles[hearingBubbles.length - 1]?.id ?? null;

        hearingBubbles.forEach((bubble) => {
            if (bubble.id !== keepId) {
                discardBubble(bubble, "extra_hearing_bubble");
            }
        });
    };

    const applyFollowingBubbleLimit = () => {
        if (!state.following) {
            return;
        }

        const retainedBubbles = trimAutotranslatorBubblesForFollowing(state.bubbles);

        if (retainedBubbles.length === state.bubbles.length) {
            return;
        }

        const retainedIds = new Set(retainedBubbles.map((bubble) => bubble.id));
        [...state.bubbles].forEach((bubble) => {
            if (!retainedIds.has(bubble.id)) {
                discardBubble(bubble, "follow_limit");
            }
        });
    };

    const deferFollowingBubbleLimit = (callback: () => void) => {
        const wasSuppressed = suppressFollowingBubbleLimit;
        suppressFollowingBubbleLimit = true;

        try {
            callback();
        } finally {
            suppressFollowingBubbleLimit = wasSuppressed;

            if (!suppressFollowingBubbleLimit) {
                applyFollowingBubbleLimit();
            }
        }
    };

    const shouldRevealTranscript = (bubble: AutotranslatorBubble): boolean => {
        const preferredTarget = inferAutotranslatorPreferredTargetLanguage(bubble.transcript);

        if (preferredTarget === "en") {
            return true;
        }

        if (preferredTarget !== "ko") {
            return false;
        }

        return Boolean(
            getCurrentAutotranslatorTranslation(bubble) ||
                bubble.sourceLanguage === "en" ||
                bubble.sourceLanguage === "mixed" ||
                isLikelyEnglishLiveTranscript(bubble.transcript)
        );
    };

    const getTranslationContext = (bubbleId: string) => {
        const bubbleIndex = state.bubbles.findIndex((bubble) => bubble.id === bubbleId);
        const precedingBubbles = bubbleIndex >= 0 ? state.bubbles.slice(0, bubbleIndex) : state.bubbles;

        return [...conversationHistory, ...precedingBubbles]
            .filter(
                (bubble) =>
                    (!("status" in bubble) || bubble.status !== "pause") &&
                    bubble.transcript.trim().length > 0
            )
            .slice(-AUTOTRANSLATOR_CONTEXT_MAX_TURNS)
            .map((bubble) => ({
                transcript: "status" in bubble ? resolveAutotranslatorDisplayedTranscript(bubble.transcript, (bubble as AutotranslatorBubble).interpretation, (bubble as AutotranslatorBubble).contextApplied, (bubble as AutotranslatorBubble).interpretationSource) : bubble.transcript,
                translation: "status" in bubble ? getCurrentAutotranslatorTranslation(bubble as AutotranslatorBubble) : bubble.translation
            }));
    };

    const translateBubble = async (bubbleId: string) => {
        if (inFlightTranslations.has(bubbleId)) {
            queuedTranslations.add(bubbleId);
            debugLog("translation_queued", {
                bubble: bubbleId,
                reason: "in_flight"
            });
            return;
        }

        const bubble = state.bubbles.find((item) => item.id === bubbleId);

        if (!bubble || !bubble.transcript.trim()) {
            debugLog("translation_skipped", {
                bubble: bubbleId,
                reason: bubble ? "empty_transcript" : "missing_bubble"
            });
            return;
        }

        const text = normalizeLine(bubble.transcript);
        const alreadyTranslatedText = lastTranslatedTexts.get(bubbleId);

        if (alreadyTranslatedText === text && getCurrentAutotranslatorTranslation(bubble)) {
            debugLog("translation_skipped", {
                bubble: bubble.id,
                reason: "already_translated",
                text
            });
            bubble.translationPending = false;
            bubble.status = activeBubbleId === bubble.id ? "live" : "done";
            markBubbleUpdated(bubble);
            render();
            return;
        }

        const preferredTarget = inferAutotranslatorPreferredTargetLanguage(text);

        if (!preferredTarget) {
            bubble.translation = "";
            bubble.translationComparison = "";
            bubble.rawTranscript = "";
            bubble.translatedTranscript = "";
            bubble.translationPending = false;
            bubble.literalTranslation = "";
            bubble.interpretation = "";
            bubble.interpretationSource = "";
            bubble.contextApplied = false;
            bubble.sourceLanguage = "unknown";
            bubble.status = activeBubbleId === bubble.id ? "hearing" : "done";
            markBubbleUpdated(bubble);
            pruneExtraHearingBubbles(bubble.id);
            debugLog("translation_skipped", {
                bubble: bubble.id,
                reason: "unknown_target",
                text
            });
            render();
            return;
        }

        const sequence = (translationSequences.get(bubbleId) ?? 0) + 1;
        translationSequences.set(bubbleId, sequence);
        const controller = new AbortController();
        const requestStartedAt = Date.now();
        const requestContext = getTranslationContext(bubbleId);
        const requestTimeout = window.setTimeout(() => controller.abort("translation_timeout"), AUTOTRANSLATOR_TRANSLATION_TIMEOUT_MS);
        const requestMeter = textCostMeter;
        const meterId = requestMeter.begin(estimateAutotranslatorInputTokens(buildAutotranslatorTranslationPrompt(
            text.slice(0, 1400), state.properNouns.slice(0, 40).map(({ english, korean }) => ({ english, korean })), requestContext.map(turn => ({
                transcript: turn.transcript.slice(0, 420), translation: turn.translation.slice(0, 420)
            }))
        )));
        syncTextCostMeter(requestMeter);
        const inFlightTranslation: InFlightTranslation = {
            controller,
            startedAt: requestStartedAt,
            firstPartialAt: null,
            text
        };
        inFlightTranslations.set(bubbleId, inFlightTranslation);

        const revealWhileFetching = shouldRevealTranscript(bubble) || bubble.status === "translating";
        const currentTranslation = getCurrentAutotranslatorTranslation(bubble);

        bubble.translationPending = true;
        bubble.status = revealWhileFetching
            ? currentTranslation
                ? "live"
                : "translating"
            : "hearing";

        if (!revealWhileFetching) {
            pruneExtraHearingBubbles(bubble.id);
        }

        debugLog("translation_request", {
            bubble: bubble.id,
            sequence,
            target: preferredTarget,
            chars: text.length,
            contextTurns: requestContext.length,
            text
        });
        render();

        try {
            const response = await fetch("/api/autotranslator/translate", {
                method: "POST",
                credentials: "same-origin",
                signal: controller.signal,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream"
                },
                body: JSON.stringify({
                    text,
                    properNouns: state.properNouns.map(({ english, korean }) => ({ english, korean })),
                    context: requestContext
                })
            });
            let payload: TranslateResponse = {};
            if (response.ok && response.headers.get("Content-Type")?.includes("text/event-stream") && response.body) {
                let completed = false;
                let firstPartial = true;
                for await (const event of readAutotranslatorSse(response.body)) {
                    if (event.type === "translation.completed") {
                        completed = true;
                        payload = event.payload as TranslateResponse;
                        break;
                    }
                    if (event.type === "translation.progress") {
                        requestMeter.progress(meterId, event as unknown as AutotranslatorBillingProgress);
                        syncTextCostMeter(requestMeter, false);
                        continue;
                    }
                    if (controller.signal.aborted) continue;
                    if (event.type !== "translation.delta" || typeof event.translation !== "string") continue;
                    const currentText = normalizeLine(bubble.transcript);
                    if (!state.bubbles.includes(bubble) || translationSequences.get(bubbleId) !== sequence ||
                        (currentText !== text && !isAutotranslatorTranscriptExtension(text, currentText))) continue;
                    const partial = applyAutotranslatorProperNounGlossary(event.translation, preferredTarget, state.properNouns);
                    if (inferAutotranslatorDominantDisplayLanguage(partial) !== preferredTarget || hasUnsupportedAutotranslatorScript(partial)) continue;
                    setAutotranslatorBubbleTranslation(bubble, partial);
                    bubble.translatedTranscript = text;
                    bubble.translationPending = true;
                    bubble.sourceLanguage = typeof event.sourceLanguage === "string" ? event.sourceLanguage : "unknown";
                    bubble.status = "translating";
                    markBubbleUpdated(bubble);
                    render();
                    if (firstPartial) {
                        firstPartial = false;
                        inFlightTranslation.firstPartialAt = Date.now();
                        debugLog("translation_stream_first_text", { bubble: bubble.id, sequence,
                            latencyMs: Date.now() - requestStartedAt, chars: partial.length, target: preferredTarget });
                        if (currentText !== text) scheduleTranslation(bubbleId, LIVE_TRANSLATE_DELAY_MS, { live: true });
                    }
                }
                if (!completed) throw new Error("Translation stream ended before completion.");
            } else {
                payload = (await response.json().catch(() => ({}))) as TranslateResponse;
            }
            const billing = payload.billing;
            const rawUsage = readOpenAITextTokenUsage(payload.usage);
            if (payload.skipped === true || (!billing && [400, 401, 403, 404, 405, 413, 429, 503].includes(response.status))) {
                requestMeter.skip(meterId);
            } else if (billing) {
                requestMeter.settle(meterId, billing);
            } else if (rawUsage && typeof payload.model === "string") {
                requestMeter.settle(meterId, buildAutotranslatorTextBilling(payload.model, [payload.usage]));
            }
            syncTextCostMeter(requestMeter);

            debugLog("translation_billing", {
                bubble: bubble.id,
                sequence,
                model: billing?.model ?? payload.model ?? "unknown",
                pricing: billing?.pricingVersion ?? "fallback",
                requests: billing?.requestCount ?? 1,
                input: billing?.usage.inputTokens ?? rawUsage?.inputTokens ?? null,
                cached: billing?.usage.cachedInputTokens ?? rawUsage?.cachedInputTokens ?? null,
                cacheWrite: billing?.usage.cacheWriteTokens ?? rawUsage?.cacheWriteTokens ?? null,
                output: billing?.usage.outputTokens ?? rawUsage?.outputTokens ?? null,
                requestCostUsd: billing?.textCostUsd ?? null,
                estimatedRequests: requestMeter.estimatedRequests,
                sessionTextCostUsd: requestMeter.costUsd
            });

            if (requestMeter !== textCostMeter) return;

            if (!response.ok || typeof payload.translation !== "string") {
                throw new Error(getAutotranslatorError(payload, t("autotranslatorTranslateFailed")));
            }

            if (controller.signal.aborted) return;

            if (!state.bubbles.some((item) => item.id === bubbleId)) {
                debugLog("translation_discarded", {
                    bubble: bubble.id,
                    sequence,
                    reason: "bubble_replaced",
                    text
                });
                return;
            }

            if (translationSequences.get(bubbleId) !== sequence) {
                debugLog("translation_discarded", {
                    bubble: bubble.id,
                    sequence,
                    reason: "stale_sequence",
                    text
                });
                return;
            }

            const translation = applyAutotranslatorProperNounGlossary(
                payload.translation,
                preferredTarget,
                state.properNouns
            );
            const literalTranslation = applyAutotranslatorProperNounGlossary(
                typeof payload.literalTranslation === "string" ? payload.literalTranslation : payload.translation,
                preferredTarget,
                state.properNouns
            );
            const sourceLanguage = typeof payload.sourceLanguage === "string" ? payload.sourceLanguage : "unknown";
            const currentText = normalizeLine(bubble.transcript);

            if (currentText !== text) {
                if (
                    translation &&
                    sourceLanguage !== "unknown" &&
                    isAutotranslatorTranscriptExtension(text, currentText)
                ) {
                    const canonicalTranscript = resolveAutotranslatorCanonicalTranscript(
                        text,
                        typeof payload.interpretation === "string" ? payload.interpretation : "",
                        payload.contextApplied === true,
                        text
                    );

                    bubble.sourceLanguage = sourceLanguage;
                    setAutotranslatorBubbleTranslation(bubble, translation);
                    bubble.translatedTranscript = text;
                    bubble.translationPending = shouldTranslateAutotranslatorTranscript(bubble.transcript);
                    bubble.literalTranslation = literalTranslation;
                    bubble.rawTranscript = canonicalTranscript.rawTranscript;
                    bubble.interpretation = canonicalTranscript.applied ? canonicalTranscript.transcript : "";
                    bubble.interpretationSource = canonicalTranscript.applied ? text : "";
                    bubble.contextApplied = canonicalTranscript.applied;
                    bubble.status = activeBubbleId === bubble.id ? "translating" : "done";
                    lastTranslatedTexts.set(bubbleId, bubble.translatedTranscript);
                    markBubbleUpdated(bubble);
                    debugLog("translation_partial_ready", {
                        latencyMs: Date.now() - requestStartedAt,
                        bubble: bubble.id,
                        sequence,
                        source: bubble.sourceLanguage,
                        target: preferredTarget,
                        active: activeBubbleId === bubble.id,
                        contextApplied: bubble.contextApplied,
                        text,
                        current: currentText,
                        canonicalText: bubble.translatedTranscript,
                        rawText: bubble.rawTranscript,
                        translation
                    });
                    render();
                    return;
                }

                debugLog("translation_discarded", {
                    bubble: bubble.id,
                    sequence,
                    reason: "transcript_changed",
                    text,
                    current: currentText
                });
                bubble.translationPending = shouldTranslateAutotranslatorTranscript(bubble.transcript);
                bubble.status = shouldRevealTranscript(bubble) ? "translating" : "hearing";
                markBubbleUpdated(bubble);
                render();
                return;
            }

            bubble.sourceLanguage = sourceLanguage;
            if (!translation || bubble.sourceLanguage === "unknown") {
                debugLog("translation_discarded", {
                    bubble: bubble.id,
                    sequence,
                    reason: "empty_or_unknown",
                    source: bubble.sourceLanguage,
                    text,
                    translation
                });
                bubble.translationPending = false;
                bubble.rawTranscript = "";
                bubble.translatedTranscript = "";
                bubble.literalTranslation = "";
                bubble.interpretation = "";
                bubble.interpretationSource = "";
                bubble.contextApplied = false;
                discardBubble(bubble, "empty_or_unknown_translation");
                state.status = t("autotranslatorListening");
                render();
                return;
            }

            const canonicalTranscript = resolveAutotranslatorCanonicalTranscript(
                text,
                typeof payload.interpretation === "string" ? payload.interpretation : "",
                payload.contextApplied === true,
                text
            );
            bubble.rawTranscript = canonicalTranscript.rawTranscript;
            setAutotranslatorBubbleTranslation(bubble, translation);
            bubble.translatedTranscript = text;
            bubble.translationPending = false;
            bubble.literalTranslation = literalTranslation;
            bubble.interpretation = canonicalTranscript.applied ? canonicalTranscript.transcript : "";
            bubble.interpretationSource = canonicalTranscript.applied ? text : "";
            bubble.contextApplied = canonicalTranscript.applied;
            bubble.status = activeBubbleId === bubble.id ? "live" : "done";
            lastTranslatedTexts.set(bubbleId, bubble.transcript);
            markBubbleUpdated(bubble);
            debugLog("text_translation_ready", {
                latencyMs: Date.now() - requestStartedAt,
                bubble: bubble.id,
                chars: text.length,
                source: bubble.sourceLanguage,
                target: preferredTarget,
                active: activeBubbleId === bubble.id,
                contextApplied: bubble.contextApplied,
                interpretation: bubble.interpretation,
                text,
                canonicalText: bubble.transcript,
                rawText: bubble.rawTranscript,
                translation
            });
            render();
        } catch (error) {
            const aborted =
                (error instanceof Error && error.name === "AbortError") ||
                (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError");

            if ((aborted || controller.signal.aborted) && controller.signal.reason !== "translation_timeout") {
                // Teardown already settled the captions. An aborted request must
                // not resurrect pending dots, even after a new session starts.
                if (["session_stopped", "feed_cleared"].includes(String(controller.signal.reason))) return;
                if (state.bubbles.some((item) => item.id === bubbleId)) {
                    bubble.translationPending = shouldTranslateAutotranslatorTranscript(bubble.transcript);
                    bubble.status = shouldRevealTranscript(bubble) ? "translating" : "hearing";
                    markBubbleUpdated(bubble);
                    render();
                }
                return;
            }

            debugLog("translation_failed", {
                latencyMs: Date.now() - requestStartedAt,
                reason: controller.signal.aborted ? String(controller.signal.reason) : "request_failed",
                bubble: bubble.id,
                sequence,
                message: error instanceof Error ? error.message : "unknown",
                text
            });
            bubble.status = "error";
            bubble.translationPending = false;
            lastTranslatedTexts.set(bubbleId, text);
            markBubbleUpdated(bubble);
            render();
        } finally {
            syncTextCostMeter(requestMeter);
            window.clearTimeout(requestTimeout);
            if (inFlightTranslations.get(bubbleId)?.controller === controller) {
                inFlightTranslations.delete(bubbleId);
            }

            if (!state.bubbles.some((item) => item.id === bubbleId)) {
                queuedTranslations.delete(bubbleId);
                return;
            }

            if (controller.signal.aborted && ["session_stopped", "feed_cleared"].includes(String(controller.signal.reason))) {
                queuedTranslations.delete(bubbleId);
                return;
            }
            const acceptedTranslatedText = lastTranslatedTexts.get(bubbleId) ?? text;
            if (normalizeLine(bubble.transcript) !== acceptedTranslatedText) {
                queuedTranslations.add(bubbleId);
            } else {
                queuedTranslations.delete(bubbleId);
            }

            if (queuedTranslations.delete(bubbleId)) {
                void translateBubble(bubbleId);
            }
        }
    };

    // Keep an empty prefix request alive long enough to produce text. Once it
    // has, newer speech need not wait for the remaining context/review fields.
    const getInFlightTranslationDeadline = (inFlight: InFlightTranslation): number =>
        inFlight.firstPartialAt === null
            ? inFlight.startedAt + LIVE_TRANSLATE_STALE_ABORT_AFTER_MS
            : Math.min(inFlight.startedAt + LIVE_TRANSLATE_STALE_ABORT_AFTER_MS,
                inFlight.firstPartialAt + LIVE_TRANSLATE_MAX_WAIT_MS);

    const shouldSupersedeInFlightTranslation = (
        bubble: AutotranslatorBubble,
        inFlight: InFlightTranslation,
        options: { live?: boolean }
    ): boolean => {
        const currentText = normalizeLine(bubble.transcript);

        if (
            !currentText ||
            currentText === inFlight.text ||
            Date.now() < getInFlightTranslationDeadline(inFlight)
        ) {
            return false;
        }

        if (options.live !== true || inFlight.firstPartialAt !== null ||
            !isAutotranslatorTranscriptExtension(inFlight.text, currentText)) {
            return true;
        }

        const extraText = currentText.startsWith(inFlight.text) ? currentText.slice(inFlight.text.length) : currentText;
        const extraWords = extraText.match(LATIN_WORD_GLOBAL_PATTERN)?.length ?? 0;

        return (
            currentText.length - inFlight.text.length >= LIVE_TRANSLATE_STALE_MIN_EXTRA_CHARS ||
            extraWords >= LIVE_TRANSLATE_STALE_MIN_EXTRA_WORDS ||
            SENTENCE_END_PATTERN.test(currentText)
        );
    };

    const scheduleTranslation = (bubbleId: string, delayMs: number, options: { live?: boolean } = {}) => {
        const bubble = state.bubbles.find((item) => item.id === bubbleId);

        if (!bubble || !shouldTranslateAutotranslatorTranscript(bubble.transcript)) {
            debugLog("translation_schedule_skipped", {
                bubble: bubbleId,
                reason: bubble ? "not_translatable" : "missing_bubble",
                transcript: bubble?.transcript ?? ""
            });
            return;
        }

        if (!getCurrentAutotranslatorTranslation(bubble)) {
            const nextStatus = shouldRevealTranscript(bubble) ? "translating" : bubble.status;
            const changed = bubble.translationPending !== true || bubble.status !== nextStatus;
            bubble.translationPending = true;
            bubble.status = nextStatus;

            if (changed) {
                markBubbleUpdated(bubble);
                render();
            }
        }

        const inFlightTranslation = inFlightTranslations.get(bubbleId);

        if (inFlightTranslation && shouldSupersedeInFlightTranslation(bubble, inFlightTranslation, options)) {
            queuedTranslations.add(bubbleId);
            abortInFlightTranslation(bubbleId, options.live === true ? "live_text_advanced" : "final_text_ready");
            return;
        }

        if (inFlightTranslation) {
            queuedTranslations.add(bubbleId);
            // Let a useful prefix finish, but give the newest text a turn even
            // when no further ASR delta arrives to trigger supersession.
            clearTranslationTimer(bubbleId);
            translationTimers.set(bubbleId, window.setTimeout(() => {
                translationTimers.delete(bubbleId);
                if (inFlightTranslations.get(bubbleId) === inFlightTranslation &&
                    shouldSupersedeInFlightTranslation(bubble, inFlightTranslation, options)) {
                    abortInFlightTranslation(bubbleId, "queued_text_advanced");
                }
            }, Math.max(0, getInFlightTranslationDeadline(inFlightTranslation) - Date.now())));
            debugLog("translation_queued", {
                bubble: bubbleId,
                reason: "schedule_in_flight",
                transcript: bubble.transcript
            });
            return;
        }

        const now = Date.now();
        let effectiveDelayMs = delayMs;

        if (options.live) {
            const queuedAt = liveTranslationQueuedAt.get(bubbleId) ?? now;
            liveTranslationQueuedAt.set(bubbleId, queuedAt);
            // New deltas may debounce inside this window, but cannot move its
            // deadline. A minimum delay here starves continuous token streams.
            effectiveDelayMs = Math.max(0, Math.min(delayMs, LIVE_TRANSLATE_MAX_WAIT_MS - (now - queuedAt)));
        } else {
            liveTranslationQueuedAt.delete(bubbleId);
        }

        clearTranslationTimer(bubbleId);
        debugLog("translation_scheduled", {
            bubble: bubbleId,
            live: options.live === true,
            delayMs: effectiveDelayMs,
            queuedMs: options.live ? now - (liveTranslationQueuedAt.get(bubbleId) ?? now) : 0,
            transcript: bubble.transcript
        });
        translationTimers.set(
            bubbleId,
            window.setTimeout(() => {
                translationTimers.delete(bubbleId);
                liveTranslationQueuedAt.delete(bubbleId);
                void translateBubble(bubbleId);
            }, effectiveDelayMs)
        );
    };

    const replaceCompletedBubbleWithSentenceBubbles = (
        bubble: AutotranslatorBubble,
        itemId: string
    ): AutotranslatorBubble[] => {
        const pieces = splitAutotranslatorTranscriptIntoBubbleTexts(bubble.transcript);

        if (pieces.length <= 1) {
            return [bubble];
        }

        // Once readers have seen a caption, its partition is stable. Final ASR
        // punctuation/context must not replace it and restart its translation.
        if (hasRenderedBubbleTranscript(bubble.id)) {
            debugLog("bubble_split_preserved", { bubble: bubble.id, pieces: pieces.length, reason: "already_displayed" });
            return [bubble];
        }

        const sourceIndex = state.bubbles.findIndex((item) => item.id === bubble.id);

        if (sourceIndex < 0) {
            return [bubble];
        }

        const now = Date.now();
        const replacementBubbles = pieces.map(
            (transcript, index): AutotranslatorBubble => ({
                id: createId(),
                itemId: index === pieces.length - 1 ? itemId : `${itemId}:sentence:${index}`,
                transcript,
                rawTranscript: "",
                translation: "",
                translatedTranscript: "",
                translationPending: shouldTranslateAutotranslatorTranscript(transcript),
                literalTranslation: "",
                interpretation: "",
                interpretationSource: "",
                contextApplied: false,
                sourceLanguage: "unknown",
                status: shouldTranslateAutotranslatorTranscript(transcript) ? "translating" : "done",
                createdAt: bubble.createdAt + index,
                updatedAt: now
            })
        );
        const tailBubble = replacementBubbles[replacementBubbles.length - 1] ?? bubble;
        const replacedItemIds: string[] = [];

        itemBubbleIds.forEach((mappedBubbleId, mappedItemId) => {
            if (mappedBubbleId === bubble.id) {
                replacedItemIds.push(mappedItemId);
                itemBubbleIds.set(mappedItemId, tailBubble.id);
            }
        });
        itemBubbleIds.set(itemId, tailBubble.id);
        replacedItemIds.forEach((mappedItemId) => {
            transcriptItems.delete(mappedItemId);
            finalizedTranscriptItemIds.add(mappedItemId);
        });
        transcriptItems.delete(itemId);
        finalizedTranscriptItemIds.add(itemId);

        clearTranslationTimer(bubble.id);
        clearDeferredBubbleSplit(bubble.id);
        abortInFlightTranslation(bubble.id, "bubble_split");
        translationSequences.delete(bubble.id);
        inFlightTranslations.delete(bubble.id);
        queuedTranslations.delete(bubble.id);
        lastTranslatedTexts.delete(bubble.id);
        liveTranslationQueuedAt.delete(bubble.id);

        state.bubbles.splice(sourceIndex, 1, ...replacementBubbles);

        if (activeBubbleId === bubble.id) {
            activeBubbleId = tailBubble.id;
        }

        if (liveTranscriptItemId === bubble.itemId) {
            liveTranscriptItemId = null;
        }

        if (state.expandedBubbleId === bubble.id) {
            state.expandedBubbleId = tailBubble.id;
        }

        debugLog("bubble_split", {
            source: bubble.id,
            item: itemId,
            pieces: replacementBubbles.length,
            text: bubble.transcript
        });

        return replacementBubbles;
    };

    const hasRenderedBubbleTranscript = (bubbleId: string): boolean =>
        Boolean(root.querySelector(`[data-autotranslator-bubble="${bubbleId}"] .autotranslator-bubble-transcript:not(.visually-hidden)`));

    const settleDeferredBubbleSplit = (
        bubbleId: string,
        itemId: string,
        expectedTranscript: string,
        reason: string
    ) => {
        clearDeferredBubbleSplit(bubbleId);
        const bubble = findBubbleById(bubbleId);

        if (!bubble || normalizeLine(bubble.transcript) !== expectedTranscript) {
            return;
        }

        const pieces = splitAutotranslatorTranscriptIntoBubbleTexts(bubble.transcript);

        if (pieces.length <= 1) {
            return;
        }

        const sourceWasActive = activeBubbleId === bubble.id;
        const mappedItems = getMappedItemCountForBubble(bubble.id);
        const completedBubbles = replaceCompletedBubbleWithSentenceBubbles(bubble, itemId);

        if (completedBubbles.length <= 1) {
            return;
        }

        if (sourceWasActive) {
            activeBubbleId = null;
            liveTranscriptItemId = null;
        }
        clearTranscriptContinuity(bubble.id);
        deferFollowingBubbleLimit(() => {
            completedBubbles.forEach((completedBubble) => {
                completedBubble.status = shouldTranslateAutotranslatorTranscript(completedBubble.transcript)
                    ? "translating"
                    : "done";
                markBubbleUpdated(completedBubble);
            });
        });
        debugLog("bubble_split_settled", {
            source: bubble.id,
            item: itemId,
            reason,
            mappedItems,
            pieces: completedBubbles.length,
            text: expectedTranscript
        });
        render();

        completedBubbles.forEach((completedBubble, index) => {
            if (shouldTranslateAutotranslatorTranscript(completedBubble.transcript)) {
                scheduleTranslation(completedBubble.id, FINAL_TRANSLATE_DELAY_MS + index * 60);
            }
        });
    };

    const scheduleDeferredBubbleSplit = (bubble: AutotranslatorBubble, itemId: string, reason: string) => {
        clearDeferredBubbleSplit(bubble.id);
        const expectedTranscript = normalizeLine(bubble.transcript);

        if (!expectedTranscript) {
            return;
        }

        debugLog("bubble_split_settle_scheduled", {
            bubble: bubble.id,
            item: itemId,
            reason,
            delayMs: AUTOTRANSLATOR_DEFERRED_SPLIT_SETTLE_MS,
            mappedItems: getMappedItemCountForBubble(bubble.id),
            text: expectedTranscript
        });
        deferredSplitTimers.set(
            bubble.id,
            window.setTimeout(() => {
                deferredSplitTimers.delete(bubble.id);
                settleDeferredBubbleSplit(bubble.id, itemId, expectedTranscript, reason);
            }, AUTOTRANSLATOR_DEFERRED_SPLIT_SETTLE_MS)
        );
    };

    const shouldIgnoreRealtimeEvent = (channel: "transcription", eventType: string): boolean => {
        if (state.running || state.connecting) {
            return false;
        }

        debugLog("realtime_event_ignored", {
            channel,
            type: eventType,
            reason: "stopped"
        });
        return true;
    };

    const handleTranscriptionRealtimeEvent = (event: RealtimeServerEvent) => {
        const eventType = typeof event.type === "string" ? event.type : "";

        if (shouldIgnoreRealtimeEvent("transcription", eventType)) {
            return;
        }

        if (eventType === "conversation.item.input_audio_transcription.failed") {
            clearRecognizedSpeechGlow();
            return;
        }

        if (eventType === "error") {
            clearRecognizedSpeechGlow();
            const realtimeError = (event as { error?: { message?: unknown; type?: unknown } }).error;
            debugLog("realtime_error", {
                type: typeof realtimeError?.type === "string" ? realtimeError.type : "unknown",
                message: typeof realtimeError?.message === "string" ? realtimeError.message : "unknown"
            });
            return;
        }

        if (eventType === "input_audio_buffer.committed") {
            const itemId = typeof event.item_id === "string" ? event.item_id : "";
            const previousItemId = typeof event.previous_item_id === "string" ? event.previous_item_id : null;

            if (itemId) {
                const committedTurn = pendingCommitSpeechTurns.shift();
                if (committedTurn !== undefined) getOrCreateTranscriptItem(itemId, previousItemId).speechTurn = committedTurn;
                if (finalizedTranscriptItemIds.has(itemId)) {
                    debugLog("audio_buffer_commit_ignored", {
                        item: itemId,
                        previous: previousItemId,
                        reason: "finalized_document_item"
                    });
                    return;
                }

                getOrCreateTranscriptItem(itemId, previousItemId);
                const bubble = attachCommittedItemToLiveBubble(itemId, previousItemId, { createIfMissing: false });

                if (!bubble) {
                    debugLog("audio_buffer_committed_hidden", {
                        item: itemId,
                        previous: previousItemId,
                        continuity: lastContinuityBubbleId,
                        sinceContinuityMs: lastContinuityAt ? Date.now() - lastContinuityAt : null
                    });
                    return;
                }

                activeBubbleId = bubble.id;
                bubble.status = bubble.transcript ? bubble.status : "hearing";
                moveBubbleAfterPreviousItem(itemId, previousItemId);
                pruneExtraHearingBubbles(bubble.id);
                debugLog("audio_buffer_committed", {
                    item: itemId,
                    previous: previousItemId,
                    bubble: bubble.id,
                    transcript: bubble.transcript,
                    mappedItems: getMappedItemCountForBubble(bubble.id),
                    speechRunOpen,
                    speechRunStopped
                });
                render();

                if (bubble.transcript) {
                    scheduleTranslation(bubble.id, LIVE_TRANSLATE_DELAY_MS, { live: true });
                }
            }

            return;
        }

        if (eventType === "input_audio_buffer.speech_started") {
            const recentContinuityBubble = getRecentContinuityBubble();
            const shouldStartFreshBubble =
                !recentContinuityBubble || normalizeLine(recentContinuityBubble.transcript).length > 0;

            if (shouldStartFreshBubble) {
                if (recentContinuityBubble) {
                    debugLog("speech_started_fresh_bubble", {
                        previousBubble: recentContinuityBubble.id,
                        previousItem: recentContinuityBubble.itemId,
                        previousTranscript: recentContinuityBubble.transcript
                    });
                    clearTranscriptContinuity(recentContinuityBubble.id);
                }

                activeBubbleId = null;
                liveTranscriptItemId = null;
            }

            speechRunOpen = true;
            speechRunStopped = false;

            if (shouldStartFreshBubble) {
                state.status = t("autotranslatorHearing");
                debugLog("speech_started", {
                    bubble: null,
                    item: null,
                    transcript: "",
                    mappedItems: 0,
                    cue: "none"
                });
                return;
            }

            const bubble = recentContinuityBubble ?? getOrCreateLiveTranslationBubble();
            bubble.status = bubble.transcript ? "live" : "hearing";
            markBubbleUpdated(bubble);
            rememberTranscriptContinuity(bubble);
            pruneExtraHearingBubbles(bubble.id);
            state.status = t("autotranslatorHearing");
            debugLog("speech_started", {
                bubble: bubble.id,
                item: bubble.itemId,
                transcript: bubble.transcript,
                mappedItems: getMappedItemCountForBubble(bubble.id)
            });
            render();
            return;
        }

        if (eventType === "input_audio_buffer.speech_stopped") {
            speechRunStopped = true;
            state.status = t("autotranslatorSettling");
            debugLog("speech_stopped", {
                active: activeBubbleId,
                liveItem: liveTranscriptItemId,
                bubbles: state.bubbles.length
            });
            render();
            return;
        }

        if (eventType === "conversation.item.input_audio_transcription.delta") {
            const itemId = typeof event.item_id === "string" ? event.item_id : "";
            const delta = typeof event.delta === "string" ? event.delta : "";

            if (!delta.trim()) {
                debugLog("transcription_delta_ignored", {
                    item: itemId,
                    reason: "empty"
                });
                return;
            }

            if (itemId && finalizedTranscriptItemIds.has(itemId)) {
                debugLog("transcription_delta_ignored", {
                    item: itemId,
                    reason: "finalized_document_item"
                });
                return;
            }

            if (itemId) {
                const transcriptItem = getOrCreateTranscriptItem(itemId);
                const draft = appendAutotranslatorTranscriptDelta(transcriptItem.draftText ?? transcriptItem.text, delta);
                transcriptItem.draftText = draft;
                transcriptItem.updatedAt = Date.now();
                // Keep the accepted document separate from the ASR draft. A bad
                // fragment must not erase its card or finalize sibling items.
                if (hasUnsupportedAutotranslatorScript(draft)) {
                    debugLog("transcription_item_quarantined", { item: itemId, stage: "delta",
                        preservedChars: transcriptItem.text.length, reason: "unsupported_script" });
                    return;
                }
                transcriptItem.text = draft;
                separateLiveLanguageTurn(itemId);
            } else if (hasUnsupportedAutotranslatorScript(delta)) {
                debugLog("transcription_item_quarantined", { item: "live", stage: "delta", reason: "unsupported_script" });
                return;
            }
            const bubble = (itemId ? attachCommittedItemToLiveBubble(itemId) : getOrCreateLiveTranslationBubble()) ??
                getOrCreateLiveTranslationBubble();
            const beforeTranscript = bubble.transcript;
            activeBubbleId = bubble.id;
            if (itemId) {
                const transcriptItem = getOrCreateTranscriptItem(itemId);
                updateBubbleTranscriptFromDocument(bubble, "delta", itemId);
            } else {
                bubble.transcript = appendAutotranslatorTranscriptDelta(bubble.transcript, delta);
                markBubbleTranslationStale(bubble);
            }
            rememberTranscriptContinuity(bubble);
            debugLog("transcription_delta", {
                item: itemId || "live",
                bubble: bubble.id,
                delta,
                before: beforeTranscript,
                after: bubble.transcript,
                mappedItems: getMappedItemCountForBubble(bubble.id)
            });

            bubble.status = shouldRevealTranscript(bubble) ? "live" : "hearing";
            if (
                normalizeLine(beforeTranscript) !== normalizeLine(bubble.transcript) &&
                isAutotranslatorRecognizedSpeech(bubble.transcript)
            ) noteRecognizedSpeech(delta);
            markBubbleUpdated(bubble);
            pruneExtraHearingBubbles(bubble.id);
            render();
            scheduleTranslation(bubble.id, LIVE_TRANSLATE_DELAY_MS, { live: true });
            return;
        }

        if (eventType === "conversation.item.input_audio_transcription.completed") {
            const itemId = typeof event.item_id === "string" ? event.item_id : "";
            const transcript =
                typeof event.transcript === "string" ? normalizeAutotranslatorTranscriptLine(event.transcript) : "";

            if (itemId && finalizedTranscriptItemIds.has(itemId)) {
                debugLog("transcription_completed_ignored", {
                    item: itemId,
                    reason: "finalized_document_item",
                    transcript
                });
                return;
            }

            if (hasUnsupportedAutotranslatorScript(transcript)) {
                const item = itemId ? getOrCreateTranscriptItem(itemId) : null;
                if (item) { item.draftText = transcript; item.completed = true; item.updatedAt = Date.now(); }
                debugLog("transcription_item_quarantined", { item: itemId || "live", stage: "completed",
                    preservedChars: item?.text.length ?? 0, reason: "unsupported_script" });
                return;
            }

            if (itemId && transcript) {
                const item = getOrCreateTranscriptItem(itemId);
                item.draftText = transcript;
                // Re-find the same language boundary if final ASR revises the
                // prefix length; completion must not splice English into Korean.
                if (item.displayOffset > 0) {
                    const target = getDominantAutotranslatorSplitLanguage(item.text.slice(item.displayOffset));
                    const pieces = splitAutotranslatorMixedLanguageSegment(transcript);
                    item.languagePrefixIds.forEach((prefixId, index) => {
                        const prefix = transcriptItems.get(prefixId);
                        const revised = pieces[index];
                        const prefixBubble = findBubbleById(itemBubbleIds.get(prefixId) ?? null);
                        if (!prefix || !prefixBubble || !revised ||
                            getDominantAutotranslatorSplitLanguage(revised) !== getDominantAutotranslatorSplitLanguage(prefix.text)) return;
                        prefix.text = revised;
                        if (updateBubbleTranscriptFromDocument(prefixBubble, "completed_language_prefix", prefixId))
                            scheduleTranslation(prefixBubble.id, FINAL_TRANSLATE_DELAY_MS);
                    });
                    const tail = [...pieces].reverse().find(piece => getDominantAutotranslatorSplitLanguage(piece) === target);
                    if (tail) item.displayOffset = transcript.lastIndexOf(tail);
                }
                item.text = transcript;
                separateLiveLanguageTurn(itemId);
            }
            const bubble =
                (itemId
                    ? attachCommittedItemToLiveBubble(itemId, null, { createIfMissing: Boolean(transcript) })
                    : transcript
                      ? getOrCreateLiveTranslationBubble()
                      : null) ??
                (transcript ? getOrCreateLiveTranslationBubble() : null);

            if (!bubble) {
                if (itemId) {
                    const transcriptItem = getOrCreateTranscriptItem(itemId);
                    transcriptItem.completed = true;
                    transcriptItem.updatedAt = Date.now();
                    finalizedTranscriptItemIds.add(itemId);
                }
                debugLog("transcription_completed_hidden", {
                    item: itemId || "live",
                    reason: "empty_unmapped_transcript"
                });
                state.status = t("autotranslatorListening");
                render();
                return;
            }

            const hadTranscript = Boolean(bubble.transcript.trim());
            const beforeTranscript = bubble.transcript;
            const mappedItemCount = getMappedItemCountForBubble(bubble.id);

            if (transcript) {
                if (itemId) {
                    const transcriptItem = getOrCreateTranscriptItem(itemId);
                    transcriptItem.text = transcript;
                    transcriptItem.completed = true;
                    transcriptItem.updatedAt = Date.now();
                    updateBubbleTranscriptFromDocument(bubble, "completed", itemId);
                } else {
                    bubble.transcript =
                        mappedItemCount > 1
                            ? mergeAutotranslatorTranscriptText(bubble.transcript, transcript)
                            : transcript;
                    markBubbleTranslationStale(bubble);
                }
            } else if (itemId) {
                const transcriptItem = getOrCreateTranscriptItem(itemId);
                transcriptItem.completed = true;
                transcriptItem.updatedAt = Date.now();
            }
            debugLog("transcription_completed", {
                item: itemId || "live",
                bubble: bubble.id,
                received: transcript,
                before: beforeTranscript,
                after: bubble.transcript,
                action: transcript ? (mappedItemCount > 1 ? "merge" : "replace") : "no_transcript",
                mappedItems: mappedItemCount
            });
            if (normalizeLine(beforeTranscript) !== normalizeLine(bubble.transcript)) noteRecognizedSpeech(transcript);

            if (bubble.transcript) {
                const canTranslate = shouldTranslateAutotranslatorTranscript(bubble.transcript);

                if (!canTranslate) {
                    debugLog("transcription_dismissed", {
                        item: itemId || "live",
                        bubble: bubble.id,
                        reason: "not_default_language",
                        transcript: bubble.transcript
                    });
                    discardBubble(bubble, "not_default_language");
                    clearTranscriptContinuity(bubble.id);
                    state.status = t("autotranslatorListening");
                    render();
                    return;
                }

                const splitPreview =
                    mappedItemCount > 1 ? splitAutotranslatorTranscriptIntoBubbleTexts(bubble.transcript) : [];
                const shouldDeferSplit = splitPreview.length > 1 && !hasRenderedBubbleTranscript(bubble.id);

                if (shouldDeferSplit) {
                    debugLog("bubble_split_deferred", {
                        bubble: bubble.id,
                        item: itemId || bubble.itemId,
                        reason: "multi_item_continuity",
                        mappedItems: mappedItemCount,
                        pieces: splitPreview.length,
                        text: bubble.transcript
                    });
                }

                const completedBubbles =
                    mappedItemCount > 1 && shouldDeferSplit
                        ? [bubble]
                        : replaceCompletedBubbleWithSentenceBubbles(bubble, itemId || bubble.itemId);
                const tailBubble = completedBubbles[completedBubbles.length - 1] ?? bubble;
                const speechRunStillOpen = speechRunOpen && !speechRunStopped;

                deferFollowingBubbleLimit(() => {
                    completedBubbles.forEach((completedBubble) => {
                        const revealCompletedTranscript =
                            shouldRevealTranscript(completedBubble) ||
                            shouldTranslateAutotranslatorTranscript(completedBubble.transcript);
                        const currentTranslation = getCurrentAutotranslatorTranslation(completedBubble);

                        if (!currentTranslation) {
                            completedBubble.translationPending = shouldTranslateAutotranslatorTranscript(
                                completedBubble.transcript
                            );
                        }

                        completedBubble.status = currentTranslation
                            ? speechRunStillOpen && completedBubble.id === tailBubble.id
                                ? "live"
                                : "done"
                            : revealCompletedTranscript
                              ? "translating"
                              : "hearing";
                        markBubbleUpdated(completedBubble);
                    });
                });
                state.status = t("autotranslatorListening");
                rememberTranscriptContinuity(tailBubble);
                if (!speechRunStillOpen) {
                    activeBubbleId = tailBubble.id;
                    liveTranscriptItemId = null;
                    speechRunOpen = false;
                }
                pruneExtraHearingBubbles(tailBubble.id);
                render();

                completedBubbles.forEach((completedBubble) => {
                    if (!shouldDeferSplit && shouldTranslateAutotranslatorTranscript(completedBubble.transcript)) {
                        scheduleTranslation(completedBubble.id, FINAL_TRANSLATE_DELAY_MS);
                    }
                });
                if (shouldDeferSplit) {
                    scheduleDeferredBubbleSplit(bubble, itemId || bubble.itemId, "quiet_multi_item_continuity");
                }
            } else if (!hadTranscript) {
                debugLog("bubble_dismissed", {
                    item: itemId || "live",
                    bubble: bubble.id,
                    reason: "empty_completed_transcript"
                });
                discardBubble(bubble, "empty_completed_transcript");
                clearTranscriptContinuity(bubble.id);
                state.status = t("autotranslatorListening");
                render();
            }
        }
    };

    const fetchRealtimeToken = async (
        path: string,
        body: Record<string, unknown>,
        signal: AbortSignal
    ): Promise<RealtimeTokenResponse> => {
        const response = await fetch(path, {
            method: "POST",
            credentials: "same-origin",
            signal,
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });
        const payload = (await response.json().catch(() => ({}))) as RealtimeTokenResponse;

        if (!response.ok || typeof payload.clientSecret !== "string") {
            throw new Error(getAutotranslatorError(payload, t("autotranslatorStartFailed")));
        }

        return payload;
    };

    const openRealtimeConnection = async ({
        clientSecret,
        stream,
        signal
    }: {
        clientSecret: string;
        stream: MediaStream;
        signal: AbortSignal;
    }): Promise<RealtimeConnection> => {
        signal.throwIfAborted();
        const peerConnection = new RTCPeerConnection();
        const dataChannel = peerConnection.createDataChannel("oai-events");
        const connection: RealtimeConnection = {
            peerConnection,
            dataChannel,
            manualCommitTimer: null
        };
        realtimeConnections.push(connection);

        stream.getTracks().forEach((track) => {
            peerConnection.addTrack(track, stream);
        });

        dataChannel.addEventListener("message", (messageEvent) => {
            if (signal.aborted || stopExpiredBackgroundSession() || typeof messageEvent.data !== "string") {
                return;
            }

            try {
                const event = JSON.parse(messageEvent.data) as RealtimeServerEvent;

                handleTranscriptionRealtimeEvent(event);
            } catch {
                debugLog("realtime_event_ignored", {
                    channel: "transcription",
                    type: "malformed",
                    reason: "json_parse_failed"
                });
            }
        });

        peerConnection.addEventListener("connectionstatechange", () => {
            if (!signal.aborted && peerConnection.connectionState === "failed") {
                debugLog("connection_failed", {
                    kind: "transcription"
                });
                void stopSession();
                setError(t("autotranslatorConnectionLost"));
            }
        });

        const openPromise = new Promise<void>((resolve, reject) => {
            dataChannel.addEventListener(
                "open",
                () => {
                    if (signal.aborted || stopExpiredBackgroundSession()) {
                        reject(new DOMException("Session stopped", "AbortError"));
                        return;
                    }
                    debugLog("session_open", {
                        kind: "transcription"
                    });
                    connection.manualCommitTimer = window.setInterval(() => {
                        const now = Date.now();

                        if (!state.running && !state.connecting) {
                            return;
                        }

                        if (dataChannel.readyState !== "open") {
                            return;
                        }

                        if (now - lastLocalSpeechAt > LIVE_TRANSCRIPTION_SPEECH_GRACE_MS) {
                            return;
                        }

                        const sinceSpeechMs = now - lastLocalSpeechAt;
                        const commitReason = getAutotranslatorCommitReason({ now, speechStartedAt: localSpeechStartedAt,
                            lastSpeechAt: lastLocalSpeechAt, lastCommitAt: lastManualCommitAt,
                            lastCommittedSpeechAt });
                        if (!commitReason) return;
                        const quietCommit = commitReason === "pause";

                        lastManualCommitAt = now;
                        lastCommittedSpeechAt = lastLocalSpeechAt;
                        pendingCommitSpeechTurns.push(quietCommit && localSpeechBoundaryAt > lastLocalSpeechAt ? localSpeechTurn - 1 : localSpeechTurn);
                        dataChannel.send(
                            JSON.stringify({
                                type: "input_audio_buffer.commit"
                            })
                        );
                        debugLog("manual_transcription_commit", {
                            sinceSpeechMs,
                            speechAgeMs: now - localSpeechStartedAt,
                            quiet: quietCommit,
                            reason: commitReason,
                            turn: localSpeechTurn, noiseFloor: lastSpeechFrame.noiseFloor.toFixed(5),
                            rawThreshold: lastSpeechFrame.threshold.toFixed(5)
                        });
                    }, LIVE_TRANSCRIPTION_COMMIT_CHECK_MS);
                    resolve();
                },
                { once: true }
            );
            dataChannel.addEventListener(
                "error",
                () => {
                    reject(new Error(t("autotranslatorStartFailed")));
                },
                { once: true }
            );
            signal.addEventListener("abort", () => reject(new DOMException("Session stopped", "AbortError")), { once: true });
        });
        // Cancellation can happen while the SDP request is still pending.
        void openPromise.catch(() => undefined);

        const offer = await peerConnection.createOffer();
        signal.throwIfAborted();
        await peerConnection.setLocalDescription(offer);
        signal.throwIfAborted();
        await waitForIceGathering(peerConnection);
        signal.throwIfAborted();

        const localDescription = peerConnection.localDescription;
        const sdpResponse = await fetch(OPENAI_REALTIME_SDP_URL, {
            method: "POST",
            signal,
            headers: {
                Authorization: `Bearer ${clientSecret}`,
                "Content-Type": "application/sdp"
            },
            body: localDescription?.sdp ?? offer.sdp
        });

        if (!sdpResponse.ok) {
            throw new Error(t("autotranslatorStartFailed"));
        }

        signal.throwIfAborted();
        const answerSdp = await sdpResponse.text();
        signal.throwIfAborted();
        await peerConnection.setRemoteDescription({
            type: "answer",
            sdp: answerSdp
        });
        await openPromise;

        return connection;
    };

    const startSession = async () => {
        if (state.connecting || state.running || versionRefreshPending) {
            return;
        }

        if (!navigator.mediaDevices?.getUserMedia) {
            debugLog("session_start_failed", {
                reason: "no_mic_support"
            });
            setError(t("autotranslatorNoMicSupport"));
            return;
        }

        const generation = ++sessionGeneration;
        const controller = new AbortController();
        sessionStartController = controller;
        const isCurrentSession = () => !destroyed && generation === sessionGeneration && !controller.signal.aborted;
        clearStartupFeedback();
        state.startupFeedbackActive = true;
        state.connecting = true;
        state.error = "";
        state.summary = null;
        state.elapsedMs = 0;
        state.translationCount = 0;
        state.translationCostUsd = 0;
        state.estimatedTranslationCount = 0;
        textCostMeter = new AutotranslatorTextMeter();
        state.startedAt = Date.now();
        state.status = t("autotranslatorConnecting");
        realtimeConnections = [];
        debugLog("session_start_requested", {
            mode: state.audioMode,
            properNouns: state.properNouns.length,
            session: debugSessionId,
            buildId: typeof __ZIARCADIA_BUILD_ID__ === "string" ? __ZIARCADIA_BUILD_ID__ : "local",
            detector: "raw_adaptive",
            translation: "streaming",
            liveDeadlineMs: LIVE_TRANSLATE_MAX_WAIT_MS
        });
        render();
        handleAutotranslatorVisibility();

        try {
            // Prime under the Start gesture, before version/token/microphone
            // awaits can consume Safari's transient user activation.
            stopMeter();
            const Context = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (Context) {
                const context = audioContext = new Context();
                context.onstatechange = () => {
                    if (audioContext !== context || !isCurrentSession()) return;
                    debugLog("audio_context_state", { contextState: context.state, contextTime: context.currentTime.toFixed(3) });
                    if (context.state !== "running") recoverAudioContext();
                };
                void context.resume().catch(() => {
                    if (audioContext === context && isCurrentSession()) debugLog("audio_context_resume_failed", { phase: "prime", contextState: context.state });
                });
            }
            // A restored tab must compare its actual loaded build before asking
            // for a microphone/token. Finish the previous run's private uploads
            // first; polling never interrupts a live run or clears its results.
            const hasNewBuild = await versionGuard.check();
            if (!isCurrentSession() || stopExpiredBackgroundSession()) return;
            if (hasNewBuild) {
                await diagnosticStartRequest;
                await Promise.allSettled([...diagnosticCaptures].map(capture => capture.stop()));
                if (!isCurrentSession() || stopExpiredBackgroundSession()) return;
                versionRefreshPending = versionGuard.refresh();
                if (versionRefreshPending) return;
            }
            const transcriptionToken = await fetchRealtimeToken("/api/autotranslator/realtime-token", {
                audioMode: state.audioMode
            }, controller.signal);
            if (!isCurrentSession() || stopExpiredBackgroundSession()) return;
            debugLog("realtime_token_ready", {
                mode: state.audioMode,
                expiresAt: transcriptionToken.expiresAt ?? null
            });

            const closeMicMode = state.audioMode === "near";
            const capturedStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: {
                        ideal: 1
                    },
                    sampleRate: {
                        ideal: 48000
                    },
                    echoCancellation: closeMicMode,
                    noiseSuppression: closeMicMode,
                    autoGainControl: true
                }
            });
            if (!isCurrentSession() || stopExpiredBackgroundSession()) {
                capturedStream.getTracks().forEach((track) => track.stop());
                return;
            }
            rawLocalStream = capturedStream;
            const rawTrackSettings = rawLocalStream.getAudioTracks()[0]?.getSettings?.();
            debugLog("mic_stream_ready", {
                mode: state.audioMode,
                sampleRate: rawTrackSettings?.sampleRate ?? null,
                channelCount: rawTrackSettings?.channelCount ?? null,
                echoCancellation: rawTrackSettings?.echoCancellation ?? null,
                noiseSuppression: rawTrackSettings?.noiseSuppression ?? null,
                autoGainControl: rawTrackSettings?.autoGainControl ?? null
            });
            processedLocalStream = buildProcessedAudioStream(rawLocalStream);
            if (audioContext) await ensureAudioContext(audioContext, controller.signal, "startup");
            if (!isCurrentSession() || stopExpiredBackgroundSession()) return;
            await requestWakeLock(generation);
            if (!isCurrentSession() || stopExpiredBackgroundSession()) return;

            await openRealtimeConnection({
                clientSecret: transcriptionToken.clientSecret as string,
                stream: processedLocalStream,
                signal: controller.signal
            });
            if (!isCurrentSession() || stopExpiredBackgroundSession()) return;

            if (audioContext) await ensureAudioContext(audioContext, controller.signal, "connected");
            if (!isCurrentSession() || stopExpiredBackgroundSession()) return;
            state.connecting = false;
            state.running = true;
            state.status = t("autotranslatorListening");
            // An initial sign-in/network failure must not disable diagnostics
            // for the entire visit. Never assume recording consent on failure.
            if (state.diagnosticEnabled === null) void loadDiagnosticSetting();
            else beginDiagnosticCapture();
            if (state.startupFeedbackActive) {
                // A brief initial-listening cue covers the first recognition buffer;
                // real recognized text clears it sooner without delaying captions.
                startupFeedbackTimer = window.setTimeout(() => {
                    if (!isCurrentSession() || !state.running) return;
                    clearStartupFeedback();
                    render();
                }, SITE_TUNING.autotranslator.initialListenFeedbackMs);
            }
            debugLog("session_ready", {
                mode: state.audioMode,
                connections: realtimeConnections.length
            });
            render();

            elapsedTimer = window.setInterval(() => {
                if (!state.startedAt) {
                    return;
                }

                state.elapsedMs = Date.now() - state.startedAt;
                syncTextCostMeter(textCostMeter, false);
                const elapsed = root.querySelector<HTMLElement>("[data-autotranslator-elapsed]");
                const cost = root.querySelector<HTMLElement>("[data-autotranslator-cost]");

                if (elapsed) {
                    elapsed.textContent = formatElapsed(state.elapsedMs);
                }

                if (cost) {
                    cost.textContent = formatCost(estimateAutotranslatorSessionCostUsd(state.elapsedMs, state.translationCostUsd));
                }
            }, 1000);
        } catch (error) {
            if (!isCurrentSession()) return;
            debugLog("session_start_failed", {
                message: error instanceof Error ? error.message : "unknown"
            });
            await stopSession(false);
            if (!destroyed && generation + 1 === sessionGeneration) setError(error instanceof AutotranslatorAudioContextError
                ? t("autotranslatorAudioNotReady") : error instanceof Error ? error.message : t("autotranslatorStartFailed"));
        }
    };

    let nounComposing = false;
    const coarsePointer = () => window.matchMedia?.("(pointer: coarse)").matches === true;
    const syncNameViewport = () => {
        if (!state.nounFormOpen) return;
        const stage = root.querySelector<HTMLElement>(".autotranslator-stage-inner");
        const bar = root.querySelector<HTMLElement>(".autotranslator-pagebar");
        if (!stage || !bar) return;
        const viewport = window.visualViewport;
        const visibleTop = viewport?.offsetTop ?? 0;
        const visibleBottom = visibleTop + (viewport?.height ?? window.innerHeight);
        const stageTop = stage.getBoundingClientRect().top;
        const preferredTop = bar.offsetTop + bar.offsetHeight + 8;
        // iOS can shrink only the visual viewport for its keyboard. Keep the
        // fixed editor controls visible and let only its saved list scroll.
        const top = visibleBottom - stageTop - preferredTop < 230 ? Math.max(8, visibleTop - stageTop + 8) : preferredTop;
        stage.style.setProperty("--autotranslator-noun-top", `${top}px`);
        stage.style.setProperty("--autotranslator-noun-height", `${Math.min(coarsePointer() ? 420 : 520, Math.max(180, visibleBottom - stageTop - top - 10))}px`);
    };
    window.addEventListener("resize", syncNameViewport);
    window.visualViewport?.addEventListener("resize", syncNameViewport);
    window.visualViewport?.addEventListener("scroll", syncNameViewport);
    const clearNounDraft = () => {
        state.nounInput = ""; state.nounKoreanInput = ""; state.nounEditingId = null; state.nounError = "";
    };
    const closeNounEditor = (returnFocus = true) => {
        state.nounFormOpen = false;
        if (root.querySelector(".autotranslator-noun-drawer")?.contains(document.activeElement)) (document.activeElement as HTMLElement)?.blur();
        render();
        if (returnFocus) root.querySelector<HTMLButtonElement>("[data-autotranslator-toggle-noun-form]")?.focus({ preventScroll: true });
    };
    const updateNounDraftUi = () => {
        state.nounError = "";
        const pair = nounDraftPair(state);
        const english = root.querySelector<HTMLInputElement>("[data-autotranslator-noun-input]");
        const korean = root.querySelector<HTMLInputElement>("[data-autotranslator-noun-korean-input]");
        if (english) english.placeholder = pair.english || "Jisu";
        if (korean) korean.placeholder = pair.korean || "지수";
        const feedback = root.querySelector<HTMLElement>("[data-autotranslator-noun-feedback]");
        if (feedback) {
            feedback.classList.remove("has-error");
            feedback.textContent = state.nounInput.trim() || state.nounKoreanInput.trim() ? `${pair.english || "—"} / ${pair.korean || "—"}` : "";
        }
        const save = root.querySelector<HTMLButtonElement>("[data-autotranslator-save-noun]");
        if (save) save.disabled = !state.nounInput.trim() && !state.nounKoreanInput.trim();
    };
    const addProperNoun = () => {
        if (nounComposing || !state.nounInput.trim() && !state.nounKoreanInput.trim()) return;
        const parsed = nounDraftPair(state);
        const matching = state.properNouns.find(noun => noun.id === state.nounEditingId) ?? state.properNouns.find(noun =>
            parsed.english && noun.english.toLowerCase() === parsed.english.toLowerCase() || parsed.korean && noun.korean === parsed.korean);
        if (!matching && state.properNouns.length >= 40) {
            state.nounError = t("autotranslatorNameLimit"); render(); return;
        }
        const conflict = state.properNouns.find(noun => noun.id !== matching?.id &&
            (parsed.english && noun.english.toLowerCase() === parsed.english.toLowerCase() || parsed.korean && noun.korean === parsed.korean));
        if (conflict) { state.nounError = t("autotranslatorNameConflict"); render(); return; }
        const saved = { id: matching?.id ?? createId(), ...parsed };
        const position = matching ? state.properNouns.findIndex(noun => noun.id === matching.id) : state.properNouns.length;
        // A correction replaces its row. Remove an exact duplicate of that pair
        // rather than keeping contradictory copies or silently exceeding ASR's cap.
        state.properNouns = state.properNouns.filter(noun => noun.id !== saved.id && properNounKey(noun) !== properNounKey(parsed));
        if (matching) state.properNouns.splice(Math.min(state.properNouns.length, position), 0, saved);
        else state.properNouns.push(saved);
        safeWriteProperNouns(state.properNouns);
        if (coarsePointer()) (document.activeElement as HTMLElement)?.blur();
        clearNounDraft();
        render();
        if (!coarsePointer()) root.querySelector<HTMLInputElement>("[data-autotranslator-noun-input]")?.focus({ preventScroll: true });
    };

    function bind() {
        bindingController?.abort();
        bindingController = new AbortController();
        const signal = bindingController.signal;
        const listen = <K extends keyof HTMLElementEventMap>(
            element: Element | null, type: K, listener: (event: HTMLElementEventMap[K]) => void,
            options: AddEventListenerOptions = {}
        ) => element?.addEventListener(type, listener as EventListener, { ...options, signal });
        listen(root.querySelector("[data-autotranslator-start]"), "click", () => {
            if (state.running || state.connecting) {
                void stopSession();
                return;
            }

            void startSession();
        });

        listen(root.querySelector("[data-autotranslator-flip]"), "click", () => {
            state.tableMode = !state.tableMode;
            render();
        });

        listen(root.querySelector("[data-autotranslator-toggle-noun-form]"), "click", () => {
            if (state.nounFormOpen) { closeNounEditor(); return; }
            state.nounFormOpen = !state.nounFormOpen;
            render();
            if (!coarsePointer()) root.querySelector<HTMLInputElement>("[data-autotranslator-noun-input]")?.focus({ preventScroll: true });
        });

        root.querySelectorAll<HTMLElement>("[data-autotranslator-audio-mode]").forEach((button) => {
            listen(button, "click", () => {
                const mode = button.getAttribute("data-autotranslator-audio-mode");

                if (mode !== "table" && mode !== "near" && mode !== "far") {
                    return;
                }

                state.audioMode = mode;
                applyAudioModeToPipeline(state.running ? { logEvent: "audio_pipeline_mode_changed" } : {});
                render();
            });
        });

        listen(root.querySelector("[data-autotranslator-clear]"), "click", () => {
            clearRecognizedSpeechGlow();
            debugLog("feed_cleared", {
                bubbles: state.bubbles.length,
                translations: state.translationCount
            });
            state.bubbles = [];
            conversationHistory.length = 0;
            state.summary = null;
            state.unseenCount = 0;
            state.following = true;
            inspectionPaused = false;
            translationTimers.forEach((timer) => {
                window.clearTimeout(timer);
            });
            translationTimers.clear();
            deferredSplitTimers.forEach((timer) => {
                window.clearTimeout(timer);
            });
            deferredSplitTimers.clear();
            abortAllInFlightTranslations("feed_cleared");
            itemBubbleIds.clear();
            translationSequences.clear();
            inFlightTranslations.clear();
            queuedTranslations.clear();
            lastTranslatedTexts.clear();
            liveTranslationQueuedAt.clear();
            transcriptItems.clear();
            finalizedTranscriptItemIds.clear();
            transcriptItemOrder = 0;
            activeBubbleId = null;
            liveTranscriptItemId = null;
            clearTranscriptContinuity();
            speechRunOpen = false;
            speechRunStopped = false;
            render();
        });

        listen(root.querySelector("[data-autotranslator-toggle-details]"), "click", () => {
            state.detailsOpen = !state.detailsOpen;
            state.meterOpen = state.detailsOpen;
            safeWriteViewPreferences(state);
            render();
        });

        listen(root.querySelector("[data-autotranslator-diagnostic-toggle]"), "click", async () => {
            if (state.diagnosticBusy || state.diagnosticEnabled === null) return;
            const enabled = !state.diagnosticEnabled;
            state.diagnosticBusy = true;
            if (!enabled) { state.diagnosticEnabled = false; void stopDiagnosticCapture(); }
            render();
            try {
                // Finish the final encoder chunks before closing server uploads.
                if (!enabled) await Promise.all([...diagnosticCaptures].map(capture => capture.stop()));
                const response = await fetch(AUTOTRANSLATOR_DIAGNOSTIC_PATH, { method: "PATCH", credentials: "same-origin",
                    headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled }) });
                if (!response.ok) throw new Error("Diagnostic setting unavailable");
                state.diagnosticEnabled = enabled;
                state.diagnosticStatus = "ready";
                if (enabled) beginDiagnosticCapture();
            } catch { state.diagnosticStatus = "error"; }
            finally { state.diagnosticBusy = false; if (!destroyed) render(); }
        });

        listen(root.querySelector("[data-autotranslator-diagnostic-delete]"), "click", async () => {
            if (state.diagnosticBusy) return;
            state.diagnosticBusy = true;
            state.diagnosticEnabled = false;
            diagnosticGeneration += 1;
            for (const capture of diagnosticCaptures) capture.discard();
            diagnosticCaptures.clear(); diagnosticCapture = null;
            state.diagnosticStatus = "ready";
            render();
            try {
                const response = await fetch(AUTOTRANSLATOR_DIAGNOSTIC_PATH, { method: "DELETE", credentials: "same-origin" });
                if (!response.ok) throw new Error("Diagnostic deletion unavailable");
            } catch { state.diagnosticStatus = "error"; }
            finally { state.diagnosticBusy = false; if (!destroyed) render(); }
        });

        listen(root.querySelector("[data-autotranslator-follow]"), "click", () => {
            inspectionPaused = false;
            state.following = true;
            state.unseenCount = 0;
            applyFollowingBubbleLimit();
            render();
            // Exit placeholders can preserve the old height during animation.
            // Follow must resume without depending on a later translation reply.
            const feed = root.querySelector<HTMLElement>("[data-autotranslator-feed]");
            if (feed) scrollFeedToBottom(feed);
        });

        listen(root.querySelector("[data-autotranslator-noun-form]"), "submit", (event) => {
            event.preventDefault();
            addProperNoun();
        });

        listen(root.querySelector("[data-autotranslator-close-noun-form]"), "click", () => {
            closeNounEditor();
        });

        listen(root.querySelector("[data-autotranslator-cancel-noun-edit]"), "click", () => { clearNounDraft(); render(); });
        listen(root, "keydown", event => {
            if (event.key === "Escape" && !event.isComposing && state.nounFormOpen) { event.preventDefault(); closeNounEditor(); }
        });
        listen(root, "click", event => {
            // A remove/cancel click can detach its target before bubbling here.
            // The dispatch path still identifies it as an editor interaction.
            const insideEditor = event.composedPath().some(node => node instanceof Element &&
                node.matches(".autotranslator-noun-drawer, [data-autotranslator-toggle-noun-form]"));
            if (state.nounFormOpen && !insideEditor) closeNounEditor(false);
        });

        listen(root.querySelector("[data-autotranslator-noun-input]"), "input", (event) => {
            state.nounInput = (event.target as HTMLInputElement).value;
            updateNounDraftUi();
        });
        listen(root.querySelector("[data-autotranslator-noun-korean-input]"), "input", event => {
            state.nounKoreanInput = (event.target as HTMLInputElement).value;
            updateNounDraftUi();
        });
        for (const input of root.querySelectorAll(".autotranslator-noun-form input")) {
            listen(input, "compositionstart", () => { nounComposing = true; });
            listen(input, "compositionend", () => { nounComposing = false; });
        }
        root.querySelectorAll<HTMLElement>("[data-autotranslator-edit-noun]").forEach(button => {
            listen(button, "click", () => {
                const noun = state.properNouns.find(noun => noun.id === button.getAttribute("data-autotranslator-edit-noun"));
                if (!noun) return;
                state.nounInput = noun.english; state.nounKoreanInput = noun.korean; state.nounEditingId = noun.id; state.nounError = "";
                render();
                root.querySelector<HTMLInputElement>("[data-autotranslator-noun-input]")?.focus({ preventScroll: true });
            });
        });

        root.querySelectorAll<HTMLElement>("[data-autotranslator-remove-noun]").forEach((button) => {
            listen(button, "click", () => {
                const id = button.getAttribute("data-autotranslator-remove-noun");
                state.properNouns = state.properNouns.filter((noun) => noun.id !== id);
                if (state.nounEditingId === id) clearNounDraft();
                safeWriteProperNouns(state.properNouns);
                render();
            });
        });

        root.querySelectorAll<HTMLElement>("[data-autotranslator-bubble]").forEach((button) => {
            listen(button, "click", () => {
                const bubbleId = button.getAttribute("data-autotranslator-bubble");
                if (bubbleId && findBubbleById(bubbleId)?.status === "error") {
                    lastTranslatedTexts.delete(bubbleId);
                    scheduleTranslation(bubbleId, 0);
                    return;
                }
                inspectionPaused = true;
                state.following = false;
                state.expandedBubbleId = bubbleId;
                render();
            });
        });

        root.querySelectorAll("[data-autotranslator-close-expanded]").forEach((button) => {
            listen(button, "click", () => {
                state.expandedBubbleId = null;
                render();
            });
        });

        const feed = root.querySelector<HTMLElement>("[data-autotranslator-feed]");

        const pauseFollowingForDirectInput = (reason: string) => {
            if (
                !feed ||
                feed.scrollHeight - feed.clientHeight <= FOLLOW_BOTTOM_THRESHOLD_PX ||
                (!state.following && followScrollSettleTimer === null)
            ) {
                return;
            }

            if (followScrollSettleTimer !== null) {
                window.cancelAnimationFrame(followScrollSettleTimer);
                followScrollSettleTimer = null;
            }
            autoScrollingUntil = 0;
            inspectionPaused = true;
            state.following = false;
            debugLog("follow_paused", { reason });
            updateFollowButton();
        };
        listen(feed, "wheel", () => pauseFollowingForDirectInput("wheel"), { passive: true });
        listen(feed, "touchmove", () => pauseFollowingForDirectInput("touchmove"), { passive: true });
        listen(feed, "keydown", (event) => {
            if (["ArrowUp", "PageUp", "Home"].includes(event.key)) {
                pauseFollowingForDirectInput("keyboard");
            }
        });

        listen(feed, "scroll", () => {
            if (!feed || restoringScroll || Date.now() < autoScrollingUntil) {
                return;
            }

            const scrollRange = feed.scrollHeight - feed.clientHeight;
            const isScrollable = scrollRange > FOLLOW_BOTTOM_THRESHOLD_PX;

            if (!isScrollable) {
                if (!inspectionPaused) {
                    state.following = true;
                    state.unseenCount = 0;
                }
                updateFollowButton();
                return;
            }

            const distanceFromBottom = feed.scrollHeight - feed.scrollTop - feed.clientHeight;
            const nearBottom = distanceFromBottom < FOLLOW_BOTTOM_THRESHOLD_PX;

            if (nearBottom) {
                inspectionPaused = false;
                state.following = true;
                state.unseenCount = 0;
            } else if (state.following) {
                state.following = false;
            }

            updateFollowButton();
        });
    }

    render();

    void loadDiagnosticSetting();
    const versionCheckTimer = window.setInterval(() => { void checkIdleVersion(); }, 60_000);
    // Opening the tool and returning to it are safe opportunities to replace an
    // idle stale page. Start also checks, including automatic listening starts.
    void checkIdleVersion();

    if (shouldAutoStart() && !autoStartAttempted) {
        autoStartAttempted = true;
        window.setTimeout(() => {
            if (destroyed || state.running || state.connecting) {
                return;
            }

            debugLog("session_auto_start", {
                reason: "tool_opened"
            });
            void startSession();
        }, 120);
    }

    return {
        destroy: () => {
            destroyed = true;
            versionGuard.destroy();
            window.clearInterval(versionCheckTimer);
            bindingController?.abort();
            exitingBubbleSlots.forEach(({ element }) => { element.getAnimations().forEach(animation => animation.cancel()); element.remove(); });
            exitingBubbleSlots.clear();
            if (followScrollSettleTimer !== null) {
                window.cancelAnimationFrame(followScrollSettleTimer);
                followScrollSettleTimer = null;
            }
            void stopSession(false);
            window.removeEventListener("pagehide", handleAutotranslatorPageHide);
            window.removeEventListener("resize", syncNameViewport);
            window.visualViewport?.removeEventListener("resize", syncNameViewport);
            window.visualViewport?.removeEventListener("scroll", syncNameViewport);
            document.removeEventListener("visibilitychange", handleAutotranslatorVisibility);
            document.removeEventListener("resume", handleAutotranslatorVisibility);
            void flushRemoteDebugLog(true);
            root.innerHTML = "";
        },
        refreshLanguage: () => {
            render();
        }
    };
};
